import { Protocol } from "game-common/protocol"
import { emberFrameBaker } from "./sprites/ember_frame_baker"
import { ChapterChecklist } from "./gui/chapter_checklist"
import { ChapterCelebration } from "./gui/chapter_celebration"
import { SpeechBubbleLayout } from "./sprites/speech_bubble_layout"
import { ChapterAnnouncement } from "./gui/chapter_announcement"
import { HudPresence } from "./gui/hud_presence"
import { ConversationPanel } from "./gui/conversation_panel"
import { CONVERSATION_LEAVE_GAP, ConversationView, conversationGap } from "game-common/conversation"
import { HURT_BUSY_MS, QuietWatch } from "./gui/quiet_watch"
import { ControlsLesson } from "./gui/controls_lesson"
import { controlsActionSent } from "./gui/controls_lesson_state"
import { SpeechHoldButtons } from "./gui/speech_hold"
import { TileInteractionTarget, interactionsFromTile } from "game-common/entity_tile_interactions"
import { EMBER_CORE } from "game-common/charged_weapon"
import { EmberAnxietyState, HudPiece } from "game-common/models"
import { EmberAnxietyMeter } from "./gui/ember_anxiety_meter"
import { MobileBuildToolbar } from "./gui/mobile_build_toolbar"
import { BuildingPrintCountdown } from "./gui/building_print_countdown"
import { stageViewport } from "../../../canvas_layout"
import { BuildingPrintGroup } from "../../../client_models"
import { EmberInviteModal } from "./gui/ember_invite_modal"
import { WeaponAmmo } from "game-common/character/character"
import {
    ItemTypeMeta,
    locateInventoryItemObjectByItemType,
    locateInventoryItemObjects,
    resolveItemTypeToMeta,
} from "game-common/item/item"
import { SquadState } from "game-common/mechanics/combat_mechanics"
import {
    INTERACTION_REACH_TILES,
    InteractionDescriptor,
    InteractionType,
    interactionLabel,
    interactionNeedsReach,
    wheelInteractions,
} from "game-common/mechanics/entity_mechanics"
import {
    AbilityType,
    Coordinate,
    Dimensions,
    EmberInvasionCandidate,
    EmberInvite,
    EmberMinimapState,
    EmberPlayerEntry,
    EmberRosterEntry,
    EmberRunSummary,
    EmberTutorialPrompt,
    Entity,
    EmberPlayMode,
    EntityId,
    Inventory,
    InventoryItem,
    LIMBO_ROOM_NAME,
    LeaderboardMeta,
    ScreenDimensions,
    ScreenMessage,
    TileSize,
    TutorialType,
    WorldMapMeta,
    getDistance,
} from "game-common/models"
import { Callback, degrees_to_radians, getAngle } from "game-common/util"
import TextInput from "pixi-text-input"
import {
    BitmapText,
    Container,
    DisplayObject,
    Graphics,
    LINE_CAP,
    Loader,
    Point,
    Rectangle,
    Sprite,
    Text,
    Texture,
} from "pixi.js"
import {
    BuildingShapePalette,
    buildingShapePaletteAnchor,
    PALETTE_STAGE_H,
    PALETTE_W,
} from "./gui/building_shape_palette"
import { BuildingShape } from "../../../building_shapes"

import { ClientGameLogic } from "../../../client_game_logic"
import { IGuiRenderingManager, KeyModifiers } from "../../../client_models"
import { isMobile, SCALER, ScalingParams, sprintScaledIntent } from "../../../client_util"
import { ClientRenderable, EntityRenderingManager } from "./entity_rendering_manager"
import { hillHeightOf } from "./hill_lift"
import { AbilitiesWidget } from "./gui/abilities"
import { About } from "./gui/about"
import { createButton } from "./gui/button_factory"
import { UiButton } from "./gui/ui_button"
import { BUTTON_SIZES } from "./gui/ui_tokens"
import { CharacterBuilder } from "./gui/character_builder"
import { CharacterCustomizer } from "./gui/character_customizer"
import { ChatHistory, ChatLogEntry, entryFromLine } from "./gui/chat_history"
import {
    ChatHistoryWalk,
    ChatInputState,
    EMPTY_INPUT,
    EMPTY_WALK,
    EditScope,
    deleteBackward,
    deleteForward,
    historyBack,
    historyForward,
    insertText,
    moveCaret,
    rememberSent,
} from "./gui/chat_input_model"
import { CHAT_WIDTH, LOG_MAX_MESSAGES } from "./gui/chat_theme"
import { holdFor, ImportantLane, NoticeTier } from "./gui/important_lane"
import { deriveObligations, Obligation, ObligationDismissals } from "./gui/ember_obligations"
import { EmberObligationsPanel } from "./gui/ember_obligations_panel"
import { ChatChip } from "./gui/chat_chip"
import { ChatComposer, COMPOSER_PANEL_BOTTOM, COMPOSER_PANEL_X, COMPOSER_PANEL_W } from "./gui/chat_composer"
import { RecruitMenuHint } from "./gui/recruit_menu_hint"
import { ControlBar } from "./gui/control_bar"
import {
    CommandWheel,
    CommandWheelRole,
    INTERACTION_PAGE,
    isChangeRoleCommand,
    isHoldPositionCommand,
} from "./gui/command_wheel"
import { LocationPicker } from "./gui/location_picker"
import { TargetingReticle } from "./gui/targeting_reticle"
import { RecruitRole, RolePicker } from "./gui/role_picker"
import { NamePrompt } from "./gui/name_prompt"
import { Keyboard } from "./input/keyboard"
import { RunSummary } from "./gui/run_summary"
import { InvasionPicker, LetGoButton } from "./gui/invasion_picker"
import { PossessionSplash, PossessionSplashKind, splashForInvadingChange } from "./gui/possession_splash"
import { TutorialPrompt } from "./gui/tutorial_prompt"
import { CountdownTimer } from "./gui/countdown_timer"
import { WaveIndicator, WaveStatus } from "./gui/wave_indicator"
import { DayCycle } from "./gui/day_cycle"
import { HeroMessage } from "./gui/hero_message"
import { DynamicDialog } from "./gui/dynamic_dialog"
import { ModernInventoryDialog, isCraftingMessage, isTradeMessage } from "./gui/modern_inventory_dialog"
import { DynamicText } from "./gui/dynamic_text"
import { DyanmicTextContainer } from "./gui/dynamic_text_container"
import { AmmoReadout, EquippedWidget, TRAY_HEIGHT, TRAY_PAD } from "./gui/equipped"
import { readyBarSlotOf } from "./gui/ready_bar_items"
import { Fader } from "./gui/fader"
import { FeatureWidget } from "./gui/feature_widget"
import { FpsMeter } from "./gui/fps_meter"
import { FeedbackComposer } from "./gui/feedback"
import { Help } from "./gui/help"
import { IconMeter } from "./gui/icon_meter"
import { ImportantMessage } from "./gui/important_message"
import { LeaderboardWidget } from "./gui/leaderboard"
import { ManaMeter } from "./gui/mana_meter"
import { StaminaMeter } from "./gui/stamina_meter"
import { chipMetrics, getUiMetrics } from "./gui/ui_metrics"
import { Modal, ModalProps } from "./gui/modal"
import { ResourceBar } from "./gui/resource_bar"
import { SquadWidget } from "./gui/squad"
import { EmberRosterWidget } from "./gui/ember_roster"
import { FollowCamModal } from "./gui/follow_cam"
import {
    asMinimapSize,
    clampPanelWithin,
    collectMinimapTerrain,
    DEFAULT_MINIMAP_SIZE,
    EmberMinimapWidget,
    MinimapSize,
    PanelRect,
    rectsOverlap,
} from "./gui/ember_minimap"
import { EmberInventoryChip } from "./gui/ember_inventory_chip"
import { EmberMinimapChip } from "./gui/ember_minimap_chip"
import { EmberFireGauge } from "./gui/ember_fire_gauge"
import { WaveLandingFlash } from "./gui/wave_landing_flash"
import { EmberPlayersChip } from "./gui/ember_players_chip"
import { EmberPlayersPanel } from "./gui/ember_players_panel"
import { EmberInvitePrompt } from "./gui/ember_invite_prompt"
import { StickyBanner } from "./gui/sticky_banner"
import { Throbber } from "./gui/throbber"
import { Tutorial } from "./gui/tutorial"
import { Typing } from "./gui/typing"
import { Mouse } from "./input/mouse"
import { FloatingJoystick, FloatingSticks } from "./input/floating_sticks"
import { Intro } from "./intro/intro"
import { MinimapSurface } from "./minimap_surface"
import { findRendererApp, getRendererApp } from "./renderer_app"
import { SpriteLabPanel } from "./gui/spritelab_panel"
import { SpriteLabState } from "game-common/spritelab"
import { BasicSprite } from "./sprites/basic_sprite"
import { TextRenderer } from "./text/text_renderer"
import { WorldMap } from "./gui/world_map"

interface IncomingMessageMeta {
    unread: boolean
    flasherInterval: any
    expires: number
    text: ScreenMessage
}

export const BOTTOM = 460

// How far the HUD's two bottom corners are inset from the edges of the screen. The ember roster has
// always kept this margin on the right; the readied-item tray keeps the same one on the left, so the
// two corners are inset by the same amount rather than by two numbers that drifted apart.
export const HUD_EDGE_MARGIN = 6

// Where the `f` frame-rate readout parks: top band, clear of the health meters on the left and the
// day/night dial on the right.
const FPS_METER_X = 130
const FPS_METER_Y = 4
const FPS_METER_KEY = "_fpsMeter_"

// The top-right chip row: the map toggle, the inventory toggle, the players count and the chat
// button, and the gap between them. Ember rooms suppress the control bar that normally lives up
// there, so the corner is theirs.
const CHIP_ROW_Y = 6
// Air between the camp-tasks stack and whatever is above it in the top-left column.
const OBLIGATIONS_GAP = 10
/**
 * How long the camp-tasks stack stays folded after the `important` strip falls quiet.
 *
 * Long enough to cover the strip's own ~400ms fade-out, so the rows do not unfold through a notice
 * that is still on screen, plus the lane's 220ms handover -- which is what stops a burst of wave
 * banners flickering the stack open and shut between each one.
 */
const OBLIGATIONS_YIELD_MS = 600
const chipGap = () => chipMetrics().rowGap

// The player's own VITALS, docked to the top edge of the readied-item tray: hearts, then whichever
// of the mana and hunger bars the room has not suppressed, laid out left to right on ONE line.
//
// They used to head the top-left column, which put the three things a player reads mid-fight -- own
// health, own items, crew health -- in three different corners. Here the first two are one cluster
// under the hand that is already on the number keys.
//
// A row, not a stack: stacked, the three meters are ~60px tall and that whole band comes out of
// chat's column. The band is RESERVED whether or not a bar is currently in it, for the same reason
// the chip row reserves each chip's slot -- chat must not slide about as a meter appears.
//
// 32 comes from the art, MEASURED in the browser rather than guessed: the hearts draw 100x22.5
// (five 15x13 hearts on their own plate, see icon_meter.ts) and the bars' icons are 16x16 and 19x12
// (humanoid.json), so the tallest thing on the row is 22.5; plus VITALS_GAP that is 27.5, and the
// rest is air under chat's input field. Widthwise the three come to ~266 against a 318-wide tray,
// so a full row never reaches the abilities widget parked off the tray's right edge.
const VITALS_BAND = 32
// Air between the tray's top edge and the row, and between the meters along it.
const VITALS_GAP = 5
const VITALS_ROW_GAP = 8
// Air kept inside the row's plate at its right-hand end. Matches the padding the hearts' plate
// already keeps around the art at the other end (icon_meter.ts PLATE_PAD).
const VITALS_PLATE_PAD = 4

// The top of the countdown dial, which now heads the top-left column on its own. Clear of the
// name/level label that shares the top band with it (nameText, at x 90).
// Where the HUD's top band starts. Only a construction-time default now: hudTopEdge is what both
// of the band's clusters actually read, so the one that is placed per frame cannot drift from the
// one that is not. (It WAS 24 against the chip row's 6, which put the whole left column 18px below
// the chips -- at a glance, like the left half of the HUD had slipped down.)
const DIAL_TOP = CHIP_ROW_Y
// The fire gauge's gap from whatever is above it in the left column, and how fast it slides when
// that changes (px/ms: a dial's height in about a fifth of a second).
const FIRE_GAUGE_GAP = 9
const FIRE_GAUGE_SLIDE = 0.62

// Chat's ambient home, bottom-left: the input field's top edge, with the log hanging off it. The
// field's bottom has to clear the vitals row docked on the readied-item tray.
const CHAT_LEFT = 15
const CHAT_INPUT_TOP = BOTTOM - 74 - VITALS_BAND
const CHAT_LOG_BOTTOM = CHAT_INPUT_TOP - 6

// Where the log goes while the composer is open: directly under that panel, centred on it, growing
// downward. The bottom-left corner is no use there -- it is what a soft keyboard covers, and on a
// phone it is also under the move stick.
const COMPOSER_LOG_X = COMPOSER_PANEL_X + (COMPOSER_PANEL_W - CHAT_WIDTH) / 2
const COMPOSER_LOG_TOP = COMPOSER_PANEL_BOTTOM + 8

// How much bigger the readied-item slots are under a thumb, and how far the row is lifted off the
// bottom edge to make room for the growth. See where the widget is built.
const EQUIPPED_TOUCH_SCALE = 1.5

// The tether from a per-entity command wheel to the NPC it commands. Semi-transparent so it reads as
// a link rather than another piece of HUD, and started off the rim so it doesn't cut across the disc.
const WHEEL_TETHER_ALPHA = 0.45
const WHEEL_TETHER_WIDTH = 2
const WHEEL_TETHER_RIM_GAP = 4 // how far outside the wheel's rim the line begins
const WHEEL_TETHER_MIN_LENGTH = 6 // shorter than this and the line is just a nub: don't draw it
const WHEEL_TETHER_DOT_RADIUS = 3 // marker pinning the far end to the NPC's center

// The command wheel's ring. Its name plaques sit outside this, so the wheel is laid out against the
// screen by CommandWheel.getExtent, never by the radius.
const COMMAND_WHEEL_RADIUS = 64

// The one slice on an interaction wheel that is not one of the entity's verbs. A card's tap belongs
// to what you can DO with the person; this is how the same tap still reaches the follow cam, which
// is otherwise only on the lord's and a partner's cards. Prefixed so it can never collide with an
// InteractionType coming off the wire.
const WATCH_SLICE = "__watch"
// How far the subject of a pinned panel may drift from that panel's tail tip before the tail stops
// telling the truth -- half a tile, i.e. they are still standing on the spot the panel opened over.
// In stage pixels, which the world overlay puts at surfaceZoom to the world unit (computeLocation).
const PANEL_TAIL_SLACK = (TileSize / 2) * ScalingParams.surfaceZoom
// The joysticks report one of eight compass sectors, and the game moves in those same eight
// directions (see Directions in game-common/models), so each sector maps to the per-axis -1/0/1
// vector the equivalent WASD combination would produce.
const JoystickVectors: Record<string, Coordinate> = {
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
    top: { x: 0, y: -1 },
    bottom: { x: 0, y: 1 },
    top_left: { x: -1, y: -1 },
    top_right: { x: 1, y: -1 },
    bottom_left: { x: -1, y: 1 },
    bottom_right: { x: 1, y: 1 },
}

// Fraction of the stick's travel that reads as "no input". The move stick used to demand 0.9,
// which left only the outermost tenth of the travel usable and let ordinary thumb jitter fall
// out of it mid-drag.
const JoystickDeadZone = 0.3

export class GuiManager implements IGuiRenderingManager {
    stage: Container
    minimapSurface: MinimapSurface

    // chat
    typing: Typing
    chatHistory: ChatHistory
    // The touch way into chat: a real DOM field docked at the top of the screen, opened from the
    // SAY chip / the control bar's chat button. Never up at the same time as `typing`.
    chatComposer: ChatComposer
    chatChip: ChatChip
    chatHistoryTextTimer: any
    chatHistoryFader: Fader | null = null
    chatLog: ChatLogEntry[] = []
    chatInput: ChatInputState = EMPTY_INPUT
    chatWalk: ChatHistoryWalk = EMPTY_WALK
    // Whether the input currently owns the keyboard. The log does not fade itself out while it
    // is open: a line arriving mid-sentence must not take the conversation away from the player
    // who is in the middle of answering it.
    private chatOpen: boolean = false
    private readonly CHAT_HISTORY_KEY = "_chatHistory_"

    // screen messages
    bannerText: BitmapText
    screenMessages: ScreenMessage[] = []
    currentScreenMessage: ScreenMessage | null = null
    currentMessageTimeout: any = null
    importantMessage: ImportantMessage

    // player
    // meters
    hp: IconMeter
    mana: StaminaMeter
    hunger: ManaMeter
    nameText: Text

    // resource bar
    resourceBar: ResourceBar

    // leaderboard
    leaderboard: LeaderboardWidget

    // equipped
    equipped: EquippedWidget
    slotCallbacks: Record<number, Callback<void>>

    // squad
    squad: SquadWidget
    private recruitMenuHint = new RecruitMenuHint(
        () => this.logic.playerController?.suppressAttack(),
        entityId => this.recordRecruitMenuLearned(entityId, "RECRUIT_MENU_HINT_SHOWN"),
    )
    private campfireMenuLearnedLocally = false
    private recruitHintRoster: EmberRosterEntry[] = []
    private recruitMenuLearnedLocally = false
    private recruitLessonPlayerId?: EntityId

    emberAnxietyMeter: EmberAnxietyMeter
    emberRoster: EmberRosterWidget
    followCam: FollowCamModal
    private followCamPlacement: Coordinate | null = null
    // Fade state for the follow-cam: `followCamShown` is the current target (null forces the first
    // evaluation after open to run a fade); `followCamFader` is the active alpha tween.
    private followCamShown: boolean | null = false
    private followCamFader: Fader | null = null
    // Ember-room minimap. Shares the HUD slot above the roster strip with the follow-cam PiP; the
    // PiP wins it whenever a roster member is being tracked.
    emberMinimap: EmberMinimapWidget
    // The pill the collapsed map leaves in the top-right corner, and the only way back to it.
    emberMinimapChip: EmberMinimapChip
    // The way into the pack while the control bar is suppressed, which ember rooms do -- taking the
    // usual inventory button with it. Sits in the same top-right row as the map and players toggles.
    emberInventoryChip: EmberInventoryChip
    // "Who else is playing": the count chip beside the map toggle, and the list it opens down the
    // right-hand edge. Ember-room only, and the chip only while there IS somebody else logged in.
    emberPlayersChip: EmberPlayersChip
    emberPlayersPanel: EmberPlayersPanel
    // The last list the server sent, kept so opening the panel draws the current one rather than
    // waiting for the next broadcast.
    private emberPlayers: EmberPlayerEntry[] = []
    // An invitation into somebody else's camp, awaiting this player's answer. Sits between the chip
    // and the list in the same column.
    emberInvitePrompt: EmberInvitePrompt
    // Who the outstanding invitation is from, so an answer can be addressed back to them. The prompt
    // draws the NAME; the id never has to be on screen, so it lives here rather than in the widget.
    private emberInviteFrom?: EntityId
    // Whether the player has folded the map away. Remembered across rooms and sessions: an ember run
    // drops you into a FRESH room on every death and retry, so a preference that reset each time
    // would be no preference at all.
    private emberMinimapCollapsed = false
    // Open once per rescue, while allowing the player to close it again afterward.
    private emberMinimapRescueId?: EntityId
    private emberMinimapBonecallerId?: EntityId
    private readonly EMBER_MINIMAP_COLLAPSED_KEY = "_emberMinimapCollapsed_"
    private readonly EMBER_OBLIGATIONS_COLLAPSED_KEY = "_emberObligationsCollapsed_"
    // The queue in front of the one-slot `important` strip. See gui/important_lane.ts: several ember
    // beats land on the same wave boundary by construction, and before this the later one erased the
    // earlier one mid-hold.
    private importantLane = new ImportantLane()
    // The camp's standing tasks, and which of them the player has waved away.
    private emberObligations: EmberObligationsPanel
    private emberObligationDismissals = new ObligationDismissals()
    private emberObligationFeed: Obligation[] = []
    /** Time left before the task stack may unfold again; see OBLIGATIONS_YIELD_MS. */
    private obligationsYieldMs = 0
    // Where the player has DRAGGED the map to, in this HUD container's own coordinates, or null while
    // it is still parked in its default slot above the roster. Remembered for the same reason the
    // collapsed flag is: a run hands out a fresh room on every death and retry.
    private emberMinimapPlacement: Coordinate | null = null
    private readonly EMBER_MINIMAP_PLACEMENT_KEY = "_emberMinimapPlacement_"
    // Which of the three sizes the map is at. Remembered like the other two map preferences: a player
    // who decided the map is a radar should not have to decide it again next session.
    private emberMinimapSize: MinimapSize = DEFAULT_MINIMAP_SIZE
    private readonly EMBER_MINIMAP_SIZE_KEY = "_emberMinimapSize_"
    // The SpriteLab room's panel (see updateSpriteLab). Where it was dragged to, and whether it was
    // folded or had its creations list open, are per-viewer conveniences remembered like the map's.
    private spriteLabPanel?: SpriteLabPanel
    private spriteLabPlacement: Coordinate | null = null
    private readonly SPRITELAB_PANEL_KEY = "_spriteLabPanel_"
    // Cache key for the minimap's TREE layer, which the client scans off its own tile map rather than
    // being sent: room + the map's collision version (bumped on every tile write, so a felled tree or
    // a placed wall invalidates it) + the room's extent. Null means "not scanned yet".
    private emberTreeScanKey: string | null = null
    // ...plus a floor on how often that O(tiles) scan may re-run, so a burst of tile edits (placing a
    // wall writes several cells) can't rescan on every broadcast. Low-end CPUs are the target here.
    private emberTreeScanAt = 0

    // abilities
    abilities: AbilitiesWidget

    // countdown timer
    countdownTimer: CountdownTimer
    // The camp's fire, continuously, in the slot under the dial that the wave pill used to occupy.
    emberFireGauge: EmberFireGauge
    // Whether the column had a dial at its head last frame, and whether the gauge is still sliding
    // to the place that answer gave it. Null until the first frame places it, which has nothing to
    // slide from. See placeEmberFireGauge.
    private fireGaugeHeaded: boolean | null = null
    private fireGaugeSliding = false
    // The world's half of a wave landing (the HUD's half is the dial handing over to the badge).
    waveLandingFlash: WaveLandingFlash
    // The wave this client has already played the landing for, and the wave it is currently
    // counting DOWN to. The second is what says this client watched the thing arrive: the camp
    // names the coming wave on the countdown message, so a status that matches it is the landing
    // this player sat through. Both undefined means nothing seen in this room yet, which is what
    // stops somebody who walks INTO a camp mid-assault taking a hit for a wave they never saw.
    private wavePlayed?: number
    private waveAwaited?: number

    // ember wave badge — shares the countdown dial's slot, and takes it over once the wave lands
    waveIndicator: WaveIndicator

    // command wheel
    // yOffset: how far the tile's storey is drawn from the player's; the wheel sits over the drawn tile.
    private tileWheelTarget?: { coordinate: Coordinate; layerId: string; roomId: string; yOffset?: number }
    private tileWheelInteractionsKey = ""
    commandWheel: CommandWheel
    private commandWheelHome = { x: 0, y: 0 }
    // The line tying a per-entity wheel back to the NPC it commands. The wheel is pinned where it
    // opened, so this is what keeps "which unit am I ordering" legible once the NPC walks away.
    private commandWheelTether: Graphics
    // Set while the wheel was opened from a ROSTER CARD rather than from the NPC itself. Such a wheel
    // is parked in the HUD slot above the strip (not over the NPC, who may well be off screen, which
    // is the whole reason the roster exists) and is tied back to its card instead of to the world.
    private commandWheelRosterId?: EntityId
    // Set alongside commandWheelRosterId when that roster wheel is carrying an ENTITY'S OWN VERBS
    // rather than squad orders (a visitor's card). Held separately because the two wheels answer a
    // selection on different channels, and because this one has to be relabelled as the player
    // walks (see refreshRosterInteractionReach).
    private rosterInteractionId?: EntityId
    private rosterInteractionName?: string
    // Whether the player was in reach of it the last time the slices were written.
    private rosterInteractionInReach = false
    // Set when the role picker was asked for from a ROSTER-opened command wheel, and therefore belongs
    // in the same HUD slot that wheel was in rather than over its member. A member reached through the
    // roster is usually one you cannot see -- and a "Change Role" order is exactly the moment it walks
    // off -- so a picker glued to the entity would open on bare ground or off screen entirely.
    private rolePickerRosterId?: EntityId
    // That line. It lives in the HUD container UNDER the roster strip, so the panel, the cards and
    // anything the strip draws over itself -- a speech bubble above all -- read over the top of it.
    private rosterWheelConnector: Graphics
    private rolePickerConnector: Graphics

    // recruit role picker (floats over a freshly-arrived recruit; assigns forager/defender)
    private emberInviteModal: EmberInviteModal
    rolePicker: RolePicker
    // A conversation with an NPC (gui/conversation_panel.ts): the one thing on screen while it is open.
    conversationPanel: ConversationPanel
    private stairPickerMessage?: ScreenMessage
    namePrompt: NamePrompt

    // ember end-of-run scorecard (centered; the only way back into a run after dying in the camp)
    runSummary: RunSummary

    // The invasion handshake (see gui/invasion_picker.ts): the camp-chooser the scorecard's third
    // button opens, and the small "Let go" button that exists only while wearing somebody else's
    // attacker.
    invasionPicker: InvasionPicker
    possessionSplash: PossessionSplash
    // Last invading verdict seen, so the scorecard is dismissed -- and each end of a visit
    // announced -- on the TRANSITION rather than on every message that arrives during one.
    private invading = false
    letGoButton: LetGoButton
    // The newest invasion offer off the feed. `undefined` means the feed has never spoken, which is
    // a different answer from an empty list -- see runSummaryButtons, which that distinction is the
    // whole rule of.
    private invasionCandidates?: EmberInvasionCandidate[]
    // On phones, crafting hides the ready bar. Keep the latest craft cue until it comes back.
    private pendingReadyBarPulseItemId?: EntityId

    // first-run ember tutorial invitation (a low bar with show-me/skip and a draining countdown)
    private chapterChecklist: ChapterChecklist
    private chapterCelebration: ChapterCelebration
    // How much of the HUD is on screen (gui/hud_presence.ts): the pieces held back, set by signals --
    // the server's "hud" screen messages, decided by the story chapter -- and the step back while a
    // held line is read. Read last in every frame, over everyone's own layout; the GUI decides none
    // of it itself.
    readonly hudPresence = new HudPresence()
    // The story's controls lessons: walking on the wreck beach, then attacking, the ready bar and
    // interacting in chapter one (gui/controls_lesson.ts). The server asks for each; the player's own
    // input runs it.
    readonly controlsLesson = new ControlsLesson()
    // Whether the player's screen is quiet, as the server needs to know before it sends anything
    // optional their way -- the feedback rabbit (see gui/quiet_watch.ts and updateQuietWatch).
    readonly quietWatch = new QuietWatch()
    private quietHp?: { entityId: string; hp: number }
    private quietSocket?: unknown
    private lastHurtTs = 0
    // The animated lesson windows' layer (tutorial_demo.ts): one up is the player being taught.
    private tutorialDemoLayer?: Container
    private controlsLessonLastAt?: { x: number; y: number }
    chapterAnnouncement: ChapterAnnouncement
    tutorialPrompt: TutorialPrompt

    // hero message
    heroMessage: HeroMessage

    private currentPlayerUpgradePoints: number = 0
    private currentPlayerName: string = ""
    private currentPlayerHp: number = 0
    private currentPlayerHpMax: number = 0
    // Tracked alongside hp because a player can be stood back up without their hp changing, and the
    // hearts have to refill on that alone.
    private currentPlayerDowned: boolean = false
    private currentPlayerHunger: number = 0
    private currentPlayerHungerMax: number = 0
    private currentPlayerMana: number = -1
    private currentPlayerManaMax: number = -1
    private currentScore: number

    logic: ClientGameLogic
    private currentRoomId: EntityId
    private currentSceneId?: EntityId
    private currentSceneGeneration?: number

    private acceptsSceneRoom(roomId?: EntityId): boolean {
        return !roomId || roomId === this.currentRoomId || !!this.logic.scene?.contains(roomId)
    }
    // A camp's checklist and HUD list that arrived before the client had entered that camp, by target
    // (see addScreenMessage).
    private earlySceneMessages = new Map<string, ScreenMessage>()
    private currenRoomName?: string
    private roomNameHidden: boolean

    private leftJoystickAngleAllowed: boolean = true
    private inventoryUpdateTs: Map<EntityId, number> = new Map()

    // blackout
    blackout: Graphics

    // redout
    redout: Graphics

    private container: Graphics
    private incomingMessageMeta: IncomingMessageMeta

    private currentModal: DynamicDialog | null
    private upgradeIndicator: boolean
    private joystickSetup: boolean
    private touchSticks?: FloatingSticks
    private characterCustomizer: CharacterCustomizer
    private characterBuilder: CharacterBuilder
    private chipLayer?: Container
    private controlBar: ControlBar
    private stickyBanner: StickyBanner
    private userSuppressMinimap: boolean = false
    private tutorial: Tutorial
    private dayCycle: DayCycle
    // The `f` frame-rate readout. Away until somebody asks for it, and costs nothing until then.
    private fpsMeter: FpsMeter
    // Per-room flag: when true, the day/night indicator stays hidden even as day-pct updates arrive.
    private suppressDayCycle: boolean = false
    // Per-room flag: when true, the "Level up stats!" button is not built in helpMessaging().
    private suppressLevelUp: boolean = false
    // The buttons the LAST helpMessaging() build put on screen. Held only so the next build can give
    // the world's mouse back on their behalf before they are removed -- see helpMessaging().
    private helpMessagingButtons: UiButton[] = []
    // Per-room flags: when true, the mana meter / food (hunger) meter stay hidden even as
    // update() receives new player mana/hunger values.
    private suppressMana: boolean = false
    private suppressFood: boolean = false
    // Per-room flags: when true, the shard/resource counter (resourceBar) / the player name+level
    // label stay hidden even as updateInventory()/update() would otherwise show them.
    private suppressShards: boolean = false
    private suppressPlayerName: boolean = false
    // Per-room flag: when true, a guest's standing "/register to save progress" nudge is left out of
    // the help-messaging stack. Ember rooms set it -- see Visibility.suppressGuestPrompt.
    private suppressGuestPrompt: boolean = false

    private dynamicDialogToken: string
    // The open screen's subject, once it has been in the player's view (see closeScreenOffSubject).
    private screenSubjectSeen?: EntityId
    private featureWidget: FeatureWidget
    private mouseHoverElement: Graphics

    private loggingIn: boolean
    private intro: Intro
    private hudInset = 0
    private hudInsetY = 0
    private mousePointer: TargetingReticle
    private buildingPrintCountdown: BuildingPrintCountdown
    private buildingShapePalette?: BuildingShapePalette
    private mobileBuildToolbar?: MobileBuildToolbar
    private locationPicker: LocationPicker
    private controlKeyPressed: boolean = false

    constructor(stage: Container, minimapSurface: MinimapSurface, logic: ClientGameLogic) {
        this.stage = stage
        this.minimapSurface = minimapSurface
        this.logic = logic
        this.incomingMessageMeta = {
            unread: false,
            flasherInterval: null,
            expires: null,
            text: null,
        }
        this.currentModal = null

        this.upgradeIndicator = false
        this.minimapSurface.register(this.mapPointHoverListener)

        // Load chat history from localStorage
        this.loadChatHistory()
    }

    init = () => {
        // meters

        const container = new Graphics()
        this.container = container
        container.sortableChildren = true
        container.x = 10
        container.zIndex = 2
        this.stage.addChild(container)

        // These toggles remain reachable above item dialogs, including when their X is hidden.
        // Match the HUD transform so the chip row and tutorial anchors keep their coordinates.
        const chipLayer = new Container()
        this.chipLayer = chipLayer
        chipLayer.position.copyFrom(container.position)
        chipLayer.zIndex = 4
        chipLayer.visible = false
        this.stage.addChild(chipLayer)

        const bannerText = new BitmapText("", {
            fontName: "BannerFont",
        })
        bannerText.y = 50
        this.bannerText = bannerText
        container.addChild(bannerText)

        container.addChild(this.minimapSurface)
        const screenWidth = ScreenDimensions.w + TileSize * 4.7

        // message — the ambient "important" strip self-positions across the full viewport,
        // so it lives on the stage (not the +10 offset HUD container) at its own zIndex.
        const importantMessage = new ImportantMessage(0xffffff, {
            text: "",
        })
        this.importantMessage = importantMessage
        this.stage.addChild(importantMessage)

        // The standing counterpart to the strip above it: what is still true, rather than what just
        // changed. Hidden until the camp owes the player something; placed by placeEmberObligations.
        //
        // On the STAGE rather than the HUD container, because it continues the top-left column the
        // countdown dial and the let-go button are already in (both of which live here), and
        // measuring itself against those two is only honest in their own coordinate space.
        const emberObligations = new EmberObligationsPanel(
            obligation => this.dismissEmberObligation(obligation),
            collapsed => this.saveEmberObligationsCollapsed(collapsed),
        )
        emberObligations.setCollapsed(this.loadEmberObligationsCollapsed())
        // Above the `important` strip (55000), which is a full-width band crossing the row the open
        // rows hang into. The band is translucent and transient; what is STANDING has to stay
        // readable through it, so the dropdown wins that overlap rather than being greyed out by a
        // notice about something else. Still below the hero banner and the countdown dial's own
        // slot (60000), which nothing should ever cover.
        emberObligations.zIndex = 56000
        this.emberObligations = emberObligations
        // Keep camp tasks off the stage for now; the chapter checklist provides progress guidance.

        // The three vitals. Where they GO is positionVitals' job alone -- they are docked to the
        // readied-item tray, which is not built yet at this point in the constructor -- so nothing
        // here places them. The old code seeded an x off `this.stage.width / 2`, measured on an
        // all-but-empty stage, and then relied on the edge-inset shifts to drag it somewhere usable.
        const hp = new IconMeter("heart", 0)
        this.hp = hp
        container.addChild(hp)

        const mana = new StaminaMeter()
        mana.visible = false
        this.mana = mana
        container.addChild(mana)

        const hunger = new ManaMeter(0xc4a484, 19, "drumstick-brown.png")
        hunger.visible = false
        this.hunger = hunger
        container.addChild(hunger)

        // Chat, bottom-left: the input field, with the log hanging off its top edge. Both are the
        // same width and share a left edge, so they read as one component (gui/chat_theme.ts).
        const chatLeft = CHAT_LEFT
        const chatInputTop = CHAT_INPUT_TOP
        this.typing = new Typing()
        this.typing.x = chatLeft
        this.typing.y = chatInputTop
        this.typing.visible = false
        this.typing.zIndex = 3
        this.stage.addChild(this.typing)

        // The log sizes itself and hangs UP from this line, so a long conversation grows into the
        // empty grass above instead of pushing the field down over the hotbar.
        const chatHistory = new ChatHistory(CHAT_LOG_BOTTOM)
        chatHistory.zIndex = 3
        chatHistory.x = chatLeft
        chatHistory.y = CHAT_LOG_BOTTOM
        this.chatHistory = chatHistory
        this.stage.addChild(chatHistory)

        // The composer: the field a phone can actually type into. Above the command wheel / name
        // prompt tier (it dims the screen and takes the keyboard), below the end-of-run panels,
        // which are never up at the same time as a chat field.
        this.chatComposer = new ChatComposer({
            onSend: message => {
                this.dismissChatComposer()
                this.logic.playerController?.client?.sendChatMessage(message)
            },
            onDismiss: () => this.dismissChatComposer(),
        })
        this.chatComposer.zIndex = 60500
        this.stage.addChild(this.chatComposer)
        this.exposeChatDevTriggers()

        // leaderboard
        this.leaderboard = new LeaderboardWidget(this)
        this.leaderboard.x = ScreenDimensions.w - 25
        this.leaderboard.y = 0
        this.stage.addChild(this.leaderboard)

        // equipped
        this.equipped = new EquippedWidget(this, {
            interaction: getRendererApp().renderer.plugins.interaction,
            layer: this.stage,
            isWorldDrop: point => getRendererApp().screen.contains(point.x, point.y) && Mouse.isWorldPoint(point),
            drop: (itemId, at) => {
                this.logic.playerController.suppressAttack()
                this.logic.playerController.client.inventoryAction(this.logic.playerEntity.entityId, itemId, "drop",
                    undefined, (at && this.worldPointAt(at)) || undefined)
            },
        }, { showKeys: !isMobile() })
        // The readied slots are the only way to swap weapon under a thumb, and at 1x they are a
        // ~32px icon on a 640x360 stage letterboxed down to a phone -- roughly 20 CSS px, and a
        // miss as often as a hit. Scaling the widget takes the slot art, the spacing and the tap
        // targets with it, the same way the control bar is scaled up on touch just below.
        // Anchored bottom-left off BOTTOM, so growing it reaches UP, into empty grass.
        if (isMobile()) {
            this.equipped.scale.set(EQUIPPED_TOUCH_SCALE)
        }
        // Inset from the LEFT edge of the screen by exactly what the ember roster is inset from the
        // right (see positionEmberRoster). Both are children of `container`, which is itself offset,
        // so the margin is measured in screen space and the parent's offset taken back out -- the old
        // `container.x + 10` was a container-local number that put the tray at 30 on screen while the
        // roster sat at 6 on the other side.
        this.equipped.x = HUD_EDGE_MARGIN - container.x
        // Anchored off the tray's KNOWN height rather than its measured one. The widget is empty
        // until the first inventory arrives, so measuring it here reads 0; the old line compensated
        // with a constant and then needed a second constant to undo what the touch scale did to the
        // guess. TRAY_HEIGHT is fixed by construction (gui/equipped.ts), so the tray can simply sit
        // on the bottom line, at either scale.
        this.equipped.y = BOTTOM - TRAY_HEIGHT * (isMobile() ? EQUIPPED_TOUCH_SCALE : 1)
        container.addChild(this.equipped)
        // The vitals dock to the tray's top edge, so they can only be placed once it exists.
        this.positionVitals()

        // squad
        this.squad = new SquadWidget(this)
        this.squad.x = 390
        this.squad.y = BOTTOM - this.squad.height - 20
        container.addChild(this.squad)

        // The connector from a roster-opened command wheel down to the card that opened it. Added
        // BEFORE the roster so the strip -- and the speech bubbles it floats above itself -- draw
        // over the line rather than the line over them.
        this.rosterWheelConnector = new Graphics()
        this.rosterWheelConnector.visible = false
        container.addChild(this.rosterWheelConnector)

        // The same line for the ROLE PICKER a roster wheel can raise. Its own Graphics rather than a
        // share of the one above: updateCommandWheelTether wipes that one on every frame the wheel is
        // closed, which is every frame the picker is up.
        this.rolePickerConnector = new Graphics()
        this.rolePickerConnector.visible = false
        container.addChild(this.rolePickerConnector)

        // ember roster (clickable NPC portraits; only populated in ember rooms). Anchored
        // bottom-right; repositioned as the card count changes (see positionEmberRoster).
        this.emberAnxietyMeter = new EmberAnxietyMeter()
        container.addChild(this.emberAnxietyMeter)
        this.emberRoster = new EmberRosterWidget(this)
        container.addChild(this.emberRoster)
        this.stage.addChild(this.recruitMenuHint)
        this.positionEmberRoster()

        // follow-cam PiP modal (opened by clicking a roster portrait). Positioned above the roster
        // on open (see positionFollowCam).
        this.followCam = new FollowCamModal(this, {
            onGrab: this.holdOffMinimapDragAttack,
            onDragTo: this.dragFollowCamTo,
            onRelease: this.holdOffMinimapDragAttack,
            onResize: () => this.dragFollowCamTo(this.followCam.x, this.followCam.y),
        })
        this.followCam.zIndex = 2
        container.addChild(this.followCam)

        // ember minimap — under the MAP toggle by default, and anywhere on screen once the
        // player has dragged it there (see dragEmberMinimapTo). Stays hidden until the first per-room
        // broadcast arrives, and steps aside only for a panel it actually overlaps (see
        // positionEmberMinimap). Three sizes, cycled from its own corner control.
        //
        // Every one of the three preferences — folded away, parked where, and how big — is loaded
        // BEFORE the widget goes on screen, so the map never flashes up at the default and then
        // jumps to what the player chose last session.
        this.emberMinimapCollapsed = this.loadEmberMinimapCollapsed()
        this.emberMinimapPlacement = this.loadEmberMinimapPlacement()
        this.emberMinimapSize = this.loadEmberMinimapSize()
        this.emberMinimap = new EmberMinimapWidget(
            {
                onGrab: this.holdOffMinimapDragAttack,
                onDragTo: this.dragEmberMinimapTo,
                onRelease: this.dropEmberMinimap,
            },
            this.onEmberMinimapResized,
        )
        this.emberMinimap.setSize(this.emberMinimapSize)
        container.addChild(this.emberMinimap)

        // SpriteLab rooms only; stays hidden until the server's first status reply.
        this.spriteLabPanel = this.createSpriteLabPanel()
        container.addChild(this.spriteLabPanel)
        // The map's toggle. Unlike the panels it sits beside in the chip row, it is on screen whether
        // the map is folded or not: it is the one place that says the map HAS an open state, which a
        // control that only existed while the map was hidden could never say.
        this.emberMinimapChip = new EmberMinimapChip(this.toggleEmberMinimap)
        chipLayer.addChild(this.emberMinimapChip)

        // Same inventory the control bar's button opens; this chip is simply where it lives when
        // the room has hidden that bar.
        this.emberInventoryChip = new EmberInventoryChip(this.toggleInventory)
        chipLayer.addChild(this.emberInventoryChip)

        // The chat button, FOR TOUCH ONLY (chatChipWanted). Same argument as the items chip beside
        // it: a camp suppresses the control bar, and chat has only ever been opened with a key --
        // which a phone does not have. A keyboard does, so there the chip was a button repeating a
        // key the player already had, in the one corner of the screen the camp HUD is short of.
        this.chatChip = new ChatChip(() => this.logic.playerController?.requestChat())
        chipLayer.addChild(this.chatChip)

        // "Who else is playing" -- the count chip sits immediately to the LEFT of the map toggle in
        // the top-right corner, and opens the list of online players down the right-hand edge below
        // it. Both stay hidden until the server says there is somebody else to show.
        this.emberPlayersPanel = new EmberPlayersPanel({
            onInvite: entityId => {
                const player = this.emberPlayers.find(entry => entry.entityId === entityId)
                if (!player || player.here) return
                this.hideCommandWheel()
                this.hideRolePicker()
                this.positionEmberInviteModal()
                this.emberInviteModal.open(entityId, player.name)
            },
        })
        this.emberInviteModal = new EmberInviteModal((entityId, playMode) => {
            const player = this.emberPlayers.find(entry => entry.entityId === entityId)
            if (!player || player.here) return
            this.inviteEmberPlayer(entityId, playMode)
            this.emberPlayersPanel.markInvited(entityId)
        })
        this.emberInviteModal.zIndex = 65000
        this.stage.addChild(this.emberInviteModal)
        container.addChild(this.emberPlayersPanel)
        this.emberPlayersChip = new EmberPlayersChip(() => this.setEmberPlayersOpen(!this.emberPlayersPanel.visible))
        chipLayer.addChild(this.emberPlayersChip)
        // The invitation prompt. Unlike the chip and the list this is NOT ember-only: an invitation
        // finds the player wherever they are, which is the point of being invited somewhere.
        this.emberInvitePrompt = new EmberInvitePrompt({ onAnswer: this.answerEmberInvite })
        container.addChild(this.emberInvitePrompt)

        // abilities
        this.abilities = new AbilitiesWidget(this)
        this.abilities.x = 390
        this.abilities.y = this.equipped.y
        container.addChild(this.abilities)

        // player
        this.nameText = new TextRenderer().color(0x00ff00).render() as Text
        this.nameText.y = 1
        this.nameText.x = 90
        container.addChild(this.nameText)

        // resource bar
        this.resourceBar = new ResourceBar(this)
        this.resourceBar.visible = true
        this.resourceBar.setup()
        this.resourceBar.x = this.nameText.x + 5
        this.resourceBar.y = this.nameText.y
        container.addChild(this.resourceBar)

        const blackout = new Graphics()
        blackout.x = 0
        blackout.y = 0
        blackout.lineStyle(2, 0x000000)
        blackout.beginFill(0x000000)
        blackout.drawRect(0, 0, 1000, 1000)
        blackout.visible = true
        blackout.alpha = 1.0
        blackout.endFill()
        this.blackout = blackout
        this.stage.addChild(blackout)

        const redout = new Graphics()
        redout.x = 0
        redout.y = 0
        redout.lineStyle(2, 0xff0000)
        redout.beginFill(0xff0000)
        redout.drawRect(0, 0, 1000, 1000)
        redout.visible = false
        redout.alpha = 0.3
        redout.endFill()
        this.redout = redout
        this.stage.addChild(redout)

        this.minimapSurface.x = ScreenDimensions.w + 40
        this.minimapSurface.y = BOTTOM - 105

        this.characterCustomizer = new CharacterCustomizer(this, this.logic)
        this.characterBuilder = new CharacterBuilder(this, this.logic)

        const controlBar = new ControlBar(this)
        if (isMobile()) {
            controlBar.scale.set(1.5)
        } else {
            controlBar.scale.set(0.7)
        }
        controlBar.setup(
            () => {
                this.logic.invokeInventory(null)
            },
            () => {
                this.logic.invokeQuests()
            },
            () => {
                this.invokeFeedback()
            },
            () => {
                this.logic.playerController.client.requestWorldMap()
            },
            () => {
                this.logic.playerController?.requestChat()
            },
        )

        controlBar.x = screenWidth - controlBar.width - 18
        controlBar.y = 4
        controlBar.visible = false
        this.controlBar = controlBar
        this.container.addChild(controlBar)

        const stickyBanner = new StickyBanner(this)
        stickyBanner.setup()

        stickyBanner.y = TileSize * 0.07
        stickyBanner.x = controlBar.x - stickyBanner.width - TileSize * 0.15

        this.stickyBanner = stickyBanner
        this.container.addChild(stickyBanner)

        // The animated helper windows (tutorial_demo.ts) and the dimming the rest of the screen takes
        // behind them. Over every HUD panel (the checklist, the wave dial, the obligations), so all of
        // it sits under the dim; under the command wheel and pickers a lesson sends the player to, the
        // chat composer, and every celebration and modal screen. In stage coordinates, which is what a
        // window places itself in.
        const tutorialDemoLayer = new Container()
        tutorialDemoLayer.zIndex = 59000
        this.stage.addChild(tutorialDemoLayer)
        this.tutorialDemoLayer = tutorialDemoLayer

        // The chip goes in because it is what the ember tutorial's first clue points at: a camp
        // suppresses the control bar, so the chip IS the Items button in there (see roomUpdated).
        this.tutorial = new Tutorial(
            this,
            this.container,
            this.equipped,
            this.controlBar,
            this.minimapSurface,
            this.emberInventoryChip,
            tutorialDemoLayer,
        )
        this.dayCycle = new DayCycle(this)
        this.dayCycle.visible = false
        this.dayCycle.x = stickyBanner.x - TileSize * 5.25
        this.dayCycle.y = stickyBanner.y + TileSize * 0.46
        this.container.addChild(this.dayCycle)
        this.dayCycle.setup(0.25 * 360)

        // The `f` frame-rate readout. On the STAGE rather than in the HUD container, at a zIndex
        // above everything including the room-load curtain and the day/night mask: a perf tool that
        // goes dark exactly when the client is working hardest would be useless. Parked in the top
        // band between the health meters (and, in a camp, the wave dial under them) and the
        // day/night dial, which is the one strip of HUD no screen puts anything in.
        this.fpsMeter = new FpsMeter()
        this.fpsMeter.x = FPS_METER_X
        this.fpsMeter.y = FPS_METER_Y
        this.fpsMeter.zIndex = 1000000
        this.stage.addChild(this.fpsMeter)
        try {
            if (typeof localStorage !== "undefined" && localStorage.getItem(FPS_METER_KEY) === "true") {
                this.fpsMeter.setOpen(true)
            }
        } catch (error) {
            console.warn("Failed to read the fps readout's state from localStorage:", error)
        }

        this.featureWidget = new FeatureWidget(this)
        this.featureWidget.x = 0
        this.featureWidget.y = 55
        this.container.addChild(this.featureWidget)

        // The pre-wave countdown dial.
        this.countdownTimer = new CountdownTimer({ radius: 50, strokeWidth: 7.5, textColor: 0xffffff })
        // Position timer on the top-left, directly under the HP bar and left-aligned to it.
        // The HP bar lives on `container` (offset by container.x), while the timer lives on the
        // stage, so add container.x to convert into stage space. The dial is center-anchored, so add
        // half its DIAL size to left- and top-align it against the HP bar: `width`/`height` would be
        // the measured bounds, which move with the dial's glow, its chip and its entrance scale, and
        // would therefore pin the HUD's geometry to whichever frame they happened to be read on.
        // Anchored on the same margin as the tray below it, rather than on whatever the hearts
        // happened to measure: they have gone to the bottom-left cluster (see positionVitals), and
        // this dial now heads the column by itself.
        this.countdownTimer.x = HUD_EDGE_MARGIN + this.countdownTimer.dialSize / 2
        this.countdownTimer.y = DIAL_TOP + this.countdownTimer.dialSize / 2
        this.countdownTimer.visible = false
        this.countdownTimer.zIndex = 50000 // Lower than chat bubbles (100000)
        this.stage.addChild(this.countdownTimer)

        // The ember gauge rides this column rather than sitting at a fixed height in it -- see
        // placeEmberFireGauge, which is the only thing that positions it. Matched to the dial's
        // width so the two read as one stack. It shares the under-dial slot with the invader's LET
        // GO button, which is safe because the two can never want it at once: a player wearing
        // someone else's body has no camp fire of their own to tend (see setInvading).
        this.emberFireGauge = new EmberFireGauge()
        this.emberFireGauge.setWidth(this.countdownTimer.dialSize)
        this.emberFireGauge.zIndex = 50000
        this.stage.addChild(this.emberFireGauge)

        // Above the HUD, because it is a camera effect rather than a panel -- but below every modal
        // (invite 65000, run summary 70000): a wave landing behind an end-of-run panel must not wash
        // over the panel.
        this.waveLandingFlash = new WaveLandingFlash()
        this.waveLandingFlash.zIndex = 62000
        this.stage.addChild(this.waveLandingFlash)
        // Dev hook: `waveLanding(18)` in the console plays a landing on demand. Waves are a minute
        // apart, so without this checking a change to the effect means sitting through a camp.
        ;(window as any).waveLanding = (wave: number = 12) => this.playWaveLanding(wave, true)

        // Ember wave badge. Sits exactly where the countdown dial does and is the same size: the
        // dial counts DOWN to the wave, then vanishes and this takes the slot for as long as the
        // wave lasts. The two are never up together (see addScreenMessage).
        this.waveIndicator = new WaveIndicator({ radius: 50, strokeWidth: 7.5 })
        this.waveIndicator.x = this.countdownTimer.x
        this.waveIndicator.y = this.countdownTimer.y
        this.waveIndicator.zIndex = 50000
        this.stage.addChild(this.waveIndicator)

        // "Let go" -- the invader's way out, and the only exit of the five they press themself. It
        // continues the top-left column (HP bar, then the countdown dial / wave badge) rather than
        // taking a corner of its own: an invader is reading that dial, so the way out sits where
        // they are already looking. Placed once, like the dial above it.
        this.letGoButton = new LetGoButton({
            onPress: () => {
                this.logic.playerController?.client?.sendEmberInvasionCommand("release")
                this.logic.playerController?.client?.logActivity("invasion: let go")
            },
        })
        // Clear of whichever dial is up. Both of them now name their wave INSIDE the ring (see
        // dial.ts), so this sits right under the dial rather than under a pill hanging off it.
        this.letGoButton.x = Math.round(this.countdownTimer.x - this.letGoButton.getWidth() / 2)
        this.letGoButton.y = Math.round(
            this.countdownTimer.y + Math.max(this.countdownTimer.bottomExtent, this.waveIndicator.bottomExtent) + 10,
        )
        this.letGoButton.zIndex = 50001
        this.stage.addChild(this.letGoButton)

        // command wheel - positioned in center of screen
        this.commandWheel = new CommandWheel({
            radius: COMMAND_WHEEL_RADIUS,
            // A soft tick as the pointer crosses onto another order, so aiming has a feel to it.
            onHoverChange: () => this.logic.audio.uiHover(),
            onCommandSelect: (command: string, npcType: string) => {
                // Confirmation click, before the branch below: both the interaction page and the
                // squad command channel are the same gesture from the player's side.
                this.logic.audio.uiSelect()

                // targetEntityId is set only when the wheel was opened from a specific NPC's
                // command button; otherwise the command broadcasts to the whole role.
                const targetEntityId = this.commandWheel.getTargetEntityId()
                if (this.tileWheelTarget) {
                    const { yOffset, ...target } = this.tileWheelTarget
                    this.logic.playerController.suppressAttack()
                    this.logic.playerController.client.emitPlayerEvent({
                        playerEventType: "ACTIVATE_TILE_INTERACTION",
                        context: { ...target, interaction: command },
                    })
                    this.hideCommandWheel()
                    return
                }

                // The interaction page's "commands" are interaction types on one entity, so they go
                // out on the interaction channel rather than the squad command channel.
                if (npcType === INTERACTION_PAGE) {
                    if (command === "ember:play_mode" && targetEntityId) {
                        const mode =
                            this.recruitHintRoster.find(entry => entry.entityId === targetEntityId)?.playMode || "pvp"
                        this.hideRolePicker()
                        this.hideCommandWheel()
                        this.rolePickerRosterId = targetEntityId
                        this.showRolePicker(targetEntityId, undefined, { playMode: mode })
                        return
                    }
                    if (command === "ember:kick" && targetEntityId) {
                        this.logic.playerController.client.kickEmberPlayer(targetEntityId)
                        this.hideCommandWheel()
                        return
                    }
                    // "Watch" is the HUD's own slice, not the entity's: it swaps this wheel for the
                    // follow-cam PiP in the same slot. It only ever appears on a wheel opened from a
                    // roster card, which is the one place the follow cam is otherwise unreachable.
                    if (command === WATCH_SLICE) {
                        const watching = targetEntityId
                        const watchingName = this.rosterInteractionName
                        this.hideCommandWheel()
                        if (watching) {
                            this.openFollowCam(watching, watchingName)
                        }
                        return
                    }
                    // Out of reach: say so and leave the wheel up, rather than sending an order
                    // that the server would refuse and the player would read as a dead button. The
                    // wheel staying open matters -- "Watch" is right there, and watching them is
                    // the useful thing to do from where they are standing.
                    if (
                        targetEntityId &&
                        interactionNeedsReach(command as InteractionType) &&
                        !this.withinInteractionReach(targetEntityId)
                    ) {
                        this.addScreenMessage({
                            target: "important",
                            text: "Too far away. Go to them.",
                        })
                        return
                    }
                    this.hideCommandWheel()
                    if (targetEntityId) {
                        this.logic.interactionCallback(targetEntityId, command as InteractionType)
                        this.logic.playerController.client.logActivity(
                            `interaction wheel: ${command} on ${targetEntityId}`,
                        )
                    }
                    return
                }

                if (this.watchMemberCommand(command, targetEntityId)) return

                // "Hold Position" (a defender's or the Lord's) asks for a PLACE as well as an order.
                // Hand the pointer to the location picker and let the click that follows carry the
                // spot; until then nothing goes to the server. On touch there is no hovering pointer
                // to aim with, so the order keeps its older meaning there: hold where they stand.
                if (isHoldPositionCommand(command) && !isMobile()) {
                    this.hideCommandWheel()
                    this.logic.playerController.beginLocationPick({
                        caption: "Click the spot to hold",
                        blockedCaption: "Nothing can stand here",
                        onPick: (point: Coordinate) => {
                            this.logic.playerController.client.sendCommandWheelSelection(
                                command,
                                npcType,
                                targetEntityId,
                                point,
                            )
                            this.logic.playerController.client.logActivity(
                                `command wheel: ${command} for ${npcType} at ` +
                                    `${Math.round(point.x)},${Math.round(point.y)}`,
                            )
                        },
                    })
                    return
                }

                // "Change Role" doesn't order anything -- it asks the server for the role picker. Where
                // that picker should OPEN is known only here, at the wheel that asked: from a roster
                // card it belongs in the HUD slot that wheel is leaving, not over a member who may be
                // off in the forest. Recorded before hideCommandWheel below lets go of the card.
                if (isChangeRoleCommand(command) && targetEntityId && this.commandWheelRosterId === targetEntityId) {
                    this.rolePickerRosterId = targetEntityId
                } else if (isChangeRoleCommand(command)) {
                    this.rolePickerRosterId = undefined
                }

                // Work given to a forager is what the chapter-three helper teaches; once it has been
                // given, the helper has done its job.
                if (npcType === "forager" && !isChangeRoleCommand(command)) this.tutorial?.foragerOrdered()
                this.logic.playerController.client.sendCommandWheelSelection(command, npcType, targetEntityId)
                this.logic.playerController.client.logActivity(`command wheel: ${command} for ${npcType}`)
                // The per-entity wheel is not tied to the Control key, so dismiss it on selection.
                if (targetEntityId) {
                    this.hideCommandWheel()
                }
            },
        })
        this.commandWheel.x = screenWidth / 2
        this.commandWheel.y = ScreenDimensions.h / 2
        // Screen-center "home" the global (Control-key) wheel returns to after a per-entity wheel
        // has moved it over an NPC.
        this.commandWheelHome = { x: this.commandWheel.x, y: this.commandWheel.y }
        this.commandWheel.visible = false
        this.commandWheel.zIndex = 60000 // Higher than countdown timer
        // The tether lives in the same (stage) frame as the wheel so both ends can be expressed in
        // stage-local coordinates, and sits just under it so the line tucks behind the disc.
        this.commandWheelTether = new Graphics()
        this.commandWheelTether.visible = false
        this.commandWheelTether.zIndex = this.commandWheel.zIndex - 1
        this.stage.addChild(this.commandWheelTether)
        this.stage.addChild(this.commandWheel)

        // recruit role picker — opened by the server over a specific camp member (like the per-entity
        // command wheel). Selecting a card sends the chosen role back on the SQUAD_COMMAND channel,
        // scoped to that member, which its script turns into a swap to the role.
        //
        // Two commands, because the two moments are answered by different scripts: `recruit:<role>` is
        // the HIRE, answered by ember-recruit.js (which also charges the food), and `role:<role>` is a
        // CHANGE on an already-hired member, answered by its own role script (lib/role_change.js).
        // Keeping them apart is what stops a stray hire landing on a member, or a change on a recruit.
        this.rolePicker = new RolePicker({
            onSelect: (role: string, currentRole?: string) => {
                if ((role === "up" || role === "down") && this.stairPickerMessage) {
                    const choice = this.stairPickerMessage.stairChoices[role]
                    this.stairPickerMessage = undefined
                    this.logic.playerController.client.emitPlayerEvent(choice)
                    this.hideRolePicker()
                    return
                }
                const targetEntityId = this.rolePicker.getTargetEntityId()
                if (role === "pvp" || role === "cooperation") {
                    this.logic.playerController.client.setEmberPlayMode(targetEntityId, role)
                    this.hideRolePicker()
                    return
                }
                const command = currentRole ? `role:${role}` : `recruit:${role}`
                this.logic.playerController.client.sendCommandWheelSelection(command, "recruit", targetEntityId)
                this.logic.playerController.client.logActivity(`role picker: ${command} for ${targetEntityId}`)
                this.hideRolePicker()
            },
        })
        this.rolePicker.visible = false
        this.rolePicker.zIndex = 60000 // Same tier as the command wheel
        this.stage.addChild(this.rolePicker)

        this.conversationPanel = new ConversationPanel({
            choose: (view, choiceId) => this.logic.playerController.client.sendConversationChoice(view.npcEntityId, view.nodeId, choiceId),
            leave: view => this.leaveConversation(view),
        })
        this.conversationPanel.zIndex = 61000
        this.stage.addChild(this.conversationPanel)

        // name prompt -- opened by the server over a camp member (at the end of a hire, or on the
        // "Rename" order) so the player can name it. The field starts on the name the member
        // already has, so backing out is a complete answer and simply keeps it.
        this.namePrompt = new NamePrompt({
            onSubmit: (name: string) => {
                const targetEntityId = this.namePrompt.getTargetEntityId()
                if (targetEntityId) {
                    this.logic.playerController.client.sendEntityName(targetEntityId, name)
                    this.logic.playerController.client.logActivity(`named ${targetEntityId} "${name}"`)
                }
                this.hideNamePrompt()
            },
            onCancel: () => this.hideNamePrompt(),
        })
        this.namePrompt.visible = false
        this.namePrompt.zIndex = 60001 // Just over the role picker, which it opens on top of
        this.stage.addChild(this.namePrompt)

        // The location chooser — the reticle a "Hold Position" order puts under the pointer while
        // the player picks the spot. It only draws; the pick itself (and the click that ends it)
        // belongs to PlayerController, which owns world presses.
        this.buildingPrintCountdown = new BuildingPrintCountdown(() => this.logic.clientRenderer.map().getTileSurface())
        this.locationPicker = new LocationPicker()
        this.stage.addChild(this.locationPicker)

        // ember end-of-run scorecard — put up by the server when the player dies in an ember camp
        // (Protocol.type_showRunSummary). Its two buttons go back to the ember controller, which
        // either stands the player up in the camp they fell in or resets that camp; the
        // panel dismisses itself as it sends. Whether a restart is on the table at all is the
        // camp's call (it rides in on the summary's lives) — the controller enforces it either way.
        this.runSummary = new RunSummary({
            onTryAgain: () => {
                this.logic.playerController.client.sendEmberRunCommand("tryAgain")
                this.logic.playerController.client.logActivity("run summary: try again")
                this.hideRunSummary()
            },
            onRestart: () => {
                this.logic.playerController.client.sendEmberRunCommand("restart")
                this.logic.playerController.client.logActivity("run summary: restart")
                this.hideRunSummary()
            },
            // The third way on. The press opens the picker from the list the client ALREADY holds
            // -- the offer is an interval feed, not a reply -- and the `seek` that goes with it is
            // only a nudge for a fresher list. Nothing waits on the server.
            onInvade: () => {
                this.logic.playerController.client.sendEmberInvasionCommand("seek")
                this.logic.playerController.client.logActivity("run summary: resurrect as undead")
                this.showInvasionPicker()
            },
        })
        this.runSummary.visible = false
        // Above the command wheel / role picker: this one is a full-screen modal over a finished run.
        this.runSummary.zIndex = 70000
        this.stage.addChild(this.runSummary)

        // The camp chooser, opened from the scorecard and drawn over it. Picking a card is the last
        // press of the whole handshake: the server moves the player, and every exit from there is a
        // fresh camp of their own (see docs/ember-the-fallen-design.md, "Leave").
        this.invasionPicker = new InvasionPicker({
            onJoin: (entityId: EntityId) => {
                this.logic.playerController.client.sendEmberInvasionCommand("join", entityId)
                this.logic.playerController.client.logActivity(`invasion: join ${entityId}`)
                this.hideInvasionPicker()
            },
            onCancel: () => {
                this.cancelInvasionPicker()
            },
        })
        this.invasionPicker.visible = false
        // Over the scorecard, which stays up behind it.
        this.invasionPicker.zIndex = 70001
        this.stage.addChild(this.invasionPicker)

        // The two doorways of a possession. Above BOTH of those, because it is the one thing on the
        // screen the player has not seen yet -- and because the far end of a visit arrives together
        // with a whole new room, whose load must not draw over it.
        this.possessionSplash = new PossessionSplash({
            onDismiss: () => {
                this.logic.playerController?.client?.logActivity("possession splash dismissed")
            },
        })
        this.possessionSplash.visible = false
        this.possessionSplash.zIndex = 70002
        this.stage.addChild(this.possessionSplash)

        // The chapter's quest log, under the fire gauge, and the celebrations that land in it: a task
        // done is toasted mid-screen and its diamond flown into the row; a chapter done is a fanfare
        // that docks into the panel, where Continue / Next Chapter then wait.
        this.chapterChecklist = new ChapterChecklist(
            over => this.mouseOverControl(over),
            () => {
                const gauge = this.emberFireGauge
                if (!gauge) return undefined
                // In the gauge's own slot while the gauge is held back, and eased down under it as it
                // arrives, so the list is never left hanging under an empty space.
                const top = gauge.y - gauge.height_ / 2
                return { x: gauge.x, y: top + this.hudPresence.arrival("fireGauge") * (gauge.height_ + 6) }
            },
            {
                onTaskCompleted: task =>
                    this.chapterCelebration.taskComplete(
                        task.label,
                        () => this.chapterChecklist.socketGlobal(task.id),
                        () => this.chapterChecklist.reveal(task.id),
                    ),
                onChapterCompleted: (chapter, title) =>
                    this.chapterCelebration.chapterComplete(
                        chapter,
                        title,
                        () => this.chapterChecklist.panelGlobal(),
                        () => this.chapterChecklist.dock(),
                    ),
                onContinue: () => {
                    this.logic.playerController.client.sendEmberTutorialCommand("continue_story", "click")
                    this.logic.playerController.client.logActivity("chapter checklist: continue_story (click)")
                },
                onNextChapter: chapter => {
                    if (this.currentModal || this.chapterAnnouncement.isOpen()) return
                    this.logic.playerController.client.sendEmberTutorialCommand("preview_chapter", "click", chapter)
                },
            },
        )
        this.chapterChecklist.zIndex = 55000
        this.stage.addChild(this.chapterChecklist)
        // Above the HUD and the tutorial board, below every modal and the chapter title card.
        // It keeps clear of anyone's speech bubble (the Lord nearly always has something to say at
        // these moments) and of the building and forager helper windows.
        this.chapterCelebration = new ChapterCelebration(
            cue => {
                if (cue === "task") this.logic.audio.chapterTaskComplete()
                else if (cue === "stamp") this.logic.audio.chapterTaskStamp()
                else this.logic.audio.chapterComplete()
            },
            undefined,
            () => {
                const rects = SpeechBubbleLayout.screenRects()
                const helper = this.tutorial?.demoBounds()
                if (helper) rects.push({ x: helper.x, y: helper.y, w: helper.width, h: helper.height })
                return rects
            },
        )
        this.chapterCelebration.zIndex = 62000
        this.stage.addChild(this.chapterCelebration)
        this.chapterAnnouncement = new ChapterAnnouncement(
            chapter => {
                this.logic.playerController.client.sendEmberTutorialCommand("begin_chapter", "click", chapter)
                this.logic.playerController.enabledKeyboardAction(true)
                this.logic.playerController.enabledMouseAction(true)
                this.logic.playerController.suppressAttack?.(300)
            },
            () => !!this.logic.playerController && !!this.logic.playerEntity?.entityId,
            chapter => {
                this.logic.playerController.client.sendEmberTutorialCommand("maybe_later", "click", chapter)
                this.logic.playerController.enabledKeyboardAction(true)
                this.logic.playerController.enabledMouseAction(true)
                this.logic.playerController.suppressAttack?.(300)
            },
            cue => {
                if (cue === "reveal") this.logic.audio.chapterAnnounce()
                else this.logic.audio.chapterBegin()
            },
        )
        // The closing card after the story's last chapter sends its own command rather than Begin's.
        this.chapterAnnouncement.onClosingAccept = command => {
            this.logic.playerController.client.sendEmberTutorialCommand(command, "click")
            this.logic.playerController.enabledKeyboardAction(true)
            this.logic.playerController.enabledMouseAction(true)
            this.logic.playerController.suppressAttack?.(300)
        }
        this.chapterAnnouncement.zIndex = 80000
        this.stage.addChild(this.chapterAnnouncement)

        // first-run ember tutorial invitation — put up by ember-tutorial.js on a player's very
        // first camp (Protocol.type_showTutorialPrompt). Either button answers on the generic
        // player-event channel, and so does letting the countdown run out (which means "go ahead").
        // Low on the screen and clear of the roster strip (bottom-right) and the wave dial
        // (top-left), so the Lord stays visible while he talks.
        this.tutorialPrompt = new TutorialPrompt({
            onChoose: (choice, reason) => {
                this.logic.playerController.client.sendEmberTutorialCommand(choice, reason)
                this.logic.playerController.client.logActivity(`tutorial prompt: ${choice} (${reason})`)
                this.hideTutorialPrompt()
                if (choice === "skip") {
                    // Anything still waiting on a "Next" belongs to a scene that is over, and a
                    // held bubble SWALLOWS what comes after it: lines spoken while one is up queue
                    // behind it and inherit its hold (AvatarHud.spoke), which is exactly what the
                    // Lord's sign-off must not do to the player who just declined the lesson.
                    this.logic.clientRenderer.entity().releaseHeldSpeech()
                }
            },
        })
        this.tutorialPrompt.x = screenWidth / 2 - TutorialPrompt.stageWidth() / 2
        this.anchorTutorialPrompt()
        this.tutorialPrompt.visible = false
        // Same tier as the command wheel / role picker: above the HUD, below the run scorecard.
        this.tutorialPrompt.zIndex = 60000
        this.stage.addChild(this.tutorialPrompt)

        // Under the HUD's timers and dials, over the world: it stands beside the player.
        this.controlsLesson.zIndex = 49000
        this.stage.addChild(this.controlsLesson)

        // hero message - positioned in center of screen
        this.heroMessage = new HeroMessage()
        this.heroMessage.zIndex = 60000 // Higher than countdown timer
        this.stage.addChild(this.heroMessage)

        this.intro = new Intro(this)
        this.layoutHudEdges()

        // A press anywhere that isn't the wheel puts the wheel away (see onOutsidePress). Bound once,
        // for the life of the HUD, and inert while no wheel is up.
        if (typeof document !== "undefined") {
            document.addEventListener("pointerdown", this.onOutsidePress, true)
        }
    }

    // Dismiss-on-outside-press. A wheel is modal in intent but not in fact -- it floats over the
    // world and the world goes on taking clicks underneath it -- so a press that lands anywhere else
    // has to read as "put it away". Presses arrive on the DOCUMENT (see Mouse.pressIsOnGui), which is
    // why this asks pixi what was under the pointer rather than hanging a handler off the stage: a
    // press on empty canvas hits nothing and would never reach one.
    //
    // Capture phase, so it runs before the widget the press landed on: a press on another roster card
    // closes this wheel and then that card opens its own.
    //
    // Three things own their own presses and are left alone: the wheel itself (a slice is a choice,
    // not a dismissal), the role picker (a sibling of the wheel, not a rival), and the roster strip,
    // whose cards re-target the wheel or toggle it off themselves.
    private onOutsidePress = (event: PointerEvent) => {
        if (!this.commandWheel?.isOpen()) {
            return
        }
        const target = Mouse.pressTarget(event)
        // Can't tell (no interaction plugin) is not the same as "landed on nothing": leave it up.
        if (target === undefined) {
            return
        }
        for (let node: DisplayObject | null = target; node; node = node.parent) {
            if (
                node === this.commandWheel ||
                node === this.rolePicker ||
                node === this.namePrompt ||
                node === this.emberRoster
            ) {
                return
            }
        }
        this.hideCommandWheel()
    }

    updateDayPct = (pct: number) => {
        if (pct !== undefined && !this.suppressDayCycle) {
            this.dayCycle.visible = true
            this.dayCycle.setup(pct * 360)
        }
    }

    getIntro = () => this.intro

    log = (msg: string) => this.logic.playerController.client.logActivity(msg)

    // The wire's one way into the tutorial layer: an overworld popup, an ember clue to arm, or -- on a
    // null type -- the server taking the ember clues back down (see Tutorial.setup).
    showTutorial = (tutorialType: TutorialType | null, timeoutMs?: number) => {
        this.tutorial.setup(tutorialType, timeoutMs)
    }

    // What the inventory/crafting panel calls to say where one of the clued controls landed, and to
    // take the pointer off one it is no longer drawing.
    tutorialClues = (): Tutorial => this.tutorial

    // The feedback composer on screen, if any: from the feedback button, or opened for a visit by the
    // feedback rabbit (a feedbackComposer screen message). One at a time.
    private feedbackComposer?: FeedbackComposer

    invokeFeedback = () => this.openFeedbackComposer()

    // `visitId` is the rabbit's visit that asked for it; without one the player opened it themselves,
    // and is thanked by a toast because there is no rabbit there to do it.
    openFeedbackComposer = (visitId?: string) => {
        if (this.feedbackComposer?.isOpen()) {
            if (this.feedbackComposer.visitId() === visitId) return
            this.feedbackComposer.close()
        }
        const client = this.logic.playerController.client
        const composer: FeedbackComposer = new FeedbackComposer(this, {
            source: visitId ? "messenger" : "menu",
            visitId,
            onSubmit: feedback => {
                client.submitFeedback(feedback)
                if (!visitId) this.addScreenMessage({ target: "important", text: "Thank you! Your words are on their way." })
            },
            onCancel: () => {
                if (visitId) client.sendFeedbackCancelled(visitId)
            },
        })
        this.feedbackComposer = composer
        composer.open()
    }

    closeFeedbackComposer = (visitId?: string) => {
        if (!this.feedbackComposer?.isOpen()) return
        if (visitId && this.feedbackComposer.visitId() !== visitId) return
        this.feedbackComposer.close()
    }

    helpMessaging = () => {
        // Hand the world's mouse back before anything is torn down. Both buttons below take it away
        // while the pointer is on them, and this rebuild is how they go: the message button calls
        // haltMessageIndicator from its own click handler, its throbber does the same 30s later with
        // nobody touching anything, and the level-up button goes when the points are spent. pixi
        // delivers no mouseout to a removed display object -- and none at all to one whose PARENT is
        // removed, which is how the message button goes -- so the latch would stay set and every
        // world click (walk, swing, fire) would be dead until the next room load.
        this.helpMessagingButtons.forEach(button => button.releaseMouseControl())
        this.helpMessagingButtons = []

        // cleanout old message
        const existingMessage = this.container.getChildByName("_help_messaging_")
        if (existingMessage) {
            existingMessage.parent.removeChild(existingMessage)
        }
        // The "Level up stats!" prompt is positioned independently (beside the hp/mana bars), not
        // in the help-messaging stack — clean up its previous instance here too.
        const existingLevelUp = this.container.getChildByName("_level_up_stats_")
        if (existingLevelUp) {
            existingLevelUp.parent.removeChild(existingLevelUp)
        }

        const helpMessaging = new DyanmicTextContainer()
        helpMessaging.name = "_help_messaging_"
        this.container.addChild(helpMessaging)

        const messages: string[] = []
        const items: Container[] = []

        if (this.logic.playerController.isGuest() && !this.suppressGuestPrompt) {
            messages.push("/register to save progress")
        }

        let previous = null
        messages.forEach(message => {
            const messageText = new TextRenderer().align("right").render(message)
            helpMessaging.addChild(messageText)

            if (previous) {
                messageText.y = previous.y - messageText.height
            }

            items.push(messageText)
            previous = messageText
        })

        if (this.incomingMessageMeta.unread) {
            const haltIndicator = (showDialog: boolean = true) => {
                if (showDialog) {
                    this.doDynamicDialog(this.incomingMessageMeta.text)
                }
                this.haltMessageIndicator()
            }

            const button = createButton({
                label: "Incoming message",
                preset: "compact",
                variant: "danger",
                tone: "accent",
                height: BUTTON_SIZES.small.height,
                onClick: () => {
                    this.log("open incoming message")
                    haltIndicator(true)
                },
                mouseControlCallback: (on: boolean) => {
                    this.logic.playerController.enabledMouseAction(!on)
                },
            })
            button.y = (previous?.y ?? 0) - button.height
            new Throbber(button, {
                expires: 30 * 1000,
                blinkInterval: 20,
                releaseCallback: () => haltIndicator(false),
            }).update()
            previous = button
            helpMessaging.addChild(button)
            items.push(button)
            this.helpMessagingButtons.push(button)
        }

        if (this.upgradeIndicator && !this.suppressLevelUp) {
            const button = createButton({
                label: "Level up stats!",
                preset: "compact",
                variant: "primary",
                width: 120,
                height: BUTTON_SIZES.small.height,
                onClick: () => {
                    this.log("open stats upgrade ui")
                    this.logic.playerController.client.invokeStatUpgradeUI()
                },
                mouseControlCallback: (on: boolean) => {
                    this.logic.playerController.enabledMouseAction(!on)
                },
            })
            new Throbber(button, {
                expires: 5000,
                blinkInterval: 20,
            }).update()

            // Beside the countdown dial, which is what heads the top-left column now that the
            // hearts have gone down to the tray. On the dial's mid-line rather than its top, so it
            // and the camp-tasks stack (which hangs from the dial's TOP, in the same gutter) have
            // to overlap before they collide. Both are transient; neither is common.
            button.name = "_level_up_stats_"
            // GUARDED, and deliberately: this is cosmetic placement sitting on the path that builds
            // a live HUD button, and nothing about where a button lands is worth throwing out of
            // (the crafting screen once vanished entirely over a measuring helper that threw). The
            // dial is built in the constructor so the fallback is only ever reached under a stub.
            const dial = this.countdownTimer
            button.x = (dial ? dial.x + dial.dialSize / 2 + 12 : HUD_EDGE_MARGIN) - this.container.x
            button.y = (dial ? dial.y : this.hudTopEdge()) - this.container.y
            this.container.addChild(button)
            this.helpMessagingButtons.push(button)
        }

        helpMessaging.x = this.minimapSurface.x - helpMessaging.width - 10
        helpMessaging.y = BOTTOM - 30 + stageViewport().insetY
        items.forEach(item => {
            item.x = helpMessaging.width - item.width
        })
    }

    update = (player: Entity) => {
        // A FALLEN player reads EMPTY whatever their hp says. The server restores hp as part of
        // standing a downed player back up (standUpDownedPlayer), and that lands while the client
        // is still holding its own death state -- so the hearts refilled underneath a black screen,
        // which is where "you died and your health went up" came from. Zero is the honest reading
        // of being dead, and it moves the refill to the moment the player is actually back up
        // (ClientGameLogic.respawned clears `downed` and fades in together).
        //
        // `hasFallen`, not `isDowned`: the deadline behind isDowned is server-side state, so on the
        // client it answers NO on the very machine drawing the death screen. The client's truth is
        // the `downed` mirror that updateFromPlayerMessage sets from every world-state message.
        const downed = player.hasFallen()
        if (
            this.currentPlayerHp !== player.hp ||
            this.currentPlayerHpMax !== player.maxHp ||
            this.currentPlayerDowned !== downed
        ) {
            this.hp.max = 10
            // maxHp can be zero on a half-built or possessed entity, and 0/0 is NaN -- which the
            // meter would carry all the way into a mask rectangle.
            const ratio = player.maxHp > 0 ? player.hp / player.maxHp : 0
            this.hp.update(downed ? 0 : ratio * this.hp.max)
            this.currentPlayerHp = player.hp
            this.currentPlayerHpMax = player.maxHp
            this.currentPlayerDowned = downed
        }
        if (this.currentPlayerHunger !== player.hunger || this.currentPlayerHungerMax !== player.maxHunger) {
            this.hunger.visible = !this.suppressFood
            this.hunger.update((player.hunger / player.maxHunger) * this.hunger.max)
            this.currentPlayerHunger = player.hunger
            this.currentPlayerHungerMax = player.maxHunger
        }
        if (this.currentPlayerMana !== player.mana || this.currentPlayerManaMax !== player.maxMana) {
            this.mana.visible = !this.suppressMana
            this.mana.update(player.maxMana > 0 ? player.mana / player.maxMana : 0)
            this.currentPlayerMana = player.mana
            this.currentPlayerManaMax = player.maxMana
        }
        if (this.currentPlayerName !== player.name || this.currentScore !== player.score) {
            let name = player.safeName()
            this.nameText.text = `${name}, Lvl ${Math.floor(player.level)}`
            // this.nameText.text = `${name} - Lvl ${player.level} | `
            // this.nameText.text += `${Math.floor(player.score)}/${Math.floor(player.score + player.scoreTilNextLevel)} next level`
            this.currentPlayerName = player.name
            this.currentScore = player.score
        }
        if (this.currentPlayerUpgradePoints !== player.upgradePoints) {
            this.currentPlayerUpgradePoints = player.upgradePoints
            this.upgradeIndicator = this.currentPlayerUpgradePoints > 0
            this.helpMessaging()
        }
        if (player && player.entityId === this.logic.playerEntity.entityId && !this.joystickSetup) {
            this.setupJoystick(this.container)
            this.joystickSetup = true
        }
        if (player && player.entityId === this.logic.playerEntity.entityId) {
            if (this.characterBuilder.isOpen()) {
                this.characterBuilder.update()
            }
        }
        this.positionVitals()
    }

    private updateRecruitMenuHint = (_deltaTime: number) => {
        if (this.recruitLessonPlayerId !== this.logic.playerEntity?.entityId) {
            this.recruitLessonPlayerId = this.logic.playerEntity?.entityId
            this.recruitMenuLearnedLocally = false
            this.campfireMenuLearnedLocally = false
        }
        // Chapter checklists now guide recruitment; keep the old anchored gesture hint hidden.
        this.recruitMenuHint.reset()
    }

    private recordCampfireMenuLearned = (entityId: string) => {
        if (
            this.logic.getEntity(entityId)?.contextMenuSubject !== "campfire" ||
            this.campfireMenuLearnedLocally ||
            this.logic.playerEntity?.campfireMenuLearned
        )
            return
        this.campfireMenuLearnedLocally = true
        this.logic.playerController.client.emitPlayerEvent({
            playerEventType: "ENTITY_CONTEXT_MENU_OPENED",
            context: { entityId },
        })
    }

    private learnedEntityMenu = (entityId: string) => {
        if (this.logic.getEntity(entityId)?.contextMenuSubject === "campfire") {
            this.recordCampfireMenuLearned(entityId)
            return
        }
        this.recruitMenuHint.dismiss()
        this.recordRecruitMenuLearned(entityId, "ENTITY_CONTEXT_MENU_OPENED")
    }

    private recordRecruitMenuLearned = (
        entityId: string,
        playerEventType: "ENTITY_CONTEXT_MENU_OPENED" | "RECRUIT_MENU_HINT_SHOWN",
    ) => {
        const entity = this.logic.getEntity(entityId)
        if (entity?.contextMenuSubject !== "recruit") return
        if (this.recruitMenuLearnedLocally || this.logic.playerEntity?.recruitMenuLearned) return
        this.recruitMenuLearnedLocally = true
        this.logic.playerController.client.emitPlayerEvent({
            playerEventType,
            context: { entityId },
        })
    }

    updateEmberAnxiety = (feeling: EmberAnxietyState | null, roomId: EntityId) => {
        if (!this.acceptsSceneRoom(roomId)) return
        this.emberAnxietyMeter?.update(feeling)
    }

    updateTimer = (deltaTime: number) => {
        this.updateConversation(deltaTime)
        if (this.emberAnxietyMeter?.visible) {
            const viewport = getUiMetrics().safeViewport
            const scale = Math.min(getUiMetrics().touch ? 1.4 : 1, viewport.width / 298)
            this.emberAnxietyMeter.scale.set(scale)
            this.emberAnxietyMeter.position.set(viewport.left + 12, viewport.top + 65)
            // The meter beats, so it needs the frame clock. Only while it is up: away from the
            // cave it is hidden and costs nothing.
            this.emberAnxietyMeter.animate(deltaTime)
        }
        // The command wheel's open/close, hover light and pick flash. Returns at once while it rests.
        this.commandWheel?.animate(deltaTime)
        if (this.mousePointer?.visible) {
            const controller = this.logic.playerController
            const point = this.logic.clientRenderer?.mouse().state().position
            this.mousePointer.animate(
                deltaTime,
                !!controller?.isAttacking(),
                isMobile() || !controller?.isMouseActionEnabled() || !this.logic.playerEntity ||
                    this.logic.playerEntity.isDead() || this.logic.playerEntity.hasFallen() ||
                    this.isCommandWheelVisible() || this.isWorldBlockingScreenOpen() ||
                    !!this.isChatComposerVisible() || !!this.isNamePromptVisible() ||
                    !!this.isRolePickerVisible() || !!(point && !Mouse.isWorldPoint(new Point(point.x, point.y))),
            )
        } else {
            this.mousePointer?.animate(deltaTime, false, true)
        }
        this.updateGameplayControls()
        this.layoutHudEdges()
        this.updateMobileBuildToolbar()
        this.positionVitals()
        // The hearts drain, beat and flash off the frame clock rather than a wall-clock interval,
        // so they need the delta. A resting meter returns from this immediately.
        this.hp?.animate(deltaTime)
        this.mana?.animate(deltaTime)
        // Slide the readied selection ring and breathe the wear gauges of fragile weapons.
        this.equipped?.animate(deltaTime)
        this.placeColumnHead()
        this.placeEmberFireGauge(deltaTime)
        this.emberFireGauge?.animate(deltaTime)
        this.waveLandingFlash?.animate(deltaTime)
        this.tutorial?.refreshHudAnchors()
        // The clues' brackets breathe and their notes slide in and fade out off the frame clock.
        // Returns at once with no clue up, which is all but the first minutes of a first camp.
        this.tutorial?.animate(deltaTime)
        // The frame-rate readout, fed the render loop's own delta. A no-op while it is away.
        this.fpsMeter?.sample(deltaTime)
        // ...and the ping, which is only measured while somebody is looking at it (see PingProbe).
        if (this.fpsMeter?.isOpen()) {
            this.fpsMeter.setPing(this.logic.playerController?.client?.measurePing?.())
            // ...and what the Ember frame baker is holding.
            this.fpsMeter.setBaked(emberFrameBaker.held())
        }
        // The render loop calls this even when no player/entity state has changed.
        this.buildingPrintCountdown?.update()
        this.logic.playerController?.animatePendingBuilds?.()
        if (this.countdownTimer && this.countdownTimer.visible) {
            this.countdownTimer.update(deltaTime)
        }
        this.updateRecruitMenuHint(deltaTime)
        // Cheap when nothing is moving: the badge only repaints on a change or a live animation.
        this.waveIndicator?.animate(deltaTime)
        // Advance the `important` strip's queue. A no-op with an empty lane, which is the usual case.
        this.pumpImportantLane(deltaTime)
        // Then place the task stack, which yields to whatever the line above just decided to show.
        this.placeEmberObligations(deltaTime)
        this.chapterChecklist?.animate(deltaTime)
        this.chapterChecklist?.place()
        this.chapterCelebration?.animate(deltaTime)
        // Track the follow-cam subject each frame: hide the PiP while it's on-screen, show it when off.
        this.updateFollowCamVisibility()
        // Advance the lord's campfire-strength fire-particle backdrop.
        this.emberRoster.animate(deltaTime)
        // The character-creation screen animates its own stage (hearth glow, intro typewriter).
        this.characterBuilder.animate(deltaTime)
        // Glide the minimap's dots between snapshots (the feed only speaks once a second), and slide
        // the player's own dot, whose location is predicted locally and so needs no interpolation.
        if (this.emberMinimap?.visible) {
            this.emberMinimap.animate(deltaTime)
            this.emberMinimap.trackPlayer(this.logic.playerEntity?.location)
        }
        // Drain the players chip's arrival flash, and the transient "Sent" on an invite button. Both
        // are no-ops when nothing is running.
        this.emberPlayersChip?.animate(deltaTime)
        this.chatChip?.animate(deltaTime)
        if (this.emberPlayersPanel?.visible) {
            this.emberPlayersPanel.animate(deltaTime)
        }
        // Spinners, the queue's clocks, and keeping the description box parked over its socket.
        if (this.spriteLabPanel?.visible) {
            this.spriteLabPanel.fitToViewport()
            this.spriteLabPanel.animate(deltaTime)
        }
        // Drain the invitation's clock. At zero the prompt takes itself down -- doing nothing is an
        // answer -- which frees the slot the list sits below.
        if (this.emberInvitePrompt?.isOpen()) {
            this.emberInvitePrompt.animate(deltaTime)
            if (!this.emberInvitePrompt.isOpen()) {
                this.emberInviteFrom = undefined
                this.positionEmberPlayers()
            }
        }
        // ...and the one behind the run scorecard's portrait, while that's up.
        if (this.emberInviteModal?.isOpen()) {
            this.positionEmberInviteModal()
        }
        this.runSummary?.animate(deltaTime)
        // Drain the invasion picker's countdowns (the feed is two seconds coarse, so the seconds
        // are counted down locally between ticks) and its fade. No-op while shut.
        this.invasionPicker?.animate(deltaTime)
        this.possessionSplash?.animate(deltaTime)
        if (this.chapterAnnouncement?.isOpen()) {
            this.fitChapterAnnouncement()
            this.chapterAnnouncement.animate(deltaTime)
        }
        // Move the tutorial invitation in and out and drain its countdown; at zero it answers
        // "accept" for the player. Fed every frame (not only while open) so its exit can finish;
        // a no-op while it is away.
        this.tutorialPrompt?.update(deltaTime)
        this.feedbackComposer?.update()
        this.applyHudPresence(deltaTime)
        this.updateControlsLesson(deltaTime)
        this.updateQuietWatch()
    }

    /**
     * Tell the server when the player's screen goes quiet or busy (see gui/quiet_watch.ts). The GUI
     * only says WHETHER something is going on; what to do about it is the server's. Everything read
     * here is the GUI's own state or the player's own body, never a camp's.
     */
    private updateQuietWatch = () => {
        const now = Date.now()
        const player = this.logic.playerEntity
        if (player) {
            if (this.quietHp?.entityId === player.entityId && player.hp < this.quietHp.hp) this.lastHurtTs = now
            this.quietHp = { entityId: player.entityId, hp: player.hp }
        }
        // Nobody to tell yet (logging in, making a character): don't let the watch think it has told.
        const client = this.logic.playerController?.client
        if (!client) return
        // A redial is a new session on the server, which has never been told either.
        if (client.ws !== this.quietSocket) {
            this.quietSocket = client.ws
            this.quietWatch.reset()
        }
        const changed = this.quietWatch.update(now, this.screenBusyReason(now))
        if (changed !== undefined) client.sendScreenQuiet(changed)
    }

    /** Why the player's screen is busy right now, or null when it is quiet. */
    screenBusyReason = (now: number = Date.now()): string | null => {
        if (typeof document !== "undefined" && document.hidden) return "tab hidden"
        const player = this.logic.playerEntity
        if (!player || player.hp <= 0 || player.downed) return "player down"
        if (now - this.lastHurtTs < HURT_BUSY_MS) return "hurt"
        if (this.isBlackoutVisible()) return "curtain"
        if (this.isModalOpen()) return "modal"
        if (this.isWorldBlockingScreenOpen()) return "screen"
        if (this.isChapterAnnouncementVisible()) return "title card"
        if (this.tutorialPrompt?.isOpen()) return "tutorial prompt"
        if (this.tutorial?.isAnyClueShowing()) return "clue"
        if (this.tutorialDemoLayer?.children.some(child => child.visible)) return "lesson"
        if (this.controlsLesson.isActive()) return "controls lesson"
        if (this.chapterCelebration?.isBusy()) return "celebration"
        if (this.importantLane.current()) return "notice"
        if (this.heroMessage?.visible) return "hero message"
        if (this.emberInvitePrompt?.isOpen()) return "invite"
        if (this.waveIndicator?.isShowing()) return "wave"
        if (this.isLocationPickerVisible()) return "location pick"
        if (this.logic.playerController?.isBuilding?.()) return "building"
        const screen = findRendererApp()?.renderer?.screen
        if (screen) {
            // An INVITATION's line (the rabbit asking) is exempt from both: it is the optional thing
            // itself, and counting it would have it chase itself off.
            if (SpeechHoldButtons.anyInView(screen)) return "held line"
            if (SpeechBubbleLayout.anyOnScreen({ x: screen.x, y: screen.y, w: screen.width, h: screen.height })) return "speech"
        }
        return null
    }

    /**
     * Feed the controls lesson the player's input and whether the world can be seen at all. Only GUI
     * and input state: whether there is a lesson to give is the server's call (chapter_hud.ts).
     *
     * It has to run the same in a session replay, which has no keyboard or mouse -- only the server's
     * messages and what the player's client sent. So walking is read off the player's position (which
     * a replay has, from the server) rather than off the keys; the actions come in from what was sent
     * (notePlayerSent); and only the keys lighting up has no recorded source, so a replay lights the
     * WASD keys for the way the player is travelling.
     */
    private updateControlsLesson = (deltaTime: number) => {
        if (!this.controlsLesson.isActive()) {
            this.controlsLessonLastAt = undefined
            this.reportControlsLessonsDone()
            return
        }
        const player = this.logic.playerEntity
        const at = player?.location ? { x: player.location.x, y: player.location.y } : undefined
        const last = this.controlsLessonLastAt
        this.controlsLessonLastAt = at
        const step = at && last && last.x >= 0 && at.x >= 0 ? { x: at.x - last.x, y: at.y - last.y } : { x: 0, y: 0 }
        // A walk, not an arrival: a teleport (or a replay seek) moves the player a long way in one frame.
        const stepLength = Math.hypot(step.x, step.y)
        const travelled = stepLength > 0.25 && stepLength < TileSize * 1.5
        const intent = player?.speed2
        const replay = !!this.logic.playerController?.client?.replayMode
        const keyboard = this.logic.clientRenderer?.keyboard?.().state()
        const travelling: Record<string, boolean> = {
            w: travelled && step.y < -0.1, s: travelled && step.y > 0.1, a: travelled && step.x < -0.1, d: travelled && step.x > 0.1,
        }
        this.controlsLesson.animate(deltaTime, {
            blocked: !player || player.isDead() || !!this.chapterAnnouncement?.isOpen() ||
                !!this.logic.isRoomRevealHolding?.() || this.isWorldBlockingScreenOpen(),
            moving: travelled || (!replay && !!intent && (intent.x !== 0 || intent.y !== 0)),
            keyHeld: key => replay ? !!travelling[key] : !!(keyboard?.get(key) || keyboard?.get(key.toUpperCase())),
            playerAt: () => player ? this.entityStageLocal(player.entityId) : null,
            bounds: this.hudBounds(),
        })
        this.reportControlsLessonsDone()
    }

    // Tell the server which lessons the player has seen through to the end: on the wreck beach, the move note going is
    // the castaway's cue to speak (ember-tutorial.js).
    private reportControlsLessonsDone = () => {
        for (const lesson of this.controlsLesson.takeFinished()) this.logic.playerController?.client?.sendControlsLessonDone(lesson)
    }

    /**
     * See Client.notePlayerSent: the controls lesson counts the player's own swings, interactions and
     * ready-bar presses. A press is named by the key that picks its item, read off the bar as it stood
     * when the press was sent -- so the note asks for slot 1 back by the same "1" the tray shows.
     */
    public notePlayerSent = (payloadType: string, payload: any) => {
        if (!this.controlsLesson.isActive()) return
        const action = controlsActionSent(payloadType, payload, this.logic.playerEntity?.entityId)
        if (action === "arm" || action === "stow") {
            this.controlsLesson.noteReadyPress(action, readyBarSlotOf(this.logic.playerEntity?.inventory, payload?.itemId))
        } else if (action) {
            this.controlsLesson.noteUsed(action)
        }
    }

    /**
     * The last word on how much of the HUD is showing (see gui/hud_presence.ts). Fed only the GUI's
     * own state -- a held bubble in view, a title card, a clue -- never a camp's; what a
     * chapter holds back arrives as a signal. Written as ALPHA, which none of these widgets set on
     * themselves, so every widget keeps deciding its own visibility and layout and this only ever
     * fades what they decided to show. An unseen piece also stops taking input: pixi hit-tests a
     * transparent object as readily as an opaque one.
     */
    private applyHudPresence = (deltaTime: number) => {
        // Optional: a headless GUI test runs frames with no renderer, and nothing is on screen there.
        const screen = findRendererApp()?.renderer?.screen
        this.hudPresence.update(deltaTime, {
            held: !!screen && SpeechHoldButtons.anyInView(screen),
            announcing: !!this.chapterAnnouncement?.isOpen(),
            clue: !!this.tutorial?.isAnyClueShowing(),
        })
        const pieces: [HudPiece, Container | undefined][] = [
            ["chips", this.chipLayer],
            // The MAP chip goes with the map it toggles.
            ["minimap", this.emberMinimapChip],
            ["minimap", this.emberMinimap],
            ["roster", this.emberRoster],
            ["checklist", this.chapterChecklist],
            ["fireGauge", this.emberFireGauge],
            ["vitals", this.hp],
            ["vitals", this.mana],
            ["readyBar", this.equipped],
        ]
        for (const [piece, view] of pieces) {
            if (!view) continue
            view.alpha = this.hudPresence.alpha(piece)
            view.interactiveChildren = this.hudPresence.takesInput(piece)
        }
        this.applyBare()
    }

    // Nothing but the world on screen (a bare HUD: chapter 0's wreck beach). The pieces above are all held back
    // already; this takes every other ambient widget with them. Written as `renderable`, which none of these set on
    // themselves, so each keeps deciding its own visibility and is right again the moment the screen is not bare.
    // Kept up every frame while bare, because some (the guest's notes) are rebuilt on the fly. Not on this list:
    // what the world or the player raises (a chest's dialog, a title card, the command wheel, the chat field while
    // typing), the touch sticks, the screen fades, and the controls lesson: the server names the only one a bare
    // screen shows (the beach's MOVE, once the player is up: chapter_hud.ts).
    private bareApplied = false
    private applyBare = () => {
        // A conversation clears the screen the same way (gui/conversation_panel.ts).
        const bare = this.hudPresence.isBare() || this.hudPresence.isConversing()
        if (!bare && !this.bareApplied) return
        const notes = (this.container?.children || []).filter(child =>
            child.name === "_help_messaging_" || child.name === "_level_up_stats_")
        const views = [
            this.hp, this.mana, this.hunger, this.bannerText, this.minimapSurface, this.controlBar, this.stickyBanner,
            this.dayCycle, this.featureWidget, this.mobileBuildToolbar, this.mouseHoverElement, ...notes,
            this.chatHistory, this.leaderboard, this.recruitMenuHint, this.importantMessage, this.countdownTimer,
            this.waveIndicator, this.waveLandingFlash, this.letGoButton,
        ] as (Container | undefined)[]
        for (const view of views) {
            if (!view) continue
            view.renderable = !bare
            view.interactiveChildren = !bare
        }
        this.bareApplied = bare
    }

    // A line a roster member has just spoken, but ONLY while the player can't see them say it —
    // once they're in the viewport their bubble is already drawn over their head in the world, and
    // repeating it on the HUD would be noise. Null otherwise. Drives the roster's speech lane.
    // A hit the player had no way of hearing: the roster saw a camp member lose health while they
    // were too far off for the world to have played anything for them.
    //
    // The predicate is deliberately the SAME one doStruck uses to decide whether to play the hit
    // sound at all (isTooDistantFromPlayer -- which also keeps the follow-cam's focus neighbourhood
    // audible), rather than a plain viewport test. Anything looser would double up the sound on a
    // member the player can already hear being hit, which is the whole reason this is conditional.
    public playOffscreenHit = (entityId: EntityId) => {
        const entity = this.logic.getEntity(entityId)
        if (entity && !this.logic.isTooDistantFromPlayer(entity)) {
            return
        }
        this.logic.audio.hit(entity)
    }

    // A stranger has reached the camp's fire. Unlike playOffscreenHit this plays wherever the
    // player is standing, including right next to them: the sound is not a substitute for seeing
    // it happen, it is the camp's doorbell, and a player watching the fire still wants to be told
    // that the thing they are watching is now asking them a question.
    public playVisitorArrival = () => {
        this.logic.audio.visitorArrives()
    }

    // Did the member behind this card FALL, or did they just stop being on the roster?
    //
    // The roster broadcast is a list, and a removal from it carries no reason -- but the two
    // reasons want opposite animations: a camp member leaves it by dying (the card greys and
    // shatters) and a VISITOR leaves it by giving up on the camp and walking back the way they came
    // (the card drifts out, alive). The entity itself is the witness, and it is still in the world
    // in the second case: a disappointed recruit walks all the way back to the map edge before it
    // self-destructs, so at the moment its card goes it is very much still standing.
    //
    // Not decided from the card's last known HP, which is a reading up to a roster tick old -- a
    // visitor cut down between two ticks would be remembered at full health and drift politely off
    // the strip instead of falling.
    public hasMemberFallen = (entityId: EntityId): boolean => {
        const entity = this.logic.getEntity(entityId)
        if (!entity || this.logic.isDestroyed(entityId)) {
            return true
        }
        return entity.isDead() || (entity.hp !== undefined && entity.hp <= 0)
    }

    public getOffscreenSpeech = (entityId: EntityId): string | null => {
        const entity = this.logic.getEntity(entityId)
        if (!entity || !entity.location || this.logic.isDestroyed(entityId)) {
            return null
        }
        if (this.logic.isWithinPlayerViewport(entity.location)) {
            return null
        }
        return this.logic.getRecentSpeech(entityId)
    }

    updateSquadState = (squadState: SquadState) => {
        this.squad.update(squadState)
    }

    // The ember camp's wave badge, driven by the wave manager script's "waveStatus" screen messages
    // (see ember-wave-manager.js). The structured counts ride in the message's context; the text
    // carries the wave number as well, so a message that somehow arrives without a context can still
    // say whether a wave is on. Wave 0 — or an unreadable payload — takes the badge down.
    private updateWaveIndicator = (message: ScreenMessage) => {
        const context = (message.context || {}) as Partial<WaveStatus>
        const wave = Number(context.wave !== undefined ? context.wave : parseInt(message.text || "0"))
        if (isNaN(wave) || wave <= 0) {
            this.waveIndicator.update(null)
            return
        }
        // A wave has landed, so the pre-wave dial has had its turn.
        if (this.countdownTimer.visible) {
            this.countdownTimer.stop()
            this.countdownTimer.visible = false
        }
        // Consumed whether or not it matches: a countdown that was overtaken by some other wave is
        // not something to hold open and fire on later.
        const awaited = this.waveAwaited
        this.waveAwaited = undefined
        this.playWaveLanding(wave, awaited !== undefined && wave === awaited)
        this.waveIndicator.update({
            wave,
            remaining: Number(context.remaining) || 0,
            total: Number(context.total) || 0,
            alive: Number(context.alive) || 0,
        })
    }

    /**
     * The landing itself: one hit of light thrown in from the edges of the screen.
     *
     * NO SCREEN SHAKE, deliberately. The first version shook the ground as well and it read as the
     * player taking damage -- correctly, because that is exactly what a shaking screen already means
     * here: `screenShake` fires when the player is hit (client_game_logic, beside pulseDamage) and
     * `screenShakeImpulse` is an Ember Core blast going off nearby. A wave spawning at the edge of
     * the camp is neither, and nothing has touched the player at all. Borrowing the damage verb for
     * it makes both of them mean less. The light carries the beat on its own, and being
     * edge-weighted it is also spatially honest about where the thing arrived.
     *
     * waveStatus arrives constantly while a wave is being fought (it carries the kill count), so
     * this never fires on the message alone. A player arriving mid-assault did not witness that
     * wave land, and striking for it would mean every walk into a camp started with a bang.
     */
    private playWaveLanding = (wave: number, watched: boolean) => {
        const seen = this.wavePlayed
        this.wavePlayed = wave
        if (wave <= 0) {
            return
        }
        // Either this client was counting down to exactly this wave, or the number has gone up from
        // one it has already seen. The first clause is what makes a camp's FIRST wave land: there is
        // no earlier wave to have gone up from, and keying on that alone silently swallowed wave 1
        // of every single run -- which is, of course, the one most likely to be watched.
        if (!watched && (seen === undefined || wave <= seen)) {
            return
        }
        // The wave number goes with it: the flash scales itself, so wave 1 is a knock and the late
        // table is a slam without anything out here having to know how.
        this.waveLandingFlash.strike(wave)
    }

    updateEmberRoster = (roster: EmberRosterEntry[]) => {
        this.recruitHintRoster = roster
        this.emberRoster.update(roster)
        this.positionEmberRoster()
        // Only the lord's entry carries the camp's fire. No lord, no camp, no gauge -- which is also
        // what takes it down on the way out of an ember room.
        const lord = roster.find(entry => entry.role === "lord")
        this.emberFireGauge.update(this.invading ? undefined : lord?.fireStrength)
    }

    // Bottom-right of the HUD (the ember room suppresses the minimap that normally sits there).
    // Recomputed each update because the strip's width depends on how many cards are showing --
    // including by the widget itself, when a card finishes breaking apart between roster ticks.
    positionEmberRoster = () => {
        const rightEdge = this.rosterRightEdge()
        // Roughly aligned with the inventory-slots (equipped) widget's bottom (constant BOTTOM - 20),
        // then nudged down 15 so the whole roster/follow-cam system sits a little lower.
        const bottomEdge = BOTTOM + stageViewport().insetY
        this.emberRoster.x = rightEdge - this.emberRoster.rightExtent()
        this.emberRoster.y = bottomEdge - this.emberRoster.bottomExtent()
        // Keep whatever holds the slot above the roster aligned with it as cards come and go.
        this.positionFollowCam()
        this.positionRosterCommandWheel()
        this.positionRolePickerInRosterSlot()
        this.positionEmberMinimap()
    }

    // The single HUD slot directly above the ember roster strip: right-aligned with the strip, its
    // bottom edge a little clear of it. The follow-cam PiP, the minimap and a roster-opened command
    // wheel all live here (only one at a time), so they can't drift apart.
    private rosterSlotAnchor = (width: number, height: number): { x: number; y: number } => {
        const rightEdge = this.rosterRightEdge()
        const rosterTop = this.emberRoster.y - this.emberRoster.topExtent()
        return { x: rightEdge - width, y: rosterTop - 16 - height }
    }

    // Place the follow-cam below the default minimap, unless the player has dragged it elsewhere.
    private positionFollowCam = () => {
        // positionEmberRoster runs during init before followCam exists — guard against that.
        if (!this.followCam?.isOpen()) {
            return
        }
        const anchor = this.clampToScreen(
            this.followCamPlacement ?? {
                x: this.hudRightEdge() - this.followCam.getWidth(),
                y: this.chipRowBottom() + this.emberMinimap.getDefaultHeight() + chipGap(),
            },
            this.followCam.getWidth(),
            this.followCam.getHeight(),
        )
        this.followCam.x = anchor.x
        this.followCam.y = anchor.y
        this.updateFollowCamVisibility()
    }

    private dragFollowCamTo = (x: number, y: number) => {
        this.holdOffMinimapDragAttack()
        this.followCamPlacement = this.clampToScreen({ x, y }, this.followCam.getWidth(), this.followCam.getHeight())
        this.followCam.x = this.followCamPlacement.x
        this.followCam.y = this.followCamPlacement.y
    }

    // Where the minimap lives until the player drags it somewhere else: directly under the MAP toggle
    // that turns it on and off, hard against the same right edge, so the control and the thing it
    // controls are one object in the corner.
    //
    // It used to default to the slot above the roster strip -- a 176px map wants somewhere roomy -- but
    // a radar wants to be next to its switch, and that slot is the follow-cam PiP's anyway. The chip's
    // row is measured rather than assumed because the chips carry text, whose metrics are the font's
    // business.
    private updateGameplayControls = () => {
        if (!this.chipLayer) return
        this.chipLayer.visible =
            !!this.logic.playerEntity?.visible &&
            !!this.logic.roomId &&
            this.currenRoomName !== LIMBO_ROOM_NAME &&
            !this.characterBuilder?.isOpen() &&
            !this.characterCustomizer?.isOpen() &&
            !this.blackout?.visible
    }

    private minimapDefaultAnchor = (width: number): Coordinate => ({
        x: this.hudRightEdge() - width,
        y: this.chipRowBottom(),
    })

    // Item dialogs live on the stage, while the minimap uses HUD-local coordinates.
    itemPanelDefaultAnchor = (width: number): Coordinate => {
        const at = this.minimapDefaultAnchor(width)
        return { x: at.x + this.container.x, y: at.y + this.container.y }
    }

    // The y directly below the top-right chip row. Reserved whether or not any chip is currently IN
    // it, for the same reason layoutChipRow reserves each chip's slot: nothing stacked underneath
    // should slide about as chips come and go.
    /**
     * The top edge of the HUD's top band, shared by both clusters hanging from it: the countdown
     * dial's column on the left, and the chip row on the right. They read as one band across the top
     * of the screen, so they are anchored to one line rather than to two constants that agreed by
     * accident -- which they did not.
     *
     * `safeViewport.top` already carries the viewport's own top (-insetY, see stageViewport) as well
     * as a device's notch, so anything placed from this follows both without a seat in
     * layoutHudEdges' shift lists.
     */
    private hudTopEdge = (): number => CHIP_ROW_Y + getUiMetrics().safeViewport.top

    private chipRowBottom = (): number => this.hudTopEdge() + this.emberMinimapChip.getHeight() + chipGap()

    /**
     * Lay the player's vitals along the top edge of the readied-item tray (see VITALS_BAND).
     *
     * Derived from the tray every frame rather than nudged about by the edge-inset shifts, so there
     * is exactly one place that decides where these three sit — which is why hp/mana/hunger are in
     * neither of layoutHudEdges' lists.
     *
     * A hidden meter is still placed but takes no room, so suppressing hunger in an ember camp does
     * not leave a gap in the middle of the row.
     */
    private positionVitals = () => {
        if (!this.hp || !this.equipped) return
        // Each of these draws its art at some offset inside its own bounds -- the hearts sit on a
        // plate that overhangs them, the bars hang off an icon -- so each is placed by a VISIBLE
        // edge rather than by its origin.
        const edge = (meter: Container) => {
            const bounds = meter.getLocalBounds()
            return {
                left: bounds.x * meter.scale.x,
                middle: (bounds.y + bounds.height / 2) * meter.scale.y,
                width: bounds.width * meter.scale.x,
                drawn: bounds.height > 0,
            }
        }

        // The hearts set the row, and they are placed by their PLATE: that plate and the tray's
        // directly below it are two panels of one cluster, and a step between their left edges is
        // the difference between reading as one object and as two that happen to be near each other.
        //
        // Measured off plateRect rather than getLocalBounds, because a beating or bursting heart is
        // scaled up to 1.9 -- laying the row out against live bounds would shove it sideways on
        // every hit.
        const plate = this.hp.plateRect()
        const art = this.hp.artRect()
        if (!(plate.height > 0)) return
        this.hp.x = this.equipped.x - plate.x
        this.hp.y = this.equipped.y - VITALS_GAP - (plate.y + plate.height)

        // Everything after it is CENTRED on the hearts' own middle, not levelled with the plate's
        // bottom edge -- that edge is four pixels below the lowest heart, and a bar sitting on it
        // reads low against the art beside it. The hearts' art is centred on their origin, so their
        // middle IS hp.y.
        let x = this.hp.x + art.x + art.width + VITALS_ROW_GAP
        const place = (meter: Container) => {
            if (!meter) return
            const at = edge(meter)
            if (!at.drawn) return
            meter.x = x - at.left
            meter.y = this.hp.y - at.middle
            if (meter.visible) x += at.width + VITALS_ROW_GAP
        }
        place(this.mana)
        place(this.hunger)

        // ONE plate under the whole row. Sized last, because only its WIDTH changes -- the hearts
        // have already been placed by a left and top that this cannot move. Without it the hearts
        // sit on a panel while the bars float on the grass beside them, which is the normal
        // overworld case (an ember camp suppresses both bars, so it never showed up there).
        const rowRight = x - VITALS_ROW_GAP
        this.hp.setPlateWidth(Math.max(plate.width, rowRight - (this.hp.x + plate.x) + VITALS_PLATE_PAD))
    }

    // The command wheel and role picker temporarily hide an overlapping map. The follow-cam
    // can overlap it freely and renders above it instead.
    private claimedSlotRect = (): PanelRect | null => {
        const rect = (width: number, height: number): PanelRect => {
            const at = this.clampToScreen(this.rosterSlotAnchor(width, height), width, height)
            return { x: at.x, y: at.y, width, height }
        }
        if (this.commandWheelRosterId && this.commandWheel) {
            const extent = this.commandWheel.getExtent()
            return rect(extent.left + extent.right, extent.top + extent.bottom)
        }
        if (this.rolePickerRosterId && this.rolePicker) {
            return rect(this.rolePicker.getPanelWidth(), this.rolePicker.getPanelHeight())
        }
        return null
    }

    // The minimap's home is under its own MAP toggle (see minimapDefaultAnchor), and anywhere the
    // player has dragged it to after that.
    //
    // Wherever it is, it steps aside for whatever has claimed the slot above the roster strip — the
    // roster command wheel or role picker — but ONLY if the two would actually
    // OVERLAP. That last part matters more than it sounds: the map used to vanish for any of those
    // panels wherever it had been dragged to, so a player who had parked it in the opposite corner
    // watched it disappear every time they tapped a roster card, for no reason they could see — which
    // is how you come away from a playthrough not realising the map can simply be left open. A panel
    // the map is nowhere near is not a reason to take the map away.
    //
    // Collapsed, the map gives up its space entirely and its chip carries the "off" state instead. The
    // chip stays on screen either way, including while another panel overlaps the map, so the
    // player can always change whether the map is enabled.
    private positionEmberMinimap = () => {
        if (!this.emberMinimap) {
            return
        }
        const hasMap = this.emberMinimap.hasData()
        const width = this.emberMinimap.getWidth()
        const height = this.emberMinimap.getHeight()
        // Re-clamped rather than taken as read: the map is a plan of the room and carries its aspect
        // ratio, and it has three sizes — so the panel a placement was chosen for may not be the size
        // of this one.
        const at = this.emberMinimapPlacement
            ? this.clampToScreen(this.emberMinimapPlacement, width, height)
            : this.minimapDefaultAnchor(width)
        const claimed = this.claimedSlotRect()
        const clear = !claimed || !rectsOverlap({ x: at.x, y: at.y, width, height }, claimed)

        // A held-back map (gui/hud_presence.ts) is as though there were none.
        const show = hasMap && clear && !this.emberMinimapCollapsed && !this.hudPresence.hides("minimap")
        this.emberMinimap.visible = show
        if (show) {
            this.emberMinimap.x = at.x
            this.emberMinimap.y = at.y
        }

        this.emberMinimapChip.visible = hasMap && !this.hudPresence.hides("minimap")
        this.emberMinimapChip.setActive(!this.emberMinimapCollapsed)
        // Top-right corner of the screen. Ember rooms suppress the control bar that normally lives
        // there, so the corner is the camp HUD's. Laid out unconditionally: the map toggle holds its
        // slot even while it is hidden, so the chips beside it do not move when the map is unfolded.
        this.layoutChipRow()
        // The map now sits at the head of the right-edge column, so whatever stacks under it has to be
        // told when the map appears, folds away, or changes size. One-way: positionEmberPlayers reads
        // the map's placement and never sets it.
        if (this.emberPlayersChip) {
            this.positionEmberPlayers()
        }
    }

    // The x every right-justified HUD panel measures back from, in this container's coordinates.
    private rosterRightEdge = (): number =>
        ScreenDimensions.w + TileSize * 4.7 + stageViewport().inset - this.container.x - HUD_EDGE_MARGIN

    private hudRightEdge = (): number => {
        const metrics = getUiMetrics()
        const safeRight = metrics.viewport.left + metrics.viewport.width - metrics.safeViewport.right
        if (metrics.touch || isMobile()) {
            return this.rosterRightEdge()
        }
        return ScreenDimensions.w + TileSize * 4.7 + stageViewport().inset - this.container.x - 6 - safeRight
    }

    // Whether the SAY chip belongs on this machine at all.
    //
    // It is a stand-in for a key: `requestChat` is what Enter does, and the chip exists because a
    // phone has no Enter (see the constructor, and player.ts's requestChat). On a keyboard the
    // player already has that key in every room, so the chip only spent a slot in the camp's
    // corner to say a second time what Enter says -- and the corner is the one part of the HUD
    // that is actually crowded. DEVICE, not room: the room decides whether the control bar is
    // suppressed, this decides whether the stand-in is wanted when it is.
    private chatChipWanted = (): boolean => getUiMetrics().phone

    // The top-right chip row, laid out right to left: map, items, say, players.
    //
    // Every chip keeps its slot whether or not it is currently on screen. They appear and vanish
    // independently -- the map toggle exists while the room has map data, the players count
    // only while somebody else is logged in -- and a row that closed up around a missing one would
    // shuffle the others sideways under the player's thumb each time that happened. Reserving the
    // slots costs a few empty pixels and buys a row that never moves.
    //
    // Called again whenever a chip's width can change; the players chip carries a count, so it
    // does.
    private layoutChipRow = () => {
        let right = this.hudRightEdge()
        const place = (chip?: { x: number; y: number; getWidth: () => number }) => {
            if (!chip) {
                return
            }
            chip.x = right - chip.getWidth()
            chip.y = this.hudTopEdge()
            right -= chip.getWidth() + chipGap()
        }
        // A held-back MAP chip gives up its slot, like the SAY chip below: it is not coming back soon,
        // and holding the corner for it would leave the Items chip beside a hole.
        place(this.hudPresence.hides("minimap") ? undefined : this.emberMinimapChip)
        place(this.emberInventoryChip)
        // Inboard of the players chip on purpose: that one carries a count, so its width moves when
        // a tenth player logs in -- and everything LEFT of it moves with it. Reserving slots keeps
        // the row still as chips come and go, but it cannot keep a chip still beside one that
        // grows, so the growing one goes on the end.
        // ...and the SAY chip is left OUT of the row entirely on a keyboard, rather than hidden in
        // it. The reservation above buys a row that holds still as chips come and go; a chip that
        // never comes at all is not that case, and holding its slot would leave a permanent hole.
        place(this.chatChipWanted() ? this.chatChip : undefined)
        place(this.emberPlayersChip)
    }

    // Who else is logged in, as the server last told us. An empty list takes the chip down and, with
    // it, the panel -- there is nothing left to invite.
    updateEmberPlayers = (players: EmberPlayerEntry[]) => {
        if (!this.emberPlayersChip) {
            // A room update can land before the HUD has finished standing itself up.
            return
        }
        this.emberPlayers = players || []
        // The chip owns its own visibility (and the orange flash it makes on arrival) -- it is the
        // count that decides, and only the chip knows whether this count is the first one.
        this.emberPlayersChip.update(this.emberPlayers.length)
        if (this.emberPlayers.length < 1) {
            this.emberPlayersPanel.visible = false
        }
        if (this.emberPlayersPanel.visible) {
            this.emberPlayersPanel.update(this.emberPlayers)
        }
        this.positionEmberPlayers()
    }

    // Open or close the player list. Filled on open rather than kept up to date while hidden: the feed
    // only speaks when the list CHANGES, so a panel that waited for the next broadcast could sit empty
    // indefinitely.
    private setEmberPlayersOpen = (open: boolean) => {
        const show = open && this.emberPlayers.length > 0
        if (show) {
            this.emberPlayersPanel.update(this.emberPlayers)
        }
        this.emberPlayersPanel.visible = show
        this.positionEmberPlayers()
    }

    // Ask the server to pull another player into this camp. It is a request in both directions: the
    // server decides whether it is allowed and where they land, and the INVITED player decides
    // whether to come at all (see the EMBER_PLAYERS campaign and updateEmberInvite).
    private positionEmberInviteModal = () => {
        const screen = getRendererApp().renderer.screen
        // Renderer coordinates include the stage's scale and translation. Convert both viewport
        // corners into the modal's space before drawing the scrim or positioning the dialog.
        const topLeft = this.emberInviteModal.toLocal(new Point(screen.x, screen.y))
        const bottomRight = this.emberInviteModal.toLocal(new Point(screen.x + screen.width, screen.y + screen.height))
        this.emberInviteModal.resize(bottomRight.x - topLeft.x, bottomRight.y - topLeft.y, topLeft.x, topLeft.y)
    }

    private inviteEmberPlayer = (entityId: EntityId, playMode: EmberPlayMode = "pvp") => {
        this.logic.playerController?.client?.inviteEmberPlayer(entityId, playMode)
    }

    // An invitation into somebody's camp, or null to take the prompt down. Deliberately NOT gated on
    // being in an ember room: the player being invited is, by definition, somewhere else.
    updateEmberInvite = (invite: EmberInvite | null) => {
        if (!this.emberInvitePrompt) {
            return
        }
        this.emberInviteFrom = invite?.fromEntityId
        this.emberInvitePrompt.update(invite)
        this.positionEmberPlayers()
    }

    // The player's answer. Only an accepted one moves them, and the server does the moving -- this
    // says nothing about where they will land.
    private answerEmberInvite = (accepted: boolean) => {
        const from = this.emberInviteFrom
        this.emberInviteFrom = undefined
        if (from) {
            this.logic.playerController?.client?.answerEmberInvite(from, accepted)
        }
        this.positionEmberPlayers()
    }

    // The players chip takes the leftmost place in the top-right row (see layoutChipRow), and the
    // list of players hangs under it against the same right edge.
    private positionEmberPlayers = () => {
        if (!this.emberPlayersChip) {
            return
        }
        const rightEdge = this.hudRightEdge()
        // The chip's own place in the top-right row. Re-run here because this chip's width moves
        // with the count it carries, which nothing else is watching.
        this.layoutChipRow()
        // One column down the right edge: the chip's row, then the MAP if it is still parked under its
        // toggle, then the invitation prompt, then the list. The chip's row is reserved whether or not
        // the chip is IN it, for the same reason the map toggle's slot is -- so nothing below it slides
        // about as players come and go.
        let below = this.chipRowBottom()
        if (this.minimapHoldsTheCorner()) {
            below += this.emberMinimap.getHeight() + chipGap()
        }
        if (this.emberInvitePrompt?.isOpen()) {
            this.emberInvitePrompt.x = rightEdge - this.emberInvitePrompt.getWidth()
            this.emberInvitePrompt.y = below
            below += this.emberInvitePrompt.getHeight() + chipGap()
        }
        this.emberPlayersPanel.fitToViewport(below)
        this.emberPlayersPanel.x = rightEdge - this.emberPlayersPanel.getWidth()
        this.emberPlayersPanel.y =
            isMobile() || getUiMetrics().touch
                ? Math.min(below, getUiMetrics().safeViewport.bottom - this.emberPlayersPanel.getHeight() - 12)
                : below
    }

    // Whether the map is currently sitting in its default spot under the MAP toggle -- in which case
    // everything else that stacks down that right edge has to start below it rather than under the
    // chip row. Derived rather than remembered, so it cannot be read before positionEmberMinimap has
    // run: the two only share the chip row's height and the map's own size.
    private minimapHoldsTheCorner = (): boolean => !!this.emberMinimap?.visible && !this.emberMinimapPlacement

    // What the top-right MAP chip does: flip the map between shown and folded away. One control for
    // both directions, because that is what makes it read as a toggle with a state rather than a
    // button that opens something.
    private toggleEmberMinimap = () => this.setEmberMinimapCollapsed(!this.emberMinimapCollapsed)

    // Fold the map away (or bring it back), remembering the choice.
    private setEmberMinimapCollapsed = (collapsed: boolean) => {
        if (this.emberMinimapCollapsed === collapsed) {
            return
        }
        this.emberMinimapCollapsed = collapsed
        this.saveEmberMinimapCollapsed(collapsed)
        this.positionEmberMinimap()
    }

    // The collapsed preference is a per-viewer convenience, so it lives in localStorage — which can
    // throw or come back empty (private windows, blocked site data), hence the guards.
    private loadEmberMinimapCollapsed = (): boolean => {
        try {
            if (typeof localStorage !== "undefined") {
                return localStorage.getItem(this.EMBER_MINIMAP_COLLAPSED_KEY) === "true"
            }
        } catch (error) {
            console.warn("Failed to load minimap collapsed state from localStorage:", error)
        }
        return false
    }

    private saveEmberMinimapCollapsed = (collapsed: boolean) => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_MINIMAP_COLLAPSED_KEY, String(collapsed))
            }
        } catch (error) {
            console.warn("Failed to save minimap collapsed state to localStorage:", error)
        }
    }

    /**
     * Hand the lane's current choice to the strip, and advance its clock.
     *
     * Called from the frame loop with a real delta, and again with 0 the moment a message is pushed
     * so a strip that was empty lights up on the spot instead of on the next frame.
     *
     * `takeChange` is what keeps this cheap: with a settled lane -- which is nearly always -- this is
     * one addition and one boolean read per frame, and the widget is not touched at all.
     */
    private pumpImportantLane = (deltaTime: number) => {
        if (deltaTime > 0) {
            this.importantLane.update(deltaTime)
        }
        if (!this.importantLane.takeChange()) {
            return
        }
        const notice = this.importantLane.current()
        if (!notice) {
            // Nothing waiting, or a handover gap: fade the strip down rather than cutting it.
            this.importantMessage.dismiss()
            return
        }
        // The original message, with the lane's resolved hold and tier written over it: every other
        // field it was sent with (a displayCallback, most of all) survives the wait in the queue.
        this.importantMessage.update({
            ...(notice.source || {}),
            text: notice.text,
            duration: holdFor(notice),
            context: { ...(notice.source?.context || {}), tier: notice.tier },
        })
    }

    /**
     * Rebuild the camp's standing-task list from the minimap feed.
     *
     * The feed is the source for this because it already carries the state and already arrives once a
     * second (EmberMinimap.MINIMAP_INTERVAL_MS) -- see gui/ember_obligations.ts. A null feed is a room
     * change, which is also the moment any dismissal stops meaning anything: the next camp's ritual is
     * not the one this player waved away.
     */
    private refreshEmberObligations = (minimap: EmberMinimapState | null) => {
        if (!this.emberObligations) {
            return
        }
        if (!minimap) {
            this.emberObligationDismissals.reset()
            this.emberObligationFeed = []
            this.emberObligations.update([])
            return
        }
        this.emberObligationFeed = deriveObligations(minimap)
        this.emberObligations.update(this.emberObligationDismissals.visible(this.emberObligationFeed))
    }

    private dismissEmberObligation = (obligation: Obligation) => {
        this.emberObligationDismissals.dismiss(obligation)
        this.emberObligations.update(this.emberObligationDismissals.visible(this.emberObligationFeed))
    }

    /**
     * In the top row, beside the hearts: a dropdown whose header shares the HP bar's line and whose
     * rows hang below it.
     *
     * The top-left of the HUD is a COLUMN, not a corner -- hearts, then the countdown dial or the
     * wave badge in its place, then the invader's let-go button -- and the first version of this sat
     * on top of the dial, which only shows once a wave is actually counting down. So the stack moves
     * out of that column entirely and starts to the right of it, on the line the player's own
     * hearts are on. What they are, what the clock says, and what they still owe end up readable in
     * one sweep across the top of the screen.
     *
     * CLEARING THE DIAL, NOT THE HEARTS, is what sets the left edge: the dial is wider than the five
     * hearts and is left-aligned with them, so a stack that merely cleared the hearts would be back
     * under the dial the moment a wave was called. Its slot is reserved whether or not it is
     * currently drawn -- `dialSize` is a constant of the widget, not a measurement of what it is
     * painting -- so calling a wave never moves this panel, which is the one moment the player can
     * least afford to go looking for it again. (Same rule, and the same reason, as layoutChipRow
     * reserving a slot per chip.)
     *
     * Placed per frame like the anxiety meter rather than once on a layout pass, so it follows a
     * viewport inset change without needing a seat in layoutHudEdges.
     */
    private placeEmberObligations = (deltaTime: number) => {
        if (!this.emberObligations || !this.countdownTimer || !this.hp) {
            return
        }
        // Re-asserted rather than latched: the same gate as the chip row (no HUD over character
        // creation, a blackout, or a dead player), and it has to be able to let the panel back.
        const gated = !!this.chipLayer && !this.chipLayer.visible
        this.emberObligations.visible = this.emberObligations.hasTasks() && !gated
        if (!this.emberObligations.visible) {
            return
        }
        // Take turns with the strip rather than sharing the band: a notice speaks, and the stack
        // unfolds once it has gone. See EmberObligationsPanel.setYielded and OBLIGATIONS_YIELD_MS.
        this.obligationsYieldMs = this.importantLane.current()
            ? OBLIGATIONS_YIELD_MS
            : Math.max(0, this.obligationsYieldMs - deltaTime)
        this.emberObligations.setYielded(this.obligationsYieldMs > 0)
        // Beside the dial, which heads this column on its own now that the hearts have gone to the
        // tray. Both live on the stage, so no container offset comes into it. Hung from the dial's
        // TOP edge because the rows drop below the header.
        const dial = this.countdownTimer.dialSize
        this.emberObligations.position.set(
            Math.round(this.countdownTimer.x + dial / 2 + OBLIGATIONS_GAP),
            Math.round(this.countdownTimer.y - dial / 2),
        )
    }

    /**
     * The head of the top-left column: the countdown dial, the wave badge that stands in its exact
     * slot, and the invader's LET GO button under them.
     *
     * Placed per frame from hudTopEdge rather than once from a constant, so the dial's bezel lands on
     * the same line as the chip plates across the band -- including on a notched phone, where the
     * chips step down for the notch and a dial placed once did not.
     *
     * These three are therefore OUT of layoutHudEdges' vertical shift list: hudTopEdge already
     * carries the inset, so being shifted as well would double it. Their x is still shifted there,
     * which is why only the y is set here.
     */
    private placeColumnHead = () => {
        const dial = this.countdownTimer
        if (!dial) {
            return
        }
        dial.y = this.hudTopEdge() + dial.dialSize / 2
        if (this.waveIndicator) {
            // The badge takes the dial's slot exactly -- same size, never both up (addScreenMessage).
            this.waveIndicator.y = dial.y
            if (this.letGoButton) {
                this.letGoButton.y = Math.round(
                    dial.y + Math.max(dial.bottomExtent, this.waveIndicator.bottomExtent) + 10,
                )
            }
        }
    }

    /**
     * The fire gauge's place in the top-left column.
     *
     * It used to be parked at a fixed height there: the slot the old "WAVE n" pill hung in, directly
     * under the dial. That is right whenever a dial IS up, which is most of a camp being fought -- a
     * countdown, then the wave badge, then the next countdown. But both of those are TRANSIENT and
     * the fire is not; it burns for the whole run. So in a camp's opening calm, and again once its
     * waves are done, the gauge was left floating a dial's height down the left edge with nothing
     * above it to belong to, which reads as a widget that has come loose rather than as a readout.
     *
     * It rides the column instead: under whichever dial is up, and at the column's HEAD when neither
     * is. The dial keeps the corner whenever it has something to say, because it is not a camp's
     * widget at all -- addScreenMessage counts down with it in any room -- and a camp-only readout
     * should not push a general one off its anchor.
     *
     * Placed per frame and derived from the dial, exactly like placeEmberObligations beside it: one
     * writer for this position, and it follows a viewport inset without a seat in layoutHudEdges.
     */
    private placeEmberFireGauge = (deltaTime: number) => {
        if (!this.emberFireGauge || !this.countdownTimer) {
            return
        }
        const dial = this.countdownTimer
        // Both live on the stage, so no container offset comes into it. The gauge is left-anchored
        // and vertically CENTRED (see its setWidth/height_), hence the half-height on the y.
        const head = dial.y - dial.dialSize / 2
        const headed = dial.visible || !!this.waveIndicator?.visible
        const target = Math.round(
            headed
                ? head + dial.dialSize + FIRE_GAUGE_GAP + this.emberFireGauge.height_ / 2
                : head + this.emberFireGauge.height_ / 2,
        )
        this.emberFireGauge.x = Math.round(dial.x - dial.dialSize / 2)

        // A dial arriving or leaving is the one move worth sliding: it happens about twice a run,
        // it is a dial's height, and a teleport that far reads as a glitch. Every OTHER move of this
        // widget is the viewport's -- an inset change, a resize -- and those have to land at once,
        // or the gauge would ease across the screen behind a window the player is still dragging.
        const was = this.fireGaugeHeaded ?? headed
        this.fireGaugeHeaded = headed
        if (!this.emberFireGauge.visible) {
            // A room change takes the reading away mid-slide. Dropping the slide here means the next
            // camp's gauge is simply already in place rather than easing in from the last one's.
            this.fireGaugeSliding = false
        } else if (was !== headed) {
            this.fireGaugeSliding = true
        }
        if (!this.fireGaugeSliding) {
            this.emberFireGauge.y = target
            return
        }
        // Linear and CLAMPED to what is left, so it lands exactly rather than approaching forever
        // -- the same bargain the bar's own travel makes (gui/ember_fire_gauge.ts).
        const travel = FIRE_GAUGE_SLIDE * deltaTime
        const gap = target - this.emberFireGauge.y
        this.emberFireGauge.y += Math.max(-travel, Math.min(travel, gap))
        if (this.emberFireGauge.y === target) {
            this.fireGaugeSliding = false
        }
    }

    private loadEmberObligationsCollapsed = (): boolean => {
        try {
            if (typeof localStorage !== "undefined") {
                return localStorage.getItem(this.EMBER_OBLIGATIONS_COLLAPSED_KEY) === "true"
            }
        } catch (error) {
            console.warn("Failed to load camp tasks collapsed state from localStorage:", error)
        }
        return false
    }

    private saveEmberObligationsCollapsed = (collapsed: boolean) => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_OBLIGATIONS_COLLAPSED_KEY, String(collapsed))
            }
        } catch (error) {
            console.warn("Failed to save camp tasks collapsed state to localStorage:", error)
        }
    }

    // The widget owns the size cycle and tells us where it landed. Two jobs here: remember it, and
    // re-place a panel that has just changed size under a placement chosen for the old one.
    private onEmberMinimapResized = (size: MinimapSize) => {
        this.emberMinimapSize = size
        this.saveEmberMinimapSize(size)
        this.positionEmberMinimap()
    }

    // Like the other two map preferences, a per-viewer convenience in localStorage — which can throw
    // or come back empty (private windows, blocked site data), and whose contents are whatever was
    // last written there, hence the guards and asMinimapSize.
    private loadEmberMinimapSize = (): MinimapSize => {
        try {
            if (typeof localStorage !== "undefined") {
                return asMinimapSize(localStorage.getItem(this.EMBER_MINIMAP_SIZE_KEY)) ?? DEFAULT_MINIMAP_SIZE
            }
        } catch (error) {
            console.warn("Failed to load minimap size from localStorage:", error)
        }
        return DEFAULT_MINIMAP_SIZE
    }

    private saveEmberMinimapSize = (size: MinimapSize) => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_MINIMAP_SIZE_KEY, size)
            }
        } catch (error) {
            console.warn("Failed to save minimap size to localStorage:", error)
        }
    }

    // ---- dragging the minimap ------------------------------------------------
    //
    // The whole panel is a grab handle (see EmberMinimapWidget.attachDrag), because the part of the
    // camp the map covers is sometimes the part the player needs to watch. GuiManager does the moving
    // rather than the widget: it is what re-anchors the panel above the roster strip, and so the only
    // place that can both honour a dragged position and keep it on screen.

    // A press on the panel must never also swing the player's axe. The attack click arrives via a
    // document-level mousedown that cannot see which pixi widget it landed on, so it is held off the
    // same way an interaction-icon click is (see Player.suppressAttack) — renewed on grab, on every
    // drag frame and once more on release, so a drag of any length is covered end to end. A drag that
    // pauses needs no cover: a still pointer raises no mouse events, so nothing can fire.
    private static readonly MINIMAP_DRAG_ATTACK_HOLDOFF_MS = 500
    private holdOffMinimapDragAttack = () =>
        this.logic.playerController?.suppressAttack(GuiManager.MINIMAP_DRAG_ATTACK_HOLDOFF_MS)

    private dragEmberMinimapTo = (x: number, y: number) => {
        this.holdOffMinimapDragAttack()
        const at = this.clampToScreen({ x, y }, this.emberMinimap.getWidth(), this.emberMinimap.getHeight())
        this.emberMinimapPlacement = at
        this.emberMinimap.x = at.x
        this.emberMinimap.y = at.y
    }

    // Saved on release rather than per frame: a drag is hundreds of positions and only the last one is
    // the answer.
    private dropEmberMinimap = () => {
        this.holdOffMinimapDragAttack()
        this.saveEmberMinimapPlacement()
    }

    // ---- SpriteLab -------------------------------------------------------------------------------

    // The SpriteLab room's state from the server (null outside one, which takes the panel down), and
    // the message its last request produced, if any.
    updateSpriteLab = (state: SpriteLabState | null, message?: string) => {
        const panel = this.spriteLabPanel
        if (!panel) return
        const appearing = !!state && !panel.visible
        panel.update(state, message)
        if (appearing) this.placeSpriteLabPanel()
    }

    private createSpriteLabPanel = (): SpriteLabPanel => {
        const saved = this.loadSpriteLabPanel()
        this.spriteLabPlacement = saved.at
        // The panel lays itself out (and reports it) while it is being built; nothing to place yet.
        let panel: SpriteLabPanel | undefined
        panel = new SpriteLabPanel({
            onRequest: request => this.logic.playerController?.client?.sendSpriteLabRequest(request),
            onGrab: this.holdOffMinimapDragAttack,
            onDragTo: (x, y) => {
                this.holdOffMinimapDragAttack()
                if (!panel) return
                const at = this.clampToScreen({ x, y }, panel.getWidth(), panel.getHeight())
                this.spriteLabPlacement = at
                panel.position.set(at.x, at.y)
            },
            onRelease: () => {
                this.holdOffMinimapDragAttack()
                this.saveSpriteLabPanel()
            },
            // Folding, unfolding and new queue rows change the height: keep it on screen without
            // forgetting where the player put it.
            onLayoutChange: () => {
                if (!panel || this.spriteLabPanel !== panel) return
                this.placeSpriteLabPanel()
                this.saveSpriteLabPanel()
            },
            onClaimInput: () => this.logic.playerController?.cancelAttackForUi(),
            suppressGameKeys: suppressed => Keyboard.suppressTyping(suppressed),
            canvasRect: () => {
                try {
                    return getRendererApp().view.getBoundingClientRect()
                } catch (e) {
                    return null
                }
            },
            rendererScreen: () => {
                try {
                    const screen = getRendererApp().renderer.screen
                    return { width: screen.width, height: screen.height }
                } catch (e) {
                    return { width: 0, height: 0 }
                }
            },
        })
        // Over the HUD's readouts, under every modal and the command wheel.
        panel.zIndex = 55000
        panel.setMinimized(saved.minimized)
        panel.setCreationsOpen(saved.creationsOpen)
        return panel
    }

    // Top-left by default, under the name line: the room is empty grass, so nothing else wants it.
    private placeSpriteLabPanel = () => {
        const panel = this.spriteLabPanel
        if (!panel) return
        const bounds = this.hudBounds()
        const at = this.spriteLabPlacement ?? { x: bounds.left + 14, y: bounds.top + 34 }
        const clamped = this.clampToScreen(at, panel.getWidth(), panel.getHeight())
        panel.position.set(clamped.x, clamped.y)
    }

    private loadSpriteLabPanel = (): { at: Coordinate | null; minimized: boolean; creationsOpen: boolean } => {
        try {
            if (typeof localStorage !== "undefined") {
                const stored = JSON.parse(localStorage.getItem(this.SPRITELAB_PANEL_KEY) || "null")
                return {
                    at: isFinite(stored?.x) && isFinite(stored?.y) ? { x: stored.x, y: stored.y } : null,
                    minimized: stored?.minimized === true,
                    creationsOpen: stored?.creationsOpen === true,
                }
            }
        } catch (error) {
            console.warn("Failed to load the SpriteLab panel's placement from localStorage:", error)
        }
        return { at: null, minimized: false, creationsOpen: false }
    }

    private saveSpriteLabPanel = () => {
        const panel = this.spriteLabPanel
        if (!panel) return
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(
                    this.SPRITELAB_PANEL_KEY,
                    JSON.stringify({
                        ...(this.spriteLabPlacement || {}),
                        minimized: panel.isMinimized(),
                        creationsOpen: panel.isCreationsOpen(),
                    }),
                )
            }
        } catch (error) {
            console.warn("Failed to save the SpriteLab panel's placement to localStorage:", error)
        }
    }

    // The screen, in the HUD container's own coordinates: the container is nudged in from the left
    // edge, so screen left sits at -container.x; the right edge is the same expression the rest of the
    // HUD right-justifies against, and BOTTOM is the HUD's own floor.
    private hudBounds = () => {
        const { inset, insetY } = stageViewport()
        const screenWidth = ScreenDimensions.w + TileSize * 4.7 + inset
        return {
            left: -inset - this.container.x,
            top: -insetY,
            right: screenWidth - this.container.x,
            bottom: BOTTOM + insetY,
        }
    }

    // Expand the space between the two edge columns, keeping each widget's desktop-sized margin
    // and scale. Apply only the change in inset so live widget positions survive repeated frames.
    private layoutHudEdges = () => {
        const viewport = stageViewport()
        const { inset, insetY } = viewport
        const delta = inset - this.hudInset
        const deltaY = insetY - this.hudInsetY
        if (!delta && !deltaY) return
        this.hudInset = inset
        this.hudInsetY = insetY
        const shift = (widgets: DisplayObject[], offset: number, axis: "x" | "y" = "x") => {
            widgets.forEach(widget => {
                if (widget) widget[axis] += offset
            })
        }
        shift(
            [
                this.nameText,
                this.resourceBar,
                this.equipped,
                this.abilities,
                this.squad,
                this.featureWidget,
                this.bannerText,
                this.typing,
                this.countdownTimer,
                this.waveIndicator,
                this.letGoButton,
                this.fpsMeter,
                this.container.getChildByName("_level_up_stats_"),
            ],
            -delta,
        )
        if (!this.chatComposer?.isOpen()) shift([this.chatHistory], -delta)
        shift(
            [
                this.controlBar,
                this.stickyBanner,
                this.dayCycle,
                this.minimapSurface,
                this.leaderboard,
                this.container.getChildByName("_help_messaging_"),
            ],
            delta,
        )
        shift(
            [
                this.nameText,
                this.resourceBar,
                this.featureWidget,
                this.bannerText,
                this.controlBar,
                this.stickyBanner,
                this.dayCycle,
                this.leaderboard,
                this.fpsMeter,
                this.container.getChildByName("_level_up_stats_"),
            ],
            -deltaY,
            "y",
        )
        shift(
            [
                this.equipped,
                this.abilities,
                this.squad,
                this.typing,
                this.minimapSurface,
                this.container.getChildByName("_help_messaging_"),
            ],
            deltaY,
            "y",
        )
        if (!this.chatComposer?.isOpen()) this.chatHistory.setAnchor("bottom", CHAT_LOG_BOTTOM + insetY)
        for (const [overlay, color] of [
            [this.blackout, 0x000000],
            [this.redout, 0xff0000],
        ] as const) {
            if (!overlay) continue
            overlay.clear().beginFill(color)
            overlay.drawRect(viewport.left, viewport.top, viewport.width, viewport.height)
            overlay.endFill()
        }
        this.positionEmberRoster()
        this.positionEmberPlayers()
    }

    private clampToScreen = (at: Coordinate, width: number, height: number): Coordinate =>
        clampPanelWithin(at, { width, height }, this.hudBounds())

    // The dragged position is a per-viewer convenience, so it lives in localStorage — which can throw
    // or come back empty (private windows, blocked site data), and whose contents are whatever was
    // last written there, hence the guards and the shape check.
    private loadEmberMinimapPlacement = (): Coordinate | null => {
        try {
            if (typeof localStorage !== "undefined") {
                const stored = localStorage.getItem(this.EMBER_MINIMAP_PLACEMENT_KEY)
                const at = stored ? JSON.parse(stored) : null
                if (isFinite(at?.x) && isFinite(at?.y)) {
                    return { x: at.x, y: at.y }
                }
            }
        } catch (error) {
            console.warn("Failed to load minimap placement from localStorage:", error)
        }
        return null
    }

    private saveEmberMinimapPlacement = () => {
        if (!this.emberMinimapPlacement) {
            return
        }
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_MINIMAP_PLACEMENT_KEY, JSON.stringify(this.emberMinimapPlacement))
            }
        } catch (error) {
            console.warn("Failed to save minimap placement to localStorage:", error)
        }
    }

    // The ember room's per-room minimap snapshot (camp members + wave attackers). null takes the map
    // down.
    updateEmberMinimap = (minimap: EmberMinimapState | null) => {
        if (minimap?.roomId && !this.acceptsSceneRoom(minimap.roomId)) {
            this.emberMinimap.update(minimap)
            return
        }
        const rescueId = minimap?.rescue?.id
        const rescueStarted = !!rescueId && rescueId !== this.emberMinimapRescueId
        this.emberMinimapRescueId = rescueId
        const ritualId = minimap?.bonecaller?.id
        const ritualStarted = minimap?.bonecaller?.state === "active" && ritualId !== this.emberMinimapBonecallerId
        this.emberMinimapBonecallerId = ritualId
        this.emberMinimap.update(minimap)
        this.refreshEmberMinimapTrees(minimap)
        this.refreshEmberObligations(minimap)
        if (rescueStarted || ritualStarted) {
            this.setEmberMinimapCollapsed(false)
        }
        this.positionEmberMinimap()
    }

    // Terrain under the dots. The client already holds the tile map, so trees, cave rock and
    // structures are scanned here. The server supplies discovery bits and moving contacts.
    // Keyed on the map's collision version so a felled tree drops off the map, and floored on
    // EMBER_TREE_RESCAN_MS so a run of tile edits can't turn a 5Hz broadcast into a 5Hz full-room scan.
    private static readonly EMBER_TREE_RESCAN_MS = 1000
    private refreshEmberMinimapTrees = (minimap: EmberMinimapState | null) => {
        if (!minimap) {
            this.emberTreeScanKey = null
            return
        }
        const provider = this.logic.tilemapMetaProvider
        if (!provider) {
            return
        }
        const key = `${this.currentRoomId}:${minimap.generationId || ""}:${provider.getCollisionVersion()}:${minimap.width}x${minimap.height}`
        if (key === this.emberTreeScanKey) {
            return
        }
        const now = Date.now()
        // A brand-new room (nothing scanned yet) is drawn immediately; only RE-scans wait their turn.
        const sameGeneration = this.emberTreeScanKey?.startsWith(`${this.currentRoomId}:${minimap.generationId || ""}:`)
        if (sameGeneration && now - this.emberTreeScanAt < GuiManager.EMBER_TREE_RESCAN_MS) {
            return
        }
        this.emberTreeScanKey = key
        this.emberTreeScanAt = now
        const tilesWide = this.emberMinimap.tilesWide()
        const tilesHigh = this.emberMinimap.tilesHigh()
        this.emberMinimap.updateTerrain(collectMinimapTerrain(provider, tilesWide, tilesHigh), tilesWide, tilesHigh)
    }

    // The PiP only earns its screen space while the watched character is OFF-screen. Hide the PiP
    // once the subject enters the player's viewport; show it again when it
    // leaves. Called every frame (updateTimer) so it tracks the subject's movement.
    private updateFollowCamVisibility(): void {
        if (!this.followCam?.isOpen()) {
            this.followCamShown = false
            return
        }
        const focusId = this.followCam.focusEntityId
        if (!focusId || !this.emberRoster.isSelected(focusId)) {
            this.closeFollowCam()
            return
        }
        const entity = this.logic.getFollowCamEntity(focusId)
        // Floor transfers can evict the local entity before its observation frame arrives.
        // The active roster card owns Watch throughout that gap, just as it owns its command wheel.
        const rosterMember = this.recruitHintRoster.some(entry => entry.entityId === focusId)
        if (this.logic.isFocusPending() && (!entity || entity.isDead() || this.logic.isDestroyed(entity.entityId)))
            return
        if (rosterMember && (!entity || (!this.logic.observation?.room && this.logic.isDestroyed(entity.entityId))))
            return
        if (!entity || entity.isDead() || (!this.logic.observation?.room && this.logic.isDestroyed(entity.entityId))) {
            this.closeFollowCam()
            return
        }
        const onScreen =
            entity.roomId === this.logic.playerEntity?.roomId && this.logic.isWithinPlayerViewport(entity.location)
        const shouldShow = !onScreen

        // Only start a fade on an actual transition (this runs every frame).
        if (shouldShow !== this.followCamShown) {
            this.followCamShown = shouldShow
            this.followCamFader?.stop(true)
            if (shouldShow) {
                this.followCam.visible = true
                // minAlpha (the fade target) passed explicitly: Fader computes its default before
                // applying `direction`, so an "in" fade would otherwise target 0 and never appear.
                this.followCamFader = new Fader(this.followCam, { direction: "in", minAlpha: 1.0 })
            } else {
                this.followCamFader = new Fader(this.followCam, {
                    direction: "out",
                    minAlpha: 0,
                    releaseCallback: () => {
                        this.followCam.visible = false
                    },
                })
            }
            this.followCamFader.update()
        }
    }

    // Open the follow-cam PiP on an entity: show the modal, keep the entity's neighborhood live on
    // the client, and ask the server to sync it in full detail every tick.
    openFollowCam = (entityId: EntityId, name?: string) => {
        // Dismiss roster menus when opening the follow-cam.
        if (this.commandWheelRosterId) {
            this.hideCommandWheel()
        }
        if (this.rolePickerRosterId) {
            this.hideRolePicker()
        }
        this.emberRoster.holdSelection(entityId)
        this.followCam.open(entityId, name)
        // Start transparent and let updateFollowCamVisibility fade it in (null forces a fade).
        this.followCam.alpha = 0
        this.followCamShown = null
        this.positionFollowCam()
        // Refresh the map alongside the PiP.
        this.positionEmberMinimap()
        this.logic.setFocusEntityId(entityId)
        this.logic.playerController.client.setFocusEntity(entityId)
    }

    // Close the follow-cam if it's open (Escape / deactivation); no-op otherwise.
    // ---- conversations (gui/conversation_panel.ts) ----------------------------------------------

    private conversationKeysClaimed = false
    private claimsConversationKey = (key: string): boolean =>
        /^([1-9]|Enter|Escape|Esc)$/.test(key) || (key === " " && this.conversationPanel.wantsContinue())

    private onConversationKey = (key: string, down: boolean) => {
        if (down) this.conversationPanel.handleKey(key)
    }

    /** A node of a conversation, from the server: up it comes, and everything else goes. */
    private openConversation = (view: ConversationView) => {
        const opening = !this.conversationPanel.isOpen()
        this.conversationPanel.show(view, getUiMetrics().touch)
        if (!opening) return
        this.hideCommandWheel()
        this.dismissFollowCam()
        this.hudPresence.setConversing(true)
        if (!this.conversationKeysClaimed) {
            this.logic.playerController.claimKeys(this.claimsConversationKey)
            this.logic.clientRenderer.keyboard().addListener(this.onConversationKey)
            this.conversationKeysClaimed = true
        }
    }

    /** The conversation is over (the server says so, or the player left it). */
    private closeConversation = (npcEntityId?: string) => {
        const view = this.conversationPanel.current()
        if (!view || (npcEntityId && view.npcEntityId !== npcEntityId)) return
        this.conversationPanel.close()
        this.hudPresence.setConversing(false)
        if (this.conversationKeysClaimed) {
            this.logic.clientRenderer.keyboard().removeListener(this.onConversationKey)
            this.logic.playerController.releaseKeys(this.claimsConversationKey)
            this.conversationKeysClaimed = false
        }
    }

    /** Esc, Leave, or walking off: closed here at once, and the server told. */
    private leaveConversation = (view: ConversationView) => {
        this.closeConversation(view.npcEntityId)
        this.logic.playerController.client.sendConversationLeave(view.npcEntityId)
    }

    /** Every frame: stand the NPC's line over its head, and leave once the player has walked more than a tile off. */
    private updateConversation = (deltaTime: number) => {
        const panel = this.conversationPanel
        if (!panel || (!panel.isOpen() && !panel.visible)) return
        const view = panel.current()
        let anchor: { x: number; y: number } | null = null
        if (view) {
            const npc = this.logic.isDestroyed(view.npcEntityId) ? undefined : this.logic.getEntity(view.npcEntityId)
            const me = this.logic.playerEntity
            if (!npc || !me || me.roomId !== npc.roomId || conversationGap(me.location, npc.location) > CONVERSATION_LEAVE_GAP) {
                this.leaveConversation(view)
            } else {
                const overlay = this.logic.clientRenderer.map().overlay()
                // Over the head: a body is drawn about two of its tiles above where it stands.
                anchor = this.stage.toLocal(overlay.computeLocation(npc.location.x, npc.location.y - 34), overlay)
            }
        }
        panel.update(deltaTime, anchor, getUiMetrics().safeViewport)
    }

    dismissFollowCam = () => {
        if (this.followCam?.isOpen()) {
            this.closeFollowCam()
        }
    }

    closeFollowCam = () => {
        this.followCamFader?.stop(true)
        this.followCamShown = false
        this.followCam.close()
        this.emberRoster.clearSelection()
        // Refresh the map after closing the PiP.
        this.positionEmberMinimap()
        this.logic.setFocusEntityId(null)
        this.logic.playerController.client.setFocusEntity(null)
    }

    updateBannerText = (text: string) => {
        this.bannerText.text = text
    }

    updateLocation = (label: string) => {
        //
    }

    startChat = () => {
        // The two fields are the same conversation; only one of them can hold the keyboard.
        this.dismissChatComposer()
        this.chatOpen = true
        this.chatInput = EMPTY_INPUT
        this.chatWalk = { ...this.chatWalk, index: -1, draft: "" }
        this.renderChatInput()

        clearTimeout(this.chatHistoryTextTimer)
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        this.chatHistory.alpha = 1.0
        this.chatHistory.show()
    }

    // --- the composer: chat on a device with no keyboard ------------------------------------

    // Put the composer up (gui/chat_composer.ts). The inline field and the composer are never both
    // open: they are the same conversation, and they would both be holding the keyboard.
    public openChatComposer = () => {
        if (!this.chatComposer || this.chatComposer.isOpen()) {
            return
        }
        this.dismissChatInput()
        this.chatOpen = true
        // The log moves with the field it belongs to: docked under the composer at the top of the
        // screen, where no soft keyboard can cover it, and pinned there -- a log that faded out
        // from under a half-typed reply would take the conversation away mid-sentence.
        clearTimeout(this.chatHistoryTextTimer)
        this.chatHistoryFader?.stop(true)
        this.chatHistory.alpha = 1.0
        this.chatHistory.x = COMPOSER_LOG_X
        this.chatHistory.setAnchor("top", COMPOSER_LOG_TOP)
        this.chatHistory.show()
        // The field owns the keyboard from here (see Keyboard.suppressTyping), which also releases
        // whatever was held when it opened -- the movement loop reads that map every frame, so
        // clearing it is what stops the player walking while they type.
        Keyboard.suppressTyping(true)
        this.chatComposer.open()
    }

    public dismissChatComposer = () => {
        if (!this.chatComposer?.isOpen()) {
            return
        }
        // The press that is closing this panel is still travelling. pixi delivers `pointerdown` to
        // the scrim BEFORE the document-level `mousedown` the world action arrives on, so by the
        // time that one is handled the composer is already shut and isChatComposerVisible() no
        // longer gates it -- measured live: a tap on the dimmed world closed the composer and fired
        // a shot. Holding attacks off for a beat is what an interaction icon does with the same
        // problem (see Player.suppressAttack).
        this.logic.playerController?.suppressAttack()
        this.chatComposer.close()
        Keyboard.suppressTyping(false)
        this.chatOpen = false
        // Back to the ambient log, bottom-left, which fades itself out again.
        this.chatHistory.x = CHAT_LEFT - stageViewport().inset
        this.chatHistory.setAnchor("bottom", CHAT_LOG_BOTTOM + stageViewport().insetY)
        this.scheduleChatHistoryFade(5000)
    }

    public isChatComposerVisible = (): boolean => !!this.chatComposer?.isOpen()

    // Browser-console triggers, in the same shape as window.playersFlare / window.rosterWidget:
    //
    //     openChat()     // put the composer up at a desktop, where Enter opens the inline field
    //     chatFlare()    // flare the SAY chip as if a line had landed while it was closed
    //     chatComposer   // the panel itself: isOpen(), text()
    //
    // The composer is the TOUCH path, so nothing a developer at a keyboard can do opens it -- there
    // would otherwise be no way to look at it without a phone in hand.
    private exposeChatDevTriggers = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).chatComposer = this.chatComposer
        ;(window as any).openChat = (): string => {
            this.openChatComposer()
            return "[openChat] composer up"
        }
        ;(window as any).chatFlare = (): string => {
            this.chatChip?.flash()
            const why = this.chatChipWanted() ? "not in a camp" : "a keyboard has Enter"
            return `[chatFlare] chip ${this.chatChip?.visible ? "up" : `hidden (${why})`}`
        }
    }

    // Redraws the input from the current state. Everything that edits the line ends here.
    private renderChatInput = () => {
        this.typing.update(this.chatInput)
    }

    // Takes the input off the screen, and the log with it, dropping the half-written line. Every
    // client-side slash command below starts this way before doing its own thing.
    private dismissChatInput = () => {
        this.chatOpen = false
        this.chatInput = EMPTY_INPUT
        this.typing.removeText()
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        this.chatHistory.alpha = 1.0
        this.chatHistory.hide()
    }

    appendChatHistory = (message: string, speaker?: string) => {
        if (!message) {
            return
        }
        this.chatHistory.show()

        // Callers hand over the speaker and the line apart; a single formatted "Name: line" is
        // still understood, because that is what this took for its whole life before.
        const named = speaker === undefined ? entryFromLine(message) : { speaker, text: message }
        this.chatLog.push({ ...named, self: !!named.speaker && named.speaker === this.logic?.playerEntity?.name })
        this.chatLog = this.chatLog.slice(-LOG_MAX_MESSAGES)
        this.chatHistory.updateEntries(this.chatLog)


        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        clearTimeout(this.chatHistoryTextTimer)
        this.chatHistory.alpha = 1.0

        // A line that lands while the player is typing does NOT start the clock: fading the log
        // out from under someone who is in the middle of answering it is how a reply gets sent to
        // a conversation the sender can no longer see. stopChat starts it again on the way out.
        if (this.chatOpen) {
            return
        }
        // Nobody is reading, so this line is about to fade. On touch the chip's flare is the only
        // notice the player gets that it was ever there: there is no keyboard to open chat with,
        // and the log itself is deliberately not made tappable (it is drawn over the move stick, and
        // an interactive panel there would swallow the thumb that steers). The chip rate-limits
        // itself to one flare at a time, so a busy conversation does not leave the corner blinking.
        if (isMobile()) {
            this.chatChip?.flash()
        }
        this.scheduleChatHistoryFade(5000)
    }

    private scheduleChatHistoryFade = (delay: number) => {
        clearTimeout(this.chatHistoryTextTimer)
        this.chatHistoryTextTimer = setTimeout(() => {
            this.chatHistoryFader = new Fader(this.chatHistory, {
                direction: "out",
                minAlpha: 0.0,
                fadeInterval: 16, // ~60fps
                fadeAlphaDecrement: 0.02,
                releaseCallback: () => {
                    this.chatHistory.hide()
                    this.chatHistory.alpha = 1.0 // Reset alpha for next time
                },
            })
            this.chatHistoryFader.update()
        }, delay)
    }

    // One keystroke of chat input. `mods` is the modifier state at the moment the key went down,
    // which is what tells a plain Backspace from a word-wise one -- and what keeps the letter of
    // an OS shortcut (cmd+R) out of the line.
    captureKeystroke = (c: string, mods?: KeyModifiers) => {
        const scope: EditScope = mods?.meta ? "line" : mods?.alt || mods?.ctrl ? "word" : "char"

        if (c === "Escape") {
            // player.ts closes the input right after this; dropping the line here is what makes
            // Escape a cancel rather than a send.
            this.chatInput = EMPTY_INPUT
            this.chatWalk = { ...this.chatWalk, index: -1, draft: "" }
            return
        }

        if (c === "Backspace") {
            this.chatInput = deleteBackward(this.chatInput, scope)
        } else if (c === "Delete") {
            this.chatInput = deleteForward(this.chatInput, scope)
        } else if (c === "ArrowLeft" || c === "ArrowRight") {
            const back = c === "ArrowLeft"
            // cmd+arrow is "to the end of the line" on a Mac, not "a word at a time".
            this.chatInput = mods?.meta
                ? moveCaret(this.chatInput, back ? "home" : "end")
                : moveCaret(this.chatInput, back ? "left" : "right", scope === "word")
        } else if (c === "Home" || c === "End") {
            this.chatInput = moveCaret(this.chatInput, c === "Home" ? "home" : "end")
        } else if (c === "ArrowUp" || c === "ArrowDown") {
            const walked =
                c === "ArrowUp"
                    ? historyBack(this.chatWalk, this.chatInput)
                    : historyForward(this.chatWalk, this.chatInput)
            this.chatWalk = walked.walk
            this.chatInput = walked.input
        } else if (mods?.ctrl) {
            // The line-editing keys a terminal (and a browser text field) answers to.
            switch (c.toLowerCase()) {
                case "u":
                    this.chatInput = deleteBackward(this.chatInput, "line")
                    break
                case "k":
                    this.chatInput = deleteForward(this.chatInput, "line")
                    break
                case "w":
                    this.chatInput = deleteBackward(this.chatInput, "word")
                    break
                case "a":
                    this.chatInput = moveCaret(this.chatInput, "home")
                    break
                case "e":
                    this.chatInput = moveCaret(this.chatInput, "end")
                    break
                default:
                    return
            }
        } else if (mods?.meta) {
            // cmd+R, cmd+L, cmd+1 ... the player is talking to the browser, not to the camp.
            return
        } else if (c.length === 1) {
            this.chatInput = insertText(this.chatInput, c)
        } else {
            return
        }

        this.renderChatInput()
    }

    // Pasted text (see Keyboard.addPasteListener): dropped in at the caret, flattened to one line
    // and trimmed to whatever is left under the length cap.
    insertChatText = (text: string) => {
        this.chatInput = insertText(this.chatInput, text)
        this.renderChatInput()
    }

    stopChat = (): string => {
        this.chatOpen = false
        // Stop any active fader and timeout
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        clearTimeout(this.chatHistoryTextTimer)
        const messageToSend = this.chatInput.value.trim()

        if (messageToSend === "/register") {
            this.dismissChatInput()

            if (this.logic.playerController.isGuest()) {
                this.collectPlayerAccount(
                    this.logic.playerEntity.name,
                    (username: string, password: string, email: string) => {
                        if (username && password) {
                            this.logic.playerController.client.register(username, password, email)
                        }
                    },
                )
            } else {
                this.addScreenMessage({
                    target: "important",
                    text: "You're already registered",
                })
            }

            return ""
        }

        if (messageToSend === "/message") {
            this.dismissChatInput()

            if (this.incomingMessageMeta.text) {
                this.doDynamicDialog(this.incomingMessageMeta.text)
                this.haltMessageIndicator()
            }
            return ""
        }

        if (messageToSend === "/help") {
            this.logic.playerController.client.logActivity("invoke help")

            this.dismissChatInput()
            this.help(() => {})

            return ""
        }

        if (messageToSend === "/upgrade") {
            this.logic.playerController.client.invokeStatUpgradeUI()
            this.dismissChatInput()

            return ""
        }

        // The frame-rate readout's second door, and the only one a phone has: `f` needs a keyboard,
        // and both thumbs on a touch screen already belong to the movement and attack sticks. Typed
        // rather than given a HUD control because it is a tool -- a fifth chip in the top-right row
        // would cost a permanent slot at the width where slots are scarcest, to carry something
        // almost nobody presses.
        //
        // Deliberately not dev-gated. A player who can say "22 fps, worst frame 140ms" has given a
        // far better bug report than one who can only say it feels bad, and the panel shows nothing
        // that isn't theirs already.
        if (messageToSend === "/fps roster") {
            // Local rendering A/B check: keep the same NPCs, simulation and frame meter,
            // but omit the roster's draw pass. No server command or gameplay state change.
            this.emberRoster.renderable = !this.emberRoster.renderable
            this.dismissChatInput()
            return ""
        }

        if (messageToSend === "/fps") {
            this.logic.playerController.client.logActivity("invoke fps")

            this.dismissChatInput()
            this.toggleFpsMeter()

            return ""
        }

        if (messageToSend === "/about") {
            this.logic.playerController.client.logActivity("invoke about")

            this.dismissChatInput()
            this.about()

            return ""
        }

        if (messageToSend.startsWith("/timer")) {
            this.logic.playerController.client.logActivity("invoke timer")

            this.dismissChatInput()

            // Parse timer duration from command (e.g., "/timer 30" for 30 seconds)
            const parts = messageToSend.split(" ")
            const duration = parts.length > 1 ? parseInt(parts[1]) : 10 // default 10 seconds

            if (isNaN(duration) || duration <= 0) {
                this.addScreenMessage({
                    target: "important",
                    text: "Invalid timer duration. Use: /timer [seconds]",
                })
            } else {
                this.countdownTimer.visible = true
                this.countdownTimer.start(duration)
            }

            return ""
        }

        if (messageToSend === "/timerstop") {
            this.logic.playerController.client.logActivity("stop timer")

            this.dismissChatInput()

            if (this.countdownTimer.visible) {
                this.countdownTimer.stop()
                this.countdownTimer.visible = false
                this.addScreenMessage({
                    target: "important",
                    text: "Timer stopped",
                })
            } else {
                this.addScreenMessage({
                    target: "important",
                    text: "No timer is currently running",
                })
            }

            return ""
        }

        if (messageToSend === "/timerreset") {
            this.logic.playerController.client.logActivity("reset timer")

            this.dismissChatInput()

            this.countdownTimer.reset()
            this.countdownTimer.visible = false
            this.addScreenMessage({
                target: "important",
                text: "Timer reset",
            })

            return ""
        }

        if (messageToSend === "/wheel") {
            this.logic.playerController.client.logActivity("invoke command wheel")

            this.dismissChatInput()

            this.commandWheel.fadeIn()
            this.addScreenMessage({
                text: "Command wheel opened. Click commands to select, use button to switch NPCs.",
            })

            return ""
        }

        if (messageToSend === "/wheelclose") {
            this.logic.playerController.client.logActivity("close command wheel")

            this.dismissChatInput()

            this.commandWheel.fadeOut()
            this.addScreenMessage({
                text: "Command wheel closed",
            })

            return ""
        }

        if (messageToSend.startsWith("/hero")) {
            this.logic.playerController.client.logActivity("test hero message")

            this.dismissChatInput()

            // Parse text and duration from command
            // Format: /hero [duration] [text] or /hero [text]
            const parts = messageToSend.split(" ")
            let text = "HERO MESSAGE!" // default text
            let duration = 2000 // default 2 seconds

            if (parts.length > 1) {
                // Check if second part is a number (duration)
                const secondPart = parseInt(parts[1])
                if (!isNaN(secondPart)) {
                    // Format: /hero [duration] [text]
                    duration = secondPart
                    text = parts.slice(2).join(" ") || "HERO MESSAGE!"
                } else {
                    // Format: /hero [text]
                    text = parts.slice(1).join(" ")
                }
            }

            this.addScreenMessage({
                target: "hero",
                text: text,
                duration: duration,
            })

            return ""
        }

        // The log outlives the input it was opened with, so the player can read the answer to
        // what they just sent.
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        this.scheduleChatHistoryFade(10000)

        // Remember what was sent for the up-arrow walk (consecutive duplicates collapse), and
        // reset the walk so the next Up starts from this line rather than mid-history.
        this.chatWalk = rememberSent(this.chatWalk, messageToSend)
        if (messageToSend) {
            this.saveChatHistory()
        }

        this.chatInput = EMPTY_INPUT
        this.typing.removeText()

        return messageToSend
    }

    private saveChatHistory = () => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.CHAT_HISTORY_KEY, JSON.stringify(this.chatWalk.entries))
            }
        } catch (error) {
            console.warn("Failed to save chat history to localStorage:", error)
        }
    }

    private loadChatHistory = () => {
        try {
            if (typeof localStorage !== "undefined") {
                const saved = localStorage.getItem(this.CHAT_HISTORY_KEY)
                if (saved) {
                    const parsed = JSON.parse(saved)
                    if (Array.isArray(parsed)) {
                        this.chatWalk = {
                            entries: parsed.filter(entry => typeof entry === "string"),
                            index: -1,
                            draft: "",
                        }
                    }
                }
            }
        } catch (error) {
            console.warn("Failed to load chat history from localStorage:", error)
            this.chatWalk = EMPTY_WALK
        }
    }

    clearChatHistory = () => {
        this.chatWalk = EMPTY_WALK
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.removeItem(this.CHAT_HISTORY_KEY)
            }
        } catch (error) {
            console.warn("Failed to clear chat history from localStorage:", error)
        }
    }

    addScreenMessage = (message: ScreenMessage) => {
        const { target, text, roomId, action, actionToken, context, suppressOnCloseEmitEvent } = message
        if (suppressOnCloseEmitEvent && this.currentModal) {
            this.currentModal.modal.suppressOnCloseCallback = suppressOnCloseEmitEvent
        }

        if ((target === "chapterChecklist" || target === "hud") && roomId && !this.acceptsSceneRoom(roomId)) {
            // The server sends a camp's checklist and HUD list on the tick the player arrives there,
            // which can beat the client's own scene change by a few milliseconds, and it never sends
            // an unchanged one twice. Dropped, a fresh camp had no checklist at all. Kept instead, for
            // roomUpdated to hand over once the scene is entered.
            this.earlySceneMessages.set(target, message)
            return
        }
        if (!this.acceptsSceneRoom(roomId)) {
            return
        }
        if (target === "hud") {
            this.hudPresence.setHidden(message.hudHidden || [], !!message.bare)
            this.layoutChipRow()
            // Once taken a lesson runs on the player's own input, until the server names none: they
            // have reached the Lord, or walked off from the fire (chapter_hud.ts). Whatever is up fades.
            if (message.controlsLesson) this.controlsLesson.request(message.controlsLesson, isMobile())
            else this.controlsLesson.dismiss()
            return
        }
        if (target === "chapterChecklist") {
            this.chapterChecklist.update(message.chapterChecklist || null)
            this.chapterChecklist.place()
            return
        }
        if (target === "conversation") {
            if (action === "close") this.closeConversation(context?.npcEntityId)
            else if (context) this.openConversation(context as ConversationView)
            return
        }
        if (target === "feedbackComposer") {
            const visitId = typeof context?.visitId === "string" ? context.visitId : undefined
            if (action === "close") this.closeFeedbackComposer(visitId)
            else this.openFeedbackComposer(visitId)
            return
        }

        if (target === "stairPicker") {
            this.hideRolePicker()
            if (action !== "close" && message.stairChoices) {
                this.closeModal()
                this.stairPickerMessage = message
                this.rolePicker.bindStairs(message.entityId)
                this.positionRolePickerOverEntity(message.entityId)
                this.rolePicker.fadeIn()
                this.preventContextMenu()
            }
            return
        }

        if (action === "close" && target !== "featureWidget") {
            this.closeModal()
            return
        }

        if (action === "openDynamicDialog") {
            this.dynamicDialogToken = actionToken
        }

        if (!target) {
            this.screenMessages.push(message)
            // Always consume messages immediately to allow interruption
            this.consumeMessages()
        } else {
            if (target === "leaderboard") {
                this.updateBannerText(text)
            }
            if (target === "important") {
                // Into the queue, not onto the strip. The lane decides what shows and for how long,
                // and `updateTimer` below is what hands its choice to the widget.
                this.importantLane.push({
                    text: message.text || "",
                    tier: (message.context?.tier === "alert" ? "alert" : "notice") as NoticeTier,
                    durationMs: message.duration,
                    source: message,
                })
                this.pumpImportantLane(0)
            }
            if (target === "waveStatus") {
                this.updateWaveIndicator(message)
            }
            if (target === "countdown") {
                const duration = parseInt(text)
                if (!isNaN(duration) && duration > 0) {
                    // The dial and the wave badge share one slot: starting a countdown means the
                    // previous wave is over, so the badge comes down.
                    this.waveIndicator.update(null)
                    this.countdownTimer.visible = true
                    // The wave is optional: the camp sends it, `/timer` doesn't, and without it the
                    // dial simply goes uncaptioned.
                    this.countdownTimer.start(
                        duration,
                        Number(message.context?.wave) || 0,
                        message.context?.expedition === true,
                    )
                    // Remember what this dial is counting to, so the landing knows the player sat
                    // through it. Read off the message rather than off the dial's own visibility:
                    // the dial takes ITSELF off screen at zero, and the server's "it landed" arrives
                    // a round trip later, so by then `visible` is a race.
                    this.waveAwaited = Number(message.context?.wave) || undefined
                } else if (!isNaN(duration)) {
                    // A non-positive value is an explicit "hide the countdown" signal (e.g. the ember
                    // waves were stopped on a delegated respawn). Clear the ring immediately instead of
                    // letting its client-side timer run down.
                    this.countdownTimer.stop()
                    this.countdownTimer.visible = false
                } else {
                    this.addScreenMessage({
                        target: "important",
                        text: "Invalid countdown duration. Must be a positive number.",
                    })
                }
            }
            if (target === "hero") {
                this.heroMessage.show(message)
            }
            if (target === "dialog") {
                if (message.forceTarget) {
                    this.doDynamicDialog(message)
                } else {
                    this.availableMessage(message)
                }
            }
            if (target === "stickyBanner") {
                this.stickyBanner.setup(message.text, message.context?.icon, message.context?.hoverText)
                this.stickyBanner.x = this.controlBar.x - this.stickyBanner.width - TileSize
                this.dayCycle.x = this.stickyBanner.x - TileSize * 1.25
            }
            if (target === "featureWidget") {
                this.featureWidget.setup(message)
            }
        }
    }

    updateLeaderboard = (leaderboards: LeaderboardMeta[] | null) => {
        this.leaderboard.update(leaderboards)
    }

    updateInventory = (owner: Entity, inventory: Inventory) => {
        if (this.inventoryUpdateTs.get(owner.entityId) === inventory.lastUpdateTs) {
            return false
        }

        this.inventoryUpdateTs.set(owner.entityId, inventory.lastUpdateTs)

        const player = this.getPlayer()
        if (player.entityId === owner.entityId) {
            if (this.dynamicDialogToken) {
                this.logic.emitConversationResponse(this.dynamicDialogToken)
            }
            // Worked out BEFORE the tray is drawn, because the tray now carries the count: the
            // arrows used to be read off the ResourceBar beside the player's NAME, in the opposite
            // corner from the bow they belong to.
            const ammoBearingWeapon = locateInventoryItemObjects(inventory, "weapon")
                .sort((b, a) => (a.active ? 2 : 1 - (b.active ? 2 : 1)))
                .find((o: InventoryItem) => {
                    return !!WeaponAmmo[o.itemType]
                })

            let ammo: AmmoReadout | undefined
            if (ammoBearingWeapon) {
                const ammoType: ItemTypeMeta = resolveItemTypeToMeta(WeaponAmmo[ammoBearingWeapon.itemTypeMeta.type])
                if (ammoType) {
                    // Only while the bow is actually IN HAND. A bow sitting in the pack makes the
                    // arrow count noise -- it is not what the next click is going to spend.
                    if (ammoBearingWeapon.active) {
                        const ammoItem = locateInventoryItemObjectByItemType(inventory, ammoType.type)
                        ammo = {
                            icon: `${ammoType.type}-${ammoType.color}.png`,
                            quantity: ammoItem?.quantity || 0,
                        }
                    }
                    this.logic.playerController.updateLongRangedEquippedState(ammoBearingWeapon.active)
                } else {
                    this.logic.playerController.updateLongRangedEquippedState(false)
                }
            } else {
                this.logic.playerController.updateLongRangedEquippedState(false)
            }

            const chargedItem = inventory.items.find(
                item => item.active && item.quantity > 0 && item.flags?.chargedWeaponId,
            )
            const charged =
                player.chargedWeaponDefinition ||
                (chargedItem?.flags.chargedWeaponId === EMBER_CORE.id ? EMBER_CORE : undefined)
            if (chargedItem && charged) {
                const material = resolveItemTypeToMeta(charged.ammunition.items[0])
                const quantity = inventory.items.reduce(
                    (sum, item) =>
                        charged.ammunition.items.includes(item.itemTypeMeta?.type as any)
                            ? sum + Math.max(0, item.quantity || 0)
                            : sum,
                    0,
                )
                ammo = {
                    icon: material.spriteId || `${material.type}-${material.color}.png`,
                    quantity: Math.floor(quantity / charged.ammunition.quantity),
                }
                this.logic.playerController.updateLongRangedEquippedState(true)
            }

            this.slotCallbacks = this.equipped.update(
                inventory,
                (itemId: string) => {
                    this.logic.playerController.selectSlot(itemId)
                },
                ammo,
            )

            this.abilities.update(inventory, (shortcut: string) => {
                this.logic.playerController.activeAbility(shortcut)
            })

            this.abilities.x = this.equipped.x + this.equipped.width

            const shards = locateInventoryItemObjectByItemType(inventory, "shard-metal")
            this.resourceBar.setup([
                {
                    icon: `shard-metal-gray.png`,
                    quantity: shards?.quantity || 0,
                },
            ])
            this.resourceBar.x = this.nameText.x + this.nameText.width + 20
            // Honor per-room suppression (e.g. ember rooms) — the ammo branch above may have
            // forced it visible; keep it hidden while suppressed.
            this.resourceBar.visible = !this.suppressShards
        }

        // this.inventoryGui.update()

        return true
    }

    activateInventorySlot = (slot: number) => {
        const callback = this.slotCallbacks[slot]
        if (callback) {
            callback()
        }
    }

    mouseOverControl = (disable: boolean) => {
        this.logic.enablePlayerMouseAction(!disable)
    }

    private consumeMessages = () => {
        // Clear any existing timeout to allow interruption
        if (this.currentMessageTimeout) {
            clearTimeout(this.currentMessageTimeout)
            this.currentMessageTimeout = null
        }

        // If there's a current message, interrupt it and process the next one
        if (this.currentScreenMessage) {
            if (this.currentScreenMessage.displayCallback) {
                this.currentScreenMessage.displayCallback()
            }
            this.currentScreenMessage = null
        }

        this.currentScreenMessage = this.screenMessages.shift()
        if (!this.currentScreenMessage) {
            return
        }

        const { text, roomId, displayCallback } = this.currentScreenMessage
        let delayMs = 0
        if (this.acceptsSceneRoom(roomId)) {
            this.bannerText.text = text

            const wordCount = text.split(" ").length
            delayMs = Math.max(1000, ((wordCount * 60) / 200) * 1000)
        }

        this.currentMessageTimeout = setTimeout(() => {
            if (displayCallback) {
                displayCallback()
            }
            this.bannerText.text = ""
            this.currentScreenMessage = null
            this.currentMessageTimeout = null
            this.consumeMessages()
        }, delayMs)
    }

    /**
     * Close the curtain. `durationMs` is how long it takes; the default is the 1s this always was.
     *
     * A transition (Protocol.TransitFade) sets it, because a scene change wants the curtain to close
     * at a pace that matches what is happening behind it -- a descent has already spent its fade
     * dissolving the player, and the curtain following over the empty ground is the shorter half.
     */
    fadeOut = (immediate?: boolean, durationMs: number = 1000) => {
        this.blackout.alpha = 0.0
        this.blackout.visible = true
        this.bannerText.text = ""

        // Clear any pending message timeout
        if (this.currentMessageTimeout) {
            clearTimeout(this.currentMessageTimeout)
            this.currentMessageTimeout = null
        }
        this.currentScreenMessage = null
        if (immediate) {
            this.blackout.alpha = 1.0
        } else {
            const step = Math.max(16, durationMs / 10)
            const loop = setInterval(() => {
                const newAlpha = Math.min(1.0, this.blackout.alpha + 0.1)
                this.blackout.alpha = newAlpha

                if (newAlpha === 1.0) {
                    clearInterval(loop)
                }
            }, step)
        }
    }

    /**
     * Close the curtain and leave it closed, with none of fadeOut's housekeeping (it also clears the
     * banner and the message queue, which a room load has no business doing).
     *
     * Used to hold a freshly loaded room out of sight while it finishes arriving: fadeIn starts from
     * full black anyway, so this only brings that black forward to the moment the room lands rather
     * than the moment the screen opens on it. See RoomReveal.
     */
    holdBlackout = () => {
        this.blackout.alpha = 1.0
        this.blackout.visible = true
    }

    isBlackoutVisible = (): boolean => !!this.blackout.visible && this.blackout.alpha > 0

    fadeIn = (immediate = false) => {
        if (immediate) {
            this.blackout.alpha = 0
            this.blackout.visible = false
            return
        }
        this.blackout.alpha = 1.0
        this.blackout.visible = true
        const loop = setInterval(() => {
            const newAlpha = Math.max(0.0, this.blackout.alpha - 0.1)
            this.blackout.alpha = newAlpha

            if (newAlpha === 0.0) {
                clearInterval(loop)
                this.blackout.alpha = 0.0
                this.blackout.visible = false
            }
        }, 100)
    }

    roomUpdated = (
        roomId: EntityId,
        roomName?: string,
        suppressMinimap?: boolean,
        suppressDayCycle?: boolean,
        suppressControlBar?: boolean,
        suppressLevelUp?: boolean,
        suppressMana?: boolean,
        suppressFood?: boolean,
        suppressShards?: boolean,
        suppressPlayerName?: boolean,
        suppressTutorial?: boolean,
        suppressGuestPrompt?: boolean,
        suppressTeamCommands?: boolean,
    ) => {
        // Each room's campaigns keep their own idea of whether this screen is quiet, and the room being
        // entered has never been told: say it again there.
        if (roomId !== this.currentRoomId) this.quietWatch.reset()
        const sceneId = this.logic.scene?.id ?? roomId
        const sceneGeneration = this.logic.scene?.generation
        const sceneChanged =
            sceneId !== (this.currentSceneId ?? this.currentRoomId) ||
            (this.currentSceneGeneration !== undefined && this.currentSceneGeneration !== sceneGeneration)
        // Camp HUD state belongs to the scene. Taking stairs keeps its countdown and wave state;
        // entering another scene clears it, even if the previous active room was an upper floor.
        if (sceneChanged && this.countdownTimer) {
            // Land anything still in flight before the list it was flying to goes.
            this.chapterCelebration?.flush()
            this.chapterChecklist?.update(null)
            // What one camp held back is its own; the next says for itself.
            this.hudPresence.setHidden([])
            this.controlsLesson.reset()
            this.countdownTimer.reset()
            this.countdownTimer.visible = false
            // Same for the wave badge: it's per-room wave UI, and the room it belonged to is gone.
            this.waveIndicator?.update(null)
            // ...and the fire gauge, for the same reason. It would otherwise keep showing the
            // strength of a camp the player has walked out of until the next roster packet, and in
            // a room that sends no roster at all (a cave) it would simply never come down.
            this.emberFireGauge?.update(undefined)
            // Forget which wave was last played, and what was being counted down to: the next
            // camp's wave 1 is a landing this player has not watched yet.
            this.wavePlayed = undefined
            this.waveAwaited = undefined
        }

        // Clues, invitations and chat stay with the camp through floor transfers.
        if (sceneChanged) {
            this.emberAnxietyMeter?.update(null)
            this.emberInviteModal?.close()
            this.recruitHintRoster = []
            this.recruitMenuHint.reset()
            this.tutorial.clearEmberClues()
            // Nothing typed at one camp should still be on screen over the next one -- and a DOM
            // field left up keeps a phone's keyboard over the room that is loading behind it.
            this.dismissChatComposer()
        }

        const minimapRoomChanged = sceneChanged
        this.currentRoomId = roomId
        this.currentSceneId = sceneId
        this.currentSceneGeneration = sceneGeneration
        // After the scene change above has cleared the last camp's state, never before it.
        this.earlySceneMessages.forEach((message, target) => {
            if (!this.acceptsSceneRoom(message.roomId)) return
            this.earlySceneMessages.delete(target)
            this.addScreenMessage(message)
        })
        this.currenRoomName = roomName
        this.screenMessages = this.screenMessages.filter(n => this.acceptsSceneRoom(n.roomId))
        this.controlBar.visible = !suppressControlBar
        // The pack has to stay reachable. Suppressing the control bar takes the inventory button
        // with it, and in a camp there is no keyboard to fall back on, so the chip in the top-right
        // row stands in for exactly as long as the bar is gone.
        this.emberInventoryChip.visible = !!suppressControlBar
        // Chat's button follows the pack's: whenever the bar that carries it is gone, the chip in
        // the top-right row stands in for it -- but only on a machine that needs standing in for.
        this.chatChip.visible = !!suppressControlBar && this.chatChipWanted()
        this.layoutChipRow()
        // Latch per-room mana / food (hunger) meter suppression so it survives later update() calls,
        // which otherwise force the meters visible on any mana/hunger change. Hide immediately on
        // entry (and the meters are restored on exit when the flags come back false).
        this.suppressMana = !!suppressMana
        this.suppressFood = !!suppressFood
        this.mana.visible = !this.suppressMana
        if (this.suppressFood) {
            this.hunger.visible = false
        }
        // Latch per-room shard/resource counter + player-name suppression. updateInventory() only
        // fires when the inventory changes, so apply the resourceBar state here on entry too; the
        // name label is applied directly here (update() never toggles its visibility).
        this.suppressShards = !!suppressShards
        this.suppressPlayerName = !!suppressPlayerName
        this.resourceBar.visible = !this.suppressShards
        this.nameText.visible = !this.suppressPlayerName
        // Latch per-room day/night suppression so it survives later updateDayPct() calls, which
        // otherwise force the widget visible on every day-pct tick. Hide immediately on entry too.
        this.suppressDayCycle = !!suppressDayCycle
        if (this.suppressDayCycle) {
            this.dayCycle.visible = false
        }
        // Latch per-room "Level up stats!" suppression and rebuild the help-messaging stack so a
        // button from the previous room is removed on entry (and restored on exit).
        this.suppressLevelUp = !!suppressLevelUp
        // Latch per-room tutorial suppression so showTutorial() is a no-op while in the room (and
        // popups return once the flag comes back false on exit). Ember rooms set this.
        this.tutorial.suppressed = !!suppressTutorial
        // Same for a guest's "/register to save progress" line, which helpMessaging() rebuilds from
        // scratch just below -- so setting it here is what takes the line off on entry and puts it
        // back on exit.
        this.squad.visible = !suppressTeamCommands
        this.suppressGuestPrompt = !!suppressGuestPrompt
        this.helpMessaging()
        this.minimapSurface.roomUpdated(this.userSuppressMinimap || suppressMinimap)
        this.featureWidget.remove()
        // Clear scene feeds on leaving the camp, retaining them during floor transfers.
        // clear(), not update([]): the cards are not dying, the player is leaving the room, and a
        // card cannot break apart over a room that is already gone.
        if (sceneChanged) this.emberRoster.clearRoster()
        this.positionEmberRoster()
        // Queued notices are about the room being left. Drop them rather than announcing the old
        // camp's wave over the new one.
        if (sceneChanged) {
            this.importantLane.clear()
            this.importantMessage.dismiss()
        }
        // Metadata refreshes for the same room must not erase an unchanged discovery feed.
        if (minimapRoomChanged) {
            this.emberMinimap.selectRoom(sceneId)
            this.updateEmberMinimap(null)
        }
        // The player list and follow-cam share the same scene lifetime as the roster.
        if (sceneChanged) this.updateEmberPlayers([])
        // Leaving the scene also clears the server focus subscription.
        if (sceneChanged && this.followCam?.isOpen()) {
            this.closeFollowCam()
        }
    }

    hideRoomName = (hidden: boolean) => {
        this.roomNameHidden = hidden
    }

    animateAbility = (abilityType: AbilityType, duration?: number) => {
        const pulsar = this.abilities.slotPulsar.get(abilityType)
        if (!pulsar) {
            return
        }

        pulsar.pulse(duration || 750)
    }

    animateLocalEntityCollection = (entity: Entity, target: string, position?: number) => {
        // for now -- all local entity animations relate to ability slot
        const abilityType = target.split(":")[1]
        const location = this.abilities.slotCoordinates.get(abilityType)
        if (!location) {
            return
        }

        const destX = this.abilities.x + location.x + 3
        const destY = this.abilities.y + location.y - 10

        const entityRenderingManager: EntityRenderingManager =
            this.logic.clientRenderer.entity() as EntityRenderingManager
        const clientRenderable: ClientRenderable = entityRenderingManager.renderEntity(entity)

        this.stage.addChild(clientRenderable)
        clientRenderable.x = ScreenDimensions.w * 0.58
        clientRenderable.y = 285

        const increment = 6
        const animationInterval = setInterval(() => {
            const currentDistance = getDistance(clientRenderable.x, clientRenderable.y, destX, destY)
            if (currentDistance < increment) {
                clearInterval(animationInterval)
                // clientRenderable.remove()
                const fadeInterval = setInterval(() => {
                    if (clientRenderable.alpha <= 0.0) {
                        clearInterval(fadeInterval)
                        clientRenderable.remove()
                        return
                    }

                    clientRenderable.alpha -= 0.01
                }, 1)
                return
            }
            if (clientRenderable.alpha > 0.6) {
                clientRenderable.alpha -= 0.01
            }

            clientRenderable.x += clientRenderable.x > destX ? -increment : increment
            clientRenderable.y += clientRenderable.y > destY ? -increment : increment
        }, 1)
    }

    private updateMobileBuildToolbar = () => {
        if (!isMobile()) return
        const player = this.logic.playerController
        if (!player?.mobileBuildingState || !this.equipped) return
        if (!this.mobileBuildToolbar) {
            this.mobileBuildToolbar = new MobileBuildToolbar({
                toggle: on => {
                    this.touchSticks?.releaseAll()
                    this.logic.playerController.setMobileBuildMode(on)
                },
                select: tool => this.logic.playerController.selectMobileBuildTool(tool),
                undo: () => this.logic.playerController.undoMobileBuild(),
            })
            this.container.addChild(this.mobileBuildToolbar)
        }
        this.mobileBuildToolbar.update(player.mobileBuildingState(), this.equipped.position)
        if (
            this.isItemScreenOpen() ||
            this.isCommandWheelVisible() ||
            this.isChatComposerVisible() ||
            this.isNamePromptVisible() ||
            this.characterBuilder?.isOpen() ||
            this.characterCustomizer?.isOpen() ||
            this.blackout?.visible ||
            this.currenRoomName === LIMBO_ROOM_NAME
        )
            this.mobileBuildToolbar.visible = false
    }

    private mobileBuildCoordinate = (at: Point): Coordinate | null => {
        const world = this.worldPointAt(at)
        return world ? this.logic.playerController.buildingTargetAt(world) : null
    }

    // The fine world point under a screen (pixi global) point, read off the world's own transforms.
    worldPointAt = (at: Point): Coordinate | null => {
        const overlay = this.logic.clientRenderer.map().overlay()
        if (!overlay) return null
        const local = overlay.toLocal(at)
        return overlay.worldLocation(local.x, local.y)
    }

    /**
     * The two touch sticks.
     *
     * Neither is pinned any more: a stick is summoned under the thumb that asks for it and fades
     * when that thumb lifts, so a phone screen costs nothing to a control nobody is holding. The
     * left half of the screen summons this move stick, the right half the attack stick; FloatingSticks
     * owns the gesture, the halves and the "is this touch free?" question.
     */
    setupJoystick = (container: Container) => {
        if (!isMobile()) {
            return
        }

        const moveStick = new FloatingJoystick({
            outer: Sprite.from("outer"),
            inner: Sprite.from("inner"),

            outerScale: { x: 0.5, y: 0.5 },
            innerScale: { x: 0.8, y: 0.8 },

            // Held intent, not a direct write. Player's 60Hz velocity loop rebuilds
            // speed2 from held input every tick, so anything written onto entity.speed2
            // here is erased before the next frame -- and since onChange only fires as the
            // thumb travels, a thumb held at full deflection never writes again. That pair
            // is what made the stick stutter and refuse to move continuously.
            onChange: data => {
                const entity = this.logic.playerEntity
                const { power } = data
                if (power < JoystickDeadZone) {
                    this.logic.playerController.setJoystickIntent(null)
                    return
                }

                const vector = JoystickVectors[data.direction]
                if (!vector) {
                    return
                }

                // Stick pushed right out to the rim means sprint. See sprintScaledIntent
                // for why scaling the vector is the whole mechanic, and why it must not
                // scale the shared JoystickVectors entry in place.
                const intent = sprintScaledIntent(vector, power)
                this.logic.playerController.setJoystickIntent(intent)

                // Facing follows the move stick unless the aim stick has taken it over.
                // Sprinting scales the vector but not its direction, so the same intent
                // serves for both.
                if (this.leftJoystickAngleAllowed) {
                    entity.movement.angle = getAngle(
                        { x: entity.location.x + intent.x, y: entity.location.y + intent.y },
                        { x: entity.location.x, y: entity.location.y },
                    )
                }
            },

            onStart: () => {
                this.logic.playerController.enabledMouseAction(false)
            },

            onEnd: () => {
                this.logic.playerController.setJoystickIntent(null)
                this.logic.playerController.enabledMouseAction(true)
            },
        })
        container.addChild(moveStick)

        const knob = Sprite.from("inner")
        knob.tint = 0xff0000
        let angleBeforeAim: number
        // Until the thumb steers, a press on the attack side aims at the spot it landed on (onAimDown),
        // the way a click aims at the cursor.
        let aimSteered = false
        const aimStick = new FloatingJoystick({
            outer: Sprite.from("outer"),
            inner: knob,

            outerScale: { x: 0.5, y: 0.5 },
            innerScale: { x: 0.8, y: 0.8 },

            onChange: data => {
                const entity = this.logic.playerEntity
                const { power } = data
                if (power < JoystickDeadZone) {
                    return
                }
                const aim = JoystickVectors[data.direction]
                if (!aim) {
                    return
                }
                if (!aimSteered) {
                    aimSteered = true
                    this.logic.playerController.aimAtTouch(undefined)
                }

                entity.movement.angle = getAngle(
                    { x: entity.location.x + aim.x, y: entity.location.y + aim.y },
                    { x: entity.location.x, y: entity.location.y },
                )
            },

            onStart: () => {
                this.logic.playerController.enabledMouseAction(false)
                this.leftJoystickAngleAllowed = false
                this.logic.playerController.setAttackHeld(true)
            },

            onEnd: () => {
                this.logic.playerController.enabledMouseAction(true)
                this.logic.playerController.setAttackHeld(false)
                this.leftJoystickAngleAllowed = true
                // A tap leaves the player facing what it struck, as a click leaves them facing the
                // cursor: its swing lets go after a quick tap has lifted, and must still find the spot.
                if (aimSteered) this.getPlayer().movement.angle = angleBeforeAim
            },
        })
        container.addChild(aimStick)

        this.touchSticks = new FloatingSticks({
            app: getRendererApp(),
            layer: container,
            move: moveStick,
            aim: aimStick,
            // Read per touch rather than cached: the HUD's width comes off the stage, which the
            // letterbox can still be resizing when the first player entity arrives and this runs.
            midline: () => {
                const { left, right } = this.hudBounds()
                return (left + right) / 2
            },
            // The attack already started on press, so release never adds another swing.
            onAimTap: () => {},
            // Before the stick takes hold and starts the attack, so the first swing already goes there.
            onAimDown: at => {
                angleBeforeAim = this.getPlayer().movement.angle
                aimSteered = false
                this.logic.playerController.aimAtTouch(this.worldPointAt(at) ?? undefined)
            },
            pressFallsThrough: hit => !!this.logic.clientRenderer.entity().pressFallsThrough?.(hit),
            buildMode: () => this.logic.playerController.mobileBuildingState().active,
            movementZone: at => {
                const { left, bottom } = this.hudBounds()
                return at.x >= left && at.x < left + 140 && at.y > bottom - 150 / SCALER
            },
            onWorldDown: event => {
                if (!Mouse.isWorldPoint(event.data.global)) return false
                const point = this.mobileBuildCoordinate(event.data.global)
                return (
                    !!point &&
                    this.logic.playerController.beginMobileBuildTouch(
                        point,
                        this.worldPointAt(event.data.global) ?? undefined,
                    )
                )
            },
            onWorldMove: event => {
                const point = this.mobileBuildCoordinate(event.data.global)
                if (point)
                    this.logic.playerController.moveMobileBuildTouch(
                        point,
                        this.worldPointAt(event.data.global) ?? undefined,
                    )
            },
            onWorldUp: cancelled => {
                if (!cancelled) this.logic.playerController.endTilePress()
                this.logic.playerController.endMobileBuildTouch()
            },
        })
        this.touchSticks.bind()
    }

    socialMedia = (parent?: Container): Graphics => {
        const container = new Graphics()
        container.y = 480
        if (parent) {
            parent.addChild(container)
        }

        const socialMedia = new Graphics()

        const reddit = new Sprite(Loader.shared.resources["reddit"].texture)
        reddit.scale.set(0.05)
        reddit.interactive = true
        reddit.on("click", () => {
            window.open("https://www.reddit.com/r/projectsamsara/")
            this.logic.playerController.client.logActivity(
                `>> ${this.logic.playerEntity?.name || "guest"} visited discord <<`,
            )
        })
        reddit.on("tap", () => {
            window.open("https://www.reddit.com/r/projectsamsara/")
            this.logic.playerController.client.logActivity(
                `>> ${this.logic.playerEntity?.name || "guest"} visited subreddit <<`,
            )
        })

        // discord.png pads its glyph with transparent rows (28-191 of 240 are drawn). Trim them so the
        // row's bounds are what the eye sees, else whoever centres the row centres the padding.
        const discordSheet = Loader.shared.resources["discord"].texture
        const discord = new Sprite(new Texture(discordSheet.baseTexture, new Rectangle(0, 28, discordSheet.width, 163)))
        discord.scale.set(0.13)
        discord.x = reddit.x + reddit.width + 12
        discord.y = Math.round((reddit.height - discord.height) / 2)
        discord.interactive = true
        discord.on("click", () => window.open("https://discord.gg/Mbt6UfqW6e"))
        discord.on("tap", () => window.open("https://discord.gg/Mbt6UfqW6e"))

        socialMedia.addChild(reddit)
        socialMedia.addChild(discord)

        container.addChild(socialMedia)

        return container
    }

    collectPlayerAccount = (name: string, doneCallback: Function) => {
        const release = () => {
            this.logic.playerController.enabledKeyboardAction(true)
            this.logic.playerController.enabledMouseAction(true)
        }

        const askForName = () => {
            const dialog = new Graphics()
            this.stage.addChild(dialog)

            const padding = 20

            const container = new Graphics()
            container.x = padding
            container.y = padding
            dialog.addChild(container)

            const bannerText = new TextRenderer().variant("BannerFont").render() as Text
            bannerText.y = 10
            bannerText.text = "Register new player:"
            container.addChild(bannerText)

            const placeHolder = name
            const nameField = new TextInput({
                input: {
                    fontSize: "20pt",
                    padding: "10px",
                    width: "530px",
                    color: "#26272E",
                },
                box: {
                    default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
                    focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
                    disabled: { fill: 0xdbdbdb, rounded: 16 },
                },
            })

            nameField.text = placeHolder
            nameField.y = 50
            nameField.placeholder = placeHolder
            nameField.placeholderColor = 0x000000

            container.addChild(nameField)
            nameField.focus()

            let currentName = placeHolder
            let currentPassword = ""
            let currentEmail = ""

            const submit = () => {
                if (!currentName) {
                    this.addScreenMessage({
                        target: "important",
                        text: "Player name is required",
                    })
                    return false
                }
                if (!currentPassword) {
                    this.addScreenMessage({
                        target: "important",
                        text: "Password is required",
                    })
                    return false
                }

                release()
                doneCallback(currentName, currentPassword, currentEmail)

                return true
            }

            nameField.on("input", text => {
                currentName = text
            })
            nameField.on("keyup", keycode => {
                if (keycode === 27) {
                    release()
                    container.removeChildren()
                    dialog.parent.removeChild(dialog)
                    return
                }

                if (keycode === 13) {
                    passwordField.focus()
                }
            })

            const passwordField = new TextInput({
                input: {
                    fontSize: "20pt",
                    padding: "10px",
                    width: "530px",
                    color: "#26272E",
                },
                box: {
                    default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
                    focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
                    disabled: { fill: 0xdbdbdb, rounded: 16 },
                },
            })

            passwordField._dom_input.type = "password"
            passwordField.y = 115
            passwordField.placeholder = "Password"
            passwordField.placeholderColor = 0x000000
            passwordField.on("input", text => {
                currentPassword = text
            })

            passwordField.on("keyup", keycode => {
                if (keycode === 27) {
                    release()
                    container.removeChildren()
                    dialog.parent.removeChild(dialog)
                    return
                }
                if (keycode === 13) {
                    if (submit()) {
                        container.removeChildren()
                        dialog.parent.removeChild(dialog)
                    }
                }
            })

            container.addChild(passwordField)

            const emailField = new TextInput({
                input: {
                    fontSize: "20pt",
                    padding: "10px",
                    width: "530px",
                    color: "#26272E",
                },
                box: {
                    default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
                    focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
                    disabled: { fill: 0xdbdbdb, rounded: 16 },
                },
            })

            emailField.y = 180
            emailField.placeholder = "Email (optional)"
            emailField.placeholderColor = 0x000000
            emailField.on("input", text => {
                currentEmail = text
            })
            container.addChild(emailField)

            const buttonContainer = new Graphics()
            buttonContainer.y = 245
            container.addChild(buttonContainer)

            {
                const button = new Graphics()
                button.addChild(new TextRenderer().variant("ImportantFont").render("Cancel") as Text)
                button.x = 125
                button.interactive = true
                button.on("click", () => {
                    release()
                    doneCallback()
                    container.removeChildren()
                    dialog.parent.removeChild(dialog)
                })
                buttonContainer.addChild(button)
            }

            {
                const button = new Graphics()
                const text = new TextRenderer().variant("ImportantFont").render("Register") as Text
                text.tint = 0x00ff00
                button.addChild(text)
                button.interactive = true
                button.on("click", () => {
                    if (submit()) {
                        container.removeChildren()
                        dialog.parent.removeChild(dialog)
                        doneCallback()
                    }
                })
                buttonContainer.addChild(button)
            }

            dialog.x = 15
            dialog.y = 50
            dialog.lineStyle(1, 0xffffff, 0.5)
            dialog.beginFill(0x000000, 0.9)
            dialog.drawRoundedRect(0, 0, dialog.width + padding * 2, dialog.height + padding * 2.5, 8)
            dialog.endFill()
        }

        this.logic.playerController.enabledKeyboardAction(false)
        this.logic.playerController.enabledMouseAction(false)

        askForName()
    }

    help = (doneCallback: Function) => {
        const help = new Help(this)
        help.toggle()
    }

    screenDimensions = (): Dimensions => {
        return {
            w: this.stage.width,
            h: this.stage.height,
        }
    }

    createModal = (props: ModalProps) => {
        const modal = new Modal(this.logic, this.stage, props)
        modal.update()

        return modal
    }

    about = () => {
        const about = new About(this)
        about.toggle()
    }

    doDynamicDialog = (message: ScreenMessage) => {
        if (this.currentModal) {
            // One modal shows every screen, so a new one can replace a screen onto another entity
            // without that screen ever being closed: the pack opened over a chest, a second chest over
            // the first. Send the replaced screen's close, as dismissing it would have -- it is what
            // lowers a chest's lid. Not for a re-render of the same screen (a transfer, a pickup).
            const replaced = this.currentModal.message
            if (replaced?.subjectEntityId && replaced.subjectEntityId !== message?.subjectEntityId &&
                replaced.onCloseEmitEvent) {
                this.logic.playerController.client.emitPlayerEvent(replaced.onCloseEmitEvent)
            }
            this.currentModal.updateContent(message)
            if (this.currentModal instanceof ModernInventoryDialog && this.currentModal.isInventoryOrCrafting()) {
                this.tutorial.itemScreenOpened()
            }
            this.updateItemsChip()
            return
        }

        if (!message) {
            return
        }

        // Always render via ModernInventoryDialog. It extends DynamicDialog and self-selects its
        // layout from the payload: the player's own inventory, crafting, and trade/chest screens get
        // the modern client-native panels, while any other server-driven dialog (quests, traders,
        // NPC management) falls through to the generic DynamicDialog renderer and looks exactly as
        // before. It's a drop-in for currentModal and every downstream call (updateContent/close/
        // pulseGridItem).
        let itemScreen = false
        const onClose = () => {
            if (itemScreen) this.tutorial.itemScreenClosed()
            // The screen showing NOW, which may not be the one this modal opened with: closing a chest
            // that replaced the pack has to close the chest.
            const closing = dialog.message?.onCloseEmitEvent
            if (closing) {
                this.logic.playerController.client.emitPlayerEvent(closing)
            }
            this.logic.playerController.client.onDialogClose()
            this.currentModal = undefined
            this.dynamicDialogToken = undefined
            this.updateItemsChip()
        }
        const dialog = new ModernInventoryDialog(this, this.logic, message, onClose)
        itemScreen = dialog.isInventoryOrCrafting()
        if (itemScreen) this.tutorial.itemScreenOpened()
        dialog.toggle((clicked: boolean) => {
            if (clicked) {
                // this.dynamicDialogToken = null
                this.closeModal()
            }
        })
        this.currentModal = dialog
        this.updateItemsChip()
    }

    availableMessage = (message: ScreenMessage) => {
        this.incomingMessageMeta.unread = true
        this.incomingMessageMeta.expires = Date.now() + 60 * 1000
        this.incomingMessageMeta.text = { ...message }
        this.helpMessaging()
    }

    haltMessageIndicator = () => {
        clearInterval(this.incomingMessageMeta.flasherInterval)
        this.incomingMessageMeta.unread = false
        this.incomingMessageMeta.expires = null
        this.incomingMessageMeta.text = null
        this.helpMessaging()
    }

    showUpgradeIndicator = () => {}

    closeModal = () => {
        if (this.currentModal) {
            this.currentModal.close()
        }
        this.dynamicDialogToken = null
        this.currentModal = null
        this.updateItemsChip()
    }

    private updateItemsChip = () => {
        const modal = this.currentModal
        this.emberInventoryChip?.setActive(modal instanceof ModernInventoryDialog && modal.isInventoryOrCrafting())
        const hideBottomWidgets =
            isMobile() &&
            modal instanceof ModernInventoryDialog &&
            (modal.isInventoryOrCrafting() || isTradeMessage(modal.message))
        if (this.equipped) {
            this.equipped.visible = !hideBottomWidgets
            if (this.equipped.visible && this.pendingReadyBarPulseItemId) {
                const itemId = this.pendingReadyBarPulseItemId
                this.pendingReadyBarPulseItemId = undefined
                this.equipped.pulseItem(itemId)
            }
        }
        if (this.emberRoster) this.emberRoster.visible = !hideBottomWidgets
    }

    toggleInventory = () => {
        if (!this.currentModal) {
            // Nothing but the world on screen: the pack stays shut.
            if (this.hudPresence.isBare()) return
            this.logic.invokeInventory(null)
        } else {
            this.closeModal()
        }
    }

    // `c` toggles the crafting screen the way `i` toggles the inventory. Asking the server to open
    // crafting while a screen is already up would only re-dress the modal that's there, so a second
    // press has to close it locally instead.
    toggleCrafting = () => {
        if (!this.dynamicDialogToken) {
            if (this.hudPresence.isBare()) return
            this.logic.invokeCrafting()
        } else {
            this.closeModal()
        }
    }

    // `f`. A developer tool, so the choice is remembered per browser -- somebody profiling a hitch
    // reloads the client over and over and should not have to re-arm the readout each time. The
    // store can be absent or throw (private windows, blocked site data), which only means the
    // readout starts away.
    toggleFpsMeter = (): boolean => {
        const open = this.fpsMeter.toggle()
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(FPS_METER_KEY, open ? "true" : "false")
            }
        } catch (error) {
            console.warn("Failed to save the fps readout's state to localStorage:", error)
        }
        return open
    }

    toggleCharacterCustomizer = () => {
        this.characterCustomizer.toggle()
        this.updateGameplayControls()
    }

    buildCharacter = (entityId: EntityId) => {
        const player = this.getPlayer()
        if (player?.entityId !== entityId) {
            // for now -- you can only build your own character
            return
        }

        if (!this.characterBuilder.isOpen()) {
            if (this.chipLayer) this.chipLayer.visible = false
            this.characterBuilder.toggle(() => {
                this.logic.playerController.client.finalizeCharacter()

                // Close the curtain on the CLICK, not on the teleport that answers it.
                //
                // A character drawn in LIMBO is whisked out of it into a fresh camp, and that
                // teleport brings the usual room-transition blackout with it
                // (ServerGameLogic.teleportEntity -> sendFadeInOut("out")). But it cannot arrive for
                // half a second: the finalize has to cross the socket, the server has to dispatch
                // BUILD_CHARACTER{finalized}, and EmberRoomService has to build a whole camp room.
                // This modal is opaque and comes down in the frame of this very callback, so without
                // this that half second is spent looking at limbo -- a tile-less room the visibility
                // parser gives darkness 1.0, i.e. the new character alone in the void.
                //
                // Only from limbo, because only from limbo is a room change coming. Finalizing in
                // the overworld (OVERWORLD_ENABLED=false, where the INTRO campaign opens this screen) goes
                // nowhere, and a curtain nothing lifts would be a worse bug than the one above.
                //
                // "out" is an immediate black (fadeOut(true)), so the modal's disappearance reveals
                // the curtain rather than the void, and the camp's own map build fades back in
                // (ClientGameLogic.updateFromWorldState). The "inOut" form is the watchdog: if no
                // camp ever arrives -- a failed build falls through to rescueFromLimbo, which can
                // itself come up empty -- the screen lifts anyway instead of stranding the player
                // behind a black rectangle.
                if (this.currenRoomName === LIMBO_ROOM_NAME) {
                    this.logic.fadeInOut("inOut", 10000)
                }
            })
        }
    }

    getPlayer = (): Entity => this.logic.playerEntity

    pulseInventoryItem = (itemId: EntityId) => {
        this.currentModal?.pulseGridItem(itemId)
        if (!this.equipped) return
        if (this.equipped.visible) {
            this.equipped.pulseItem(itemId)
        } else {
            this.pendingReadyBarPulseItemId = itemId
        }
    }

    mapPointHoverListener = (onMouseOver: boolean, label: string) => {
        // this.container.beginFill(0xFF0000)
        // this.container.drawCircle(705, 454, 20)
        // this.container.endFill()

        const position = this.logic.clientRenderer.mouse().state().position
        if (onMouseOver) {
            const container = new DyanmicTextContainer({
                backdrop: true,
            })
            container.append2(
                new DynamicText(label, {
                    font: "ChatFont",
                }),
            )
            this.mouseHoverElement = container
            this.container.addChild(this.mouseHoverElement)
            this.mouseHoverElement.x =
                5 + this.minimapSurface.x + this.minimapSurface.width / 2 - this.mouseHoverElement.width / 2
            this.mouseHoverElement.y = this.minimapSurface.y - this.mouseHoverElement.height + 2
        } else {
            if (this.mouseHoverElement) {
                this.container.removeChild(this.mouseHoverElement)
            }
        }
    }

    createCursorSprite = (): TargetingReticle => {
        if (!this.mousePointer) {
            this.mousePointer = new TargetingReticle(this.logic.clientRenderer?.mouse())
            this.stage.addChild(this.mousePointer)
        }
        return this.mousePointer
    }

    targetingShot = () => this.mousePointer?.shot()

    // ---- location picker -------------------------------------------------------------------------
    // The reticle under the pointer while the player chooses a spot on the ground (the defender's
    // "Hold Position" order). GuiManager owns only the drawing and the screen->world conversion;
    // arming the pick, ending it, and what the chosen spot MEANS belong to PlayerController, which
    // owns world presses.

    public showLocationPicker = (caption: string, blockedCaption: string) => {
        this.locationPicker.bindMouse(this.logic.clientRenderer?.mouse())
        this.locationPicker.show(caption, blockedCaption)
    }

    public hideLocationPicker = () => {
        this.locationPicker.hide()
    }

    setBuildingPrintGroups = (groups: BuildingPrintGroup[]) => this.buildingPrintCountdown?.setGroups(groups)
    isBuildingPrintControlHovered = () => this.buildingPrintCountdown?.isPointerOver() || false
    setBuildingShapePalette = (state: {
        visible: boolean
        shape: BuildingShape
        locked: boolean
        hollow: boolean
        position: Coordinate
    }) => {
        if (!this.buildingShapePalette && state.visible) {
            this.buildingShapePalette = new BuildingShapePalette(shape => {
                this.logic.playerController.suppressAttack()
                this.logic.playerController.selectBuildingShape(shape)
            })
            this.stage.addChild(this.buildingShapePalette)
        }
        const palette = this.buildingShapePalette
        if (!palette) return
        if (state.visible && !palette.visible) {
            // The selected tool opens under the pointer. Freeze the board during selection.
            const pointer = this.stage.toLocal(new Point(state.position.x, state.position.y))
            const anchor = buildingShapePaletteAnchor(state.shape)
            const edge = this.stage.toLocal(new Point(window.innerWidth, window.innerHeight))
            // The board carries a dropped shadow below it, so keep a little more than its own box
            // clear of the viewport edge.
            palette.position.set(
                Math.max(8, Math.min(pointer.x - anchor.x, edge.x - PALETTE_W - 8)),
                Math.max(8, Math.min(pointer.y - anchor.y, edge.y - PALETTE_STAGE_H - 8)),
            )
        }
        palette.setState(state.visible, state.shape, state.locked, state.hollow)
    }

    public isLocationPickerVisible = (): boolean => this.locationPicker.isVisible()

    public setLocationPickerBlocked = (blocked: boolean) => {
        this.locationPicker.setBlocked(blocked)
    }

    // The fine world coordinate under the pointer, read straight off pixi's own transforms: the
    // world-overlay converts its local point through the ground surface's own transform,
    // including the separate scale and pivot used when a house is projected.
    //
    // Read the transform directly; a cached combat target may have been cleared by keyboard facing.
    public worldPointAtPointer = (): Coordinate | null => {
        const overlay = this.logic.clientRenderer?.map()?.overlay()
        const mouse = this.logic.clientRenderer?.mouse()
        if (!overlay || !mouse) {
            return null
        }
        const local = overlay.toLocal(mouse.state().position as any)
        return overlay.worldLocation(local.x, local.y)
    }

    pulseDamage = (): void => {
        this.redout.alpha = 0.3
        this.redout.visible = true
        new Fader(this.redout, {
            fadeInterval: 25,
            releaseCallback: () => {
                this.redout.visible = false
            },
        }).update()
    }

    showWorldMap = (map: WorldMapMeta) => {
        const worldMap = new WorldMap(this, map, coordinate => {
            if (this.logic.runLevel === "dev") {
                this.logic.playerController.client.worldMapClicked(coordinate)
                worldMap.close()
            }
        })
        worldMap.toggle()
    }

    private isControlKeyPressed = (): boolean => {
        return (
            this.logic.clientRenderer.keyboard().state().get("Control") ||
            this.logic.clientRenderer.keyboard().state().get("ControlLeft") ||
            this.logic.clientRenderer.keyboard().state().get("ControlRight")
        )
    }

    // True when one of the player's item screens (inventory, crafting, or trading) is the open
    // modal. Under the modern renderer all three are a single ModernInventoryDialog; under the
    // classic renderer crafting/trading are still recognizable from the opening message.
    // Whether the screen also closes off the world is isWorldBlockingScreenOpen's question.
    public isItemScreenOpen = (): boolean => {
        if (this.chapterAnnouncement?.isOpen()) return true
        const modal = this.currentModal
        if (!modal) {
            return false
        }
        if (modal instanceof ModernInventoryDialog) {
            return true
        }
        return isCraftingMessage(modal.message) || isTradeMessage(modal.message)
    }

    // True when a screen is up that takes the world away from the player: a server dialog (quests,
    // NPC management), the chapter announcement. The item screens -- their own pack, crafting, a chest
    // or a trade -- do NOT: the player keeps walking, fighting and opening things with one up. A press
    // on that panel is kept from the world by Mouse.pressIsOnGui, as for any other HUD control, rather
    // than by gating the whole world on it.
    public isWorldBlockingScreenOpen = (): boolean => {
        if (!this.isItemScreenOpen()) return false
        if (this.chapterAnnouncement?.isOpen()) return true
        const modal = this.currentModal
        return !(modal instanceof ModernInventoryDialog && modal.leavesWorldLive())
    }

    // Close a screen about another entity (a chest, a camp member's pack) once that entity, having been
    // in the player's view, leaves it or is gone -- the player walked off, or it did. Only after it has
    // been seen: a member's pack opened from their roster card, while they are off screen, stays up.
    // Called every frame by the renderer.
    public closeScreenOffSubject = () => {
        const modal = this.currentModal
        const subjectId = modal?.message?.subjectEntityId
        if (!subjectId) {
            this.screenSubjectSeen = undefined
            return
        }
        const entity = this.logic.getEntity(subjectId)
        const inView = !!entity && !this.logic.isDestroyed(subjectId) && this.logic.isWithinPlayerViewport(entity.location)
        if (inView) {
            this.screenSubjectSeen = subjectId
        } else if (this.screenSubjectSeen === subjectId) {
            this.screenSubjectSeen = undefined
            this.closeModal()
        }
    }

    // Called both by the global Control-key path (no args → broadcast wheel) and by the server's
    // OPEN_COMMAND_WHEEL reply (opts → wheel bound to one NPC, locked to its role).
    public showCommandWheel = (opts?: {
        targetEntityId: string
        role: CommandWheelRole
        commands?: Array<{ name: string; displayName: string }>
    }) => {
        // The command wheel is only available in rooms that opt in (ember rooms), signalled by the
        // per-room enableCommandWheel flag that rides to the client in WorldState.visibility.
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        // Don't let the player summon the wheel on top of a chest or trade screen (their own pack and
        // crafting leave the world live). The server-driven per-NPC wheel (opts present) is
        // intentional and stays allowed.
        if (!opts && this.isWorldBlockingScreenOpen()) {
            return
        }
        // A wheel going up replaces whatever the pointer was being used for: an armed location pick
        // has no press left to answer it, and its reticle would sit under the wheel unexplained.
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        // Whatever this wheel is about, it is no longer the roster card's.
        this.releaseRosterWheel()
        if (opts) {
            this.tileWheelTarget = undefined
            this.commandWheel.bindToEntity(opts.targetEntityId, opts.role, opts.commands)
            // A per-entity wheel opens directly over its NPC instead of screen-center, and stays
            // there: updateCommandWheelTether draws the line to the NPC as it walks off.
            this.positionCommandWheelOverEntity(opts.targetEntityId)
        } else {
            this.tileWheelTarget = undefined
            this.commandWheel.clearTarget()
            this.commandWheel.clearSelection() // Clear any previous selection
            // The global (Control-key) wheel returns to its screen-center home in case a prior
            // per-entity wheel moved it.
            this.commandWheel.position.copyFrom(this.commandWheelHome)
        }
        this.commandWheel.fadeIn()

        // Prevent context menu when command wheel is visible
        this.preventContextMenu()
        if (opts) this.learnedEntityMenu(opts.targetEntityId)
    }

    // Open one camp member's command wheel from their ROSTER CARD.
    //
    // Unlike the per-NPC wheel above, this one is not put over its entity: a member you reach through
    // the roster is usually the one you can't see, so it takes the HUD slot directly above the strip
    // (the same slot the follow-cam PiP and the minimap share) and a connector line carries which
    // card it belongs to. For the same reason it does NOT close when its NPC leaves the viewport.
    private watchMemberCommand(command: string, targetEntityId?: EntityId): boolean {
        if (!["defender:watch", "lord:watch"].includes(command) || !targetEntityId) return false
        const name = this.recruitHintRoster.find(entry => entry.entityId === targetEntityId)?.name
        this.hideCommandWheel()
        this.openFollowCam(targetEntityId, name)
        this.emberRoster.holdSelection(targetEntityId)
        return true
    }

    public openRosterCommandWheel = (entityId: EntityId, role: CommandWheelRole) => {
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        if (this.isWorldBlockingScreenOpen()) {
            return
        }
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        this.commandWheelRosterId = entityId
        this.tileWheelTarget = undefined
        // The lord's orders depend on where he stands (Go upstairs), which only the server knows: his card carries them.
        const orders = role === "lord" ? this.recruitHintRoster.find(entry => entry.entityId === entityId)?.orders : undefined
        this.commandWheel.bindToEntity(entityId, role, orders)
        this.positionRosterCommandWheel()
        this.commandWheel.fadeIn()
        // The wheel has taken the slot the minimap was holding.
        this.positionEmberMinimap()
        this.preventContextMenu()
        this.learnedEntityMenu(entityId)
    }

    // Park a roster-opened wheel in the slot above the strip, clamped so no part of the disc -- glow
    // included -- can leave the screen, however few cards the strip is down to.
    private positionRosterCommandWheel = () => {
        // positionEmberRoster runs during init before the wheel exists -- guard against that.
        if (!this.commandWheelRosterId || !this.commandWheel) {
            return
        }
        // The wheel is wider than it is tall (its name plaques reach out to either side), and how much
        // depends on the names on show -- so the slot is sized from its extent, not its radius.
        const extent = this.commandWheel.getExtent()
        const width = extent.left + extent.right
        const height = extent.top + extent.bottom
        const slot = this.rosterSlotAnchor(width, height)
        const at = this.clampToScreen(slot, width, height)
        // rosterSlotAnchor and clampToScreen work in the HUD container's frame; the wheel is a direct
        // child of the stage, and is centre-anchored rather than top-left.
        this.commandWheel.position.set(this.container.x + at.x + extent.left, this.container.y + at.y + extent.top)
        this.updateRosterWheelConnector()
    }

    // The line from a roster-opened wheel down to the card that opened it, in the wheel's accent (the
    // member's role colour). Drawn UNDER the strip -- see where rosterWheelConnector is built -- so an
    // offscreen-speech bubble, and every other piece of HUD, reads over the top of it.
    private updateRosterWheelConnector = () => {
        const g = this.rosterWheelConnector
        g.clear()
        const anchor = this.commandWheelRosterId
            ? this.emberRoster.cardAnchor(this.commandWheelRosterId, "panel")
            : null
        if (!anchor || !this.commandWheel.isOpen()) {
            g.visible = false
            return
        }
        // Both ends in the HUD container's frame (the wheel lives on the stage, one level up). The
        // card end stops a dot's width clear of the panel: the line runs UNDER the strip, so a dot
        // sitting on the panel's edge would be half buried by it.
        const toX = anchor.x
        const toY = anchor.y - WHEEL_TETHER_DOT_RADIUS
        const fromCenterX = this.commandWheel.x - this.container.x
        const fromCenterY = this.commandWheel.y - this.container.y
        const dx = toX - fromCenterX
        const dy = toY - fromCenterY
        const distance = Math.hypot(dx, dy)
        const rim = this.commandWheel.getRadius() + WHEEL_TETHER_RIM_GAP
        if (distance < rim + WHEEL_TETHER_MIN_LENGTH) {
            g.visible = false
            return
        }
        g.visible = true
        g.alpha = this.commandWheel.alpha * WHEEL_TETHER_ALPHA
        g.lineStyle({ width: WHEEL_TETHER_WIDTH, color: anchor.color, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(fromCenterX + (dx / distance) * rim, fromCenterY + (dy / distance) * rim)
        g.lineTo(toX, toY)
        g.beginFill(anchor.color, 1)
        g.drawCircle(toX, toY, WHEEL_TETHER_DOT_RADIUS)
        g.endFill()
    }

    private clearRosterWheelConnector = () => {
        this.rosterWheelConnector.clear()
        this.rosterWheelConnector.visible = false
    }

    // Outline the roster's selected member out in the world (undefined takes it off). Routed through
    // the HUD rather than called from the roster widget directly, which knows only about this class.
    public highlightEmberMember = (entityId?: EntityId) => {
        this.logic.clientRenderer.entity().setRosterHighlight(entityId)
    }

    // Put away whatever the roster's active card opened -- the follow-cam PiP or that member's
    // command wheel -- and clear the card's highlight. Called by the roster before it acts on a tap,
    // so only one card is ever live and tapping the active card is a toggle.
    public closeRosterSelection = () => {
        this.dismissFollowCam()
        if (this.commandWheelRosterId) {
            this.hideCommandWheel()
        }
        if (this.rolePickerRosterId) {
            this.hideRolePicker()
        }
    }

    // Open a VISITOR'S OWN VERBS from their roster card: the same wheel a tap on the person would
    // give you ("Give Food", and "Talk" once they are fed), parked in the HUD slot above the strip
    // with a connector down to the card -- because the entire reason the player is reading a card
    // is that they cannot see who it belongs to.
    //
    // It borrows the roster command wheel's plumbing wholesale (`commandWheelRosterId` is what pins
    // it to the slot, draws the connector, and keeps it alive while its NPC is off screen); what
    // `rosterInteractionId` adds is that a selection goes out on the INTERACTION channel instead of
    // the squad one, and that the slices know how far away the player is standing.
    public openRosterPlayerWheel = (entityId: EntityId, name?: string) => {
        if (this.isWorldBlockingScreenOpen()) return
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        this.commandWheelRosterId = entityId
        this.rosterInteractionName = name
        this.tileWheelTarget = undefined
        this.commandWheel.bindToInteractions(
            entityId,
            [
                { name: WATCH_SLICE, displayName: "Watch" },
                { name: "ember:kick", displayName: "Kick" },
                { name: "ember:play_mode", displayName: "Play Mode" },
            ],
            "👤",
        )
        this.positionRosterCommandWheel()
        this.commandWheel.fadeIn()
        this.positionEmberMinimap()
        this.preventContextMenu()
    }

    public openRosterInteractionWheel = (entityId: EntityId, name?: string) => {
        if (this.isWorldBlockingScreenOpen()) {
            return
        }
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        this.commandWheelRosterId = entityId
        this.rosterInteractionId = entityId
        this.rosterInteractionName = name
        this.bindRosterInteractions(entityId)
        this.positionRosterCommandWheel()
        this.commandWheel.fadeIn()
        // The wheel has taken the slot the minimap was holding.
        this.positionEmberMinimap()
        this.preventContextMenu()
    }

    // Is the player close enough to hand something over? See INTERACTION_REACH: this is the "can
    // you see them" rule a world tap always enforced by accident, written down so a card can
    // enforce it too.
    private withinInteractionReach = (entityId: EntityId): boolean => {
        const me = this.logic.playerEntity
        const them = this.logic.getEntity(entityId)
        if (!me?.location || !them?.location || me.roomId !== them.roomId) {
            return false
        }
        const dx = me.location.x - them.location.x
        const dy = me.location.y - them.location.y
        const reach = INTERACTION_REACH_TILES * TileSize
        return dx * dx + dy * dy <= reach * reach
    }

    // Write the slices for a roster-opened interaction wheel. A verb that needs the player to be
    // there says so in its own label rather than being hidden: a card that silently dropped "Give
    // Food" would teach the player that a visitor wants nothing, which is the opposite of the
    // lesson. WATCH is appended by the HUD, not the entity -- it is how one tap still reaches the
    // follow cam now that the card's tap belongs to the verbs.
    private bindRosterInteractions = (entityId: EntityId) => {
        // A HELD verb is not among them: a slice cannot be held down, so picking one would do
        // nothing (see wheelInteractions). A card whose entity offers only held verbs still opens,
        // carrying the HUD's own "Watch" -- which is then honestly all a card can do for them.
        const descriptors = wheelInteractions(this.logic.clientRenderer.entity().getEntityInteractions(entityId) || [])
        const inReach = this.withinInteractionReach(entityId)
        this.rosterInteractionInReach = inReach
        const commands = descriptors.map(descriptor => ({
            name: descriptor.type as string,
            displayName:
                interactionNeedsReach(descriptor.type) && !inReach
                    ? `${interactionLabel(descriptor)} (too far)`
                    : interactionLabel(descriptor),
        }))
        commands.push({ name: WATCH_SLICE, displayName: "Watch" })
        this.tileWheelTarget = undefined
        this.commandWheel.bindToInteractions(entityId, commands)
    }

    // Called every frame while a roster interaction wheel is up: the player can walk into (or out
    // of) reach with it open, and a label that went stale would be a lie about the only thing the
    // wheel is being asked.
    private refreshRosterInteractionReach = () => {
        if (!this.rosterInteractionId) {
            return
        }
        if (this.withinInteractionReach(this.rosterInteractionId) === this.rosterInteractionInReach) {
            return
        }
        this.bindRosterInteractions(this.rosterInteractionId)
        // The relabelled plaques ("Give Food (too far)" -> "Give Food") change the wheel's footprint.
        this.positionRosterCommandWheel()
        this.positionEmberMinimap()
    }

    // Map tiles use the shared wheel and advertise their verbs through delta metadata.
    public showTileInteractionWheel = (target: TileInteractionTarget) => {
        if (!target.interactions.length || this.isWorldBlockingScreenOpen()) return
        this.logic.playerController.cancelLocationPick()
        this.releaseRosterWheel()
        this.clearCommandWheelTether()
        this.tileWheelTarget = {
            coordinate: { ...target.coordinate },
            layerId: target.layerId,
            roomId: target.roomId ?? this.logic.playerEntity.roomId,
            yOffset: target.yOffset,
        }
        const interactions = this.liveTileWheelInteractions() ?? target.interactions
        this.tileWheelInteractionsKey = JSON.stringify(interactions)
        this.commandWheel.bindToInteractions("", interactions)
        this.positionTileInteractionWheel()
        this.commandWheel.fadeIn()
        this.preventContextMenu()
    }

    private liveTileWheelInteractions = () => {
        const target = this.tileWheelTarget
        if (!target) return
        const provider = target.roomId === this.logic.playerEntity?.roomId ? this.logic.tilemapMetaProvider :
            this.logic.clientRenderer.map().drawnFloorProvider?.(target.roomId)
        return interactionsFromTile(provider?.getMapDeltaAt(target.coordinate.x, target.coordinate.y, target.layerId))
    }

    // Tile wheels belong to a world position. Reproject their center each frame as the camera
    // moves; screen clamping would detach the wheel from the tile that supplies its command.
    // Up a hill the tile is drawn raised by the hill's height there (world/hills), and so is its wheel.
    private positionTileInteractionWheel = () => {
        if (!this.tileWheelTarget) return
        const overlay = this.logic.clientRenderer.map().overlay()
        const { coordinate, roomId, yOffset = 0 } = this.tileWheelTarget
        const provider = roomId === this.logic.playerEntity?.roomId ? this.logic.tilemapMetaProvider :
            this.logic.clientRenderer.map().drawnFloorProvider?.(roomId)
        const x = (coordinate.x + 0.5) * TileSize, y = (coordinate.y + 0.5) * TileSize
        const center = overlay.computeLocation(x, y + yOffset - hillHeightOf(provider, x, y))
        this.commandWheel.position.copyFrom(this.stage.toLocal(center, overlay))
    }

    // Open the wheel over an entity to choose between the interactions it offers. Unlike
    // showCommandWheel this is NOT gated on the ember-room enableCommandWheel flag: chests, campfires
    // and denizens exist everywhere, and their interactions are how a player opens them now that the
    // hover icons are gone. Positioning, dismissal (Escape, Control, selection) and the fade are
    // shared with the command wheel.
    public showInteractionWheel = (entityId: EntityId, interactions: InteractionDescriptor[]) => {
        if (!interactions?.length) {
            return
        }
        // Don't put a wheel up underneath a chest or trade screen. The player's own pack and crafting
        // leave the world live, its wheels included.
        if (this.isWorldBlockingScreenOpen()) {
            return
        }
        this.logic.playerController?.cancelLocationPick()
        this.releaseRosterWheel()
        this.tileWheelTarget = undefined
        this.commandWheel.bindToInteractions(
            entityId,
            interactions.map(next => ({ name: next.type, displayName: interactionLabel(next) })),
        )
        this.clearCommandWheelTether()
        if (!this.positionCommandWheelOverEntity(entityId)) {
            this.commandWheel.position.copyFrom(this.commandWheelHome)
        }
        this.commandWheel.fadeIn()
        this.preventContextMenu()
        this.learnedEntityMenu(entityId)
    }

    // The given entity's center in stage-local coordinates (the frame the command wheel / role
    // picker live in, as direct children of the stage). Null when the entity isn't available yet.
    private entityStageLocal = (entityId: string): { x: number; y: number } | null => {
        const entity = this.logic.getEntity(entityId)
        if (!entity) {
            return null
        }
        const overlay = this.logic.clientRenderer.map().overlay()
        // computeLocation gives the entity's center in the overlay surface's local frame; convert
        // that into stage-local coordinates.
        const center = overlay.computeLocation(entity.location.x, entity.location.y)
        return this.stage.toLocal(center, overlay)
    }

    // Move the wheel so its center sits over the given entity -- or as near it as keeps the whole
    // wheel on screen. Returns false when the entity (or its overlay frame) isn't available yet.
    // Called once, at open: the wheel then stays put (a wheel that chased a walking NPC was
    // impossible to aim at), and the tether line carries the link.
    private positionCommandWheelOverEntity = (entityId: string): boolean => {
        const local = this.entityStageLocal(entityId)
        if (!local) {
            return false
        }
        this.commandWheel.position.set(local.x, local.y)
        this.clampCommandWheelToScreen()
        return true
    }

    // Keep the whole wheel -- name plaques and title included -- on screen. It opens centred on what it
    // is about, but a member standing near the edge would otherwise hang half its orders off it. The
    // tether still runs to the member wherever the wheel ends up.
    private clampCommandWheelToScreen = () => {
        const extent = this.commandWheel.getExtent()
        const at = this.clampToScreen(
            {
                x: this.commandWheel.x - this.container.x - extent.left,
                y: this.commandWheel.y - this.container.y - extent.top,
            },
            extent.left + extent.right,
            extent.top + extent.bottom,
        )
        this.commandWheel.position.set(this.container.x + at.x + extent.left, this.container.y + at.y + extent.top)
    }

    // Per-frame upkeep for a per-entity wheel: drop it once its target is gone or has left the
    // viewport (there is nothing left on screen to command), and otherwise redraw the tether from
    // the pinned wheel to the NPC. No-op for the global broadcast wheel (no bound target) and while
    // the wheel is closed. Called every frame by the renderer.
    public updateCommandWheelTether = () => {
        if (this.commandWheel.isOpen() && this.tileWheelTarget) {
            const interactions = this.liveTileWheelInteractions()
            if (interactions && JSON.stringify(interactions) !== this.tileWheelInteractionsKey) {
                this.tileWheelInteractionsKey = JSON.stringify(interactions)
                this.commandWheel.bindToInteractions("", interactions)
            }
            this.positionTileInteractionWheel()
        }
        const entityId = this.commandWheel.isOpen() ? this.commandWheel.getTargetEntityId() : undefined
        if (!entityId) {
            this.clearCommandWheelTether()
            this.clearRosterWheelConnector()
            return
        }
        // A ROSTER-opened wheel is pinned to the HUD, not to the world: it is tied to a card rather
        // than to the NPC, and it must survive its member being off screen -- reaching somebody you
        // cannot see is what the roster is for. Other floors may have no local entity (or a stale
        // destroyed marker after a room transfer), so the camp roster determines whether it is gone.
        if (this.commandWheelRosterId) {
            if (!this.recruitHintRoster.some(entry => entry.entityId === entityId)) {
                this.hideCommandWheel()
                return
            }
            this.refreshRosterInteractionReach()
            this.clearCommandWheelTether()
            this.updateRosterWheelConnector()
            return
        }
        const entity = this.logic.getEntity(entityId)
        const gone = !entity || this.logic.isDestroyed(entityId)
        if (gone || !this.logic.isWithinPlayerViewport(entity.location)) {
            this.hideCommandWheel()
            return
        }
        this.drawCommandWheelTether(entityId)
    }

    // A translucent line from the pinned wheel's rim to the commanded NPC's center, with a dot on
    // the NPC end. Drawn in the wheel's accent and multiplied by the wheel's own alpha so it fades
    // in and out with it. Suppressed while the NPC is still under the disc, where a stub of line
    // would only show as a nub poking out of the hub.
    private drawCommandWheelTether = (entityId: string) => {
        const g = this.commandWheelTether
        g.clear()
        const local = this.entityStageLocal(entityId)
        if (!local) {
            g.visible = false
            return
        }
        const dx = local.x - this.commandWheel.x
        const dy = local.y - this.commandWheel.y
        const distance = Math.hypot(dx, dy)
        const rim = this.commandWheel.getRadius() + WHEEL_TETHER_RIM_GAP
        if (distance < rim + WHEEL_TETHER_MIN_LENGTH) {
            g.visible = false
            return
        }
        const accent = this.commandWheel.getAccent()
        g.visible = true
        g.alpha = this.commandWheel.alpha * WHEEL_TETHER_ALPHA
        g.lineStyle({ width: WHEEL_TETHER_WIDTH, color: accent, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(this.commandWheel.x + (dx / distance) * rim, this.commandWheel.y + (dy / distance) * rim)
        g.lineTo(local.x, local.y)
        g.beginFill(accent, 1)
        g.drawCircle(local.x, local.y, WHEEL_TETHER_DOT_RADIUS)
        g.endFill()
    }

    private clearCommandWheelTether = () => {
        this.commandWheelTether.clear()
        this.commandWheelTether.visible = false
    }

    // Let go of a roster-opened wheel: drop its connector, un-highlight the card that opened it, and
    // hand the slot above the strip back to the minimap. Called wherever the wheel stops being that
    // card's -- it closing, or another kind of wheel taking it over.
    private releaseRosterWheel = () => {
        if (!this.commandWheelRosterId) {
            return
        }
        this.commandWheelRosterId = undefined
        this.rosterInteractionId = undefined
        this.rosterInteractionName = undefined
        this.clearRosterWheelConnector()
        this.emberRoster.clearSelection()
        this.positionEmberMinimap()
    }

    public hideCommandWheel = () => {
        const tileWheel = !!this.tileWheelTarget
        this.tileWheelTarget = undefined
        this.releaseRosterWheel()
        this.commandWheel.fadeOut()
        this.commandWheel.clearTarget()
        this.commandWheel.clearSelection() // Clear any previous selection
        this.clearCommandWheelTether()

        // Restore normal context menu behavior
        this.restoreContextMenu()
        // A right-click on a tile may have left build mode to open this wheel; the player picks it back up.
        if (tileWheel) this.logic.playerController?.tileWheelClosed()
    }

    public isCommandWheelVisible = () => {
        return this.commandWheel.isOpen()
    }

    // Server-driven: open the recruit role picker over a specific camp member so the player can assign
    // it a role. Gated to ember rooms via the same per-room visibility flag as the command wheel.
    //
    // `currentRole` arrives only with a "Change Role" order on an already-hired member, and narrows the
    // picker to the job that member is NOT doing. `weapon` is the weapon type it would fight with,
    // which the Defender card names; `playMode` turns the same widget into the guest play-mode picker.
    public showRolePicker = (
        entityId: string,
        currentRole?: RecruitRole,
        options: { playMode?: EmberPlayMode; weapon?: string; availableRoles?: RecruitRole[] } = {},
    ) => {
        const { playMode, weapon, availableRoles } = options
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        // A picker asked for from a roster card is not about a spot on screen; it inherits the HUD slot
        // (and the lit card, and the connector) from the wheel that asked for it. Anything else -- a
        // hire at the fire, a per-entity wheel -- opens over the member, where the player is looking.
        if (this.rolePickerRosterId && this.rolePickerRosterId !== entityId) {
            this.rolePickerRosterId = undefined
        }
        const fromRoster = this.rolePickerRosterId === entityId
        if (playMode) this.rolePicker.bindPlayMode(entityId, playMode)
        else this.rolePicker.bindToEntity(entityId, currentRole, weapon, availableRoles)
        if (fromRoster) {
            this.rolePicker.setTailVisible(false)
            this.positionRolePickerInRosterSlot()
            this.emberRoster.holdSelection(entityId)
            // The picker has taken the slot the minimap was holding.
            this.positionEmberMinimap()
        } else {
            this.positionRolePickerOverEntity(entityId)
        }
        this.rolePicker.fadeIn()
        this.preventContextMenu()
    }

    // Park a roster-raised picker in the slot above the strip, clamped so none of it can leave the
    // screen -- the same slot, and the same clamp, the wheel that raised it was using.
    private positionRolePickerInRosterSlot = () => {
        if (!this.rolePickerRosterId || !this.rolePicker) {
            return
        }
        const width = this.rolePicker.getPanelWidth()
        const height = this.rolePicker.getPanelHeight()
        const at = this.clampToScreen(this.rosterSlotAnchor(width, height), width, height)
        // rosterSlotAnchor and clampToScreen work in the HUD container's frame; the picker is a direct
        // child of the stage.
        this.rolePicker.placeBodyTopLeftAt(this.container.x + at.x, this.container.y + at.y)
        this.updateRolePickerConnector()
    }

    // The line from a roster-raised picker down to the card that raised it, in that member's role
    // colour -- the same connector the roster command wheel draws, and drawn with the same Graphics,
    // because the wheel is always closed by the time the picker is up.
    private updateRolePickerConnector = () => {
        const g = this.rolePickerConnector
        g.clear()
        const anchor = this.rolePickerRosterId ? this.emberRoster.cardAnchor(this.rolePickerRosterId, "panel") : null
        if (!anchor || !this.rolePicker.isOpen()) {
            g.visible = false
            return
        }
        // Both ends in the HUD container's frame. The picker's own origin is its tail TIP, which sits
        // below the body -- the line starts from the body's bottom edge, where the tail would have been.
        const toX = anchor.x
        const toY = anchor.y - WHEEL_TETHER_DOT_RADIUS
        const fromX = this.rolePicker.x - this.container.x
        const fromY = this.rolePicker.y - this.container.y - this.rolePicker.getTailDrop()
        if (toY - fromY < WHEEL_TETHER_MIN_LENGTH) {
            g.visible = false
            return
        }
        g.visible = true
        g.alpha = this.rolePicker.alpha * WHEEL_TETHER_ALPHA
        g.lineStyle({ width: WHEEL_TETHER_WIDTH, color: anchor.color, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(fromX, fromY + WHEEL_TETHER_RIM_GAP)
        g.lineTo(toX, toY)
        g.beginFill(anchor.color, 1)
        g.drawCircle(toX, toY, WHEEL_TETHER_DOT_RADIUS)
        g.endFill()
    }

    // Position the picker so its tail tip sits at the recruit's center (the panel floats above).
    // The picker is glued to its recruit, so the tail is true for exactly as long as there is
    // somebody to glue it to: once the recruit is gone from this client the panel is left standing
    // where it was, and the pointer comes off rather than aim at empty ground.
    private positionRolePickerOverEntity = (entityId: string): boolean => {
        const local = this.logic.isDestroyed(entityId) ? null : this.entityStageLocal(entityId)
        if (!local) {
            this.rolePicker.setTailVisible(false)
            return false
        }
        this.rolePicker.position.set(local.x, local.y)
        this.rolePicker.setTailVisible(true)
        return true
    }

    // Keep the picker glued over its member as the entity (or camera) moves. No-op while closed.
    // Called every frame by the renderer.
    public updateRolePickerPosition = () => {
        if (!this.rolePicker.isOpen()) {
            return
        }
        const entityId = this.rolePicker.getTargetEntityId()
        if (!entityId) {
            return
        }
        // A roster-raised picker is pinned to the HUD, not to the world: it is tied to a CARD, and must
        // survive its member being off screen -- reaching somebody you cannot see is what the roster is
        // for. Only the member actually going takes it down.
        if (this.rolePickerRosterId === entityId) {
            if (this.logic.isDestroyed(entityId) || !this.logic.getEntity(entityId)) {
                this.hideRolePicker()
                return
            }
            this.positionRolePickerInRosterSlot()
            return
        }
        this.positionRolePickerOverEntity(entityId)
    }

    public hideRolePicker = () => {
        const stairMessage = this.stairPickerMessage
        this.stairPickerMessage = undefined
        if (stairMessage?.onCloseEmitEvent)
            this.logic.playerController.client.emitPlayerEvent(stairMessage.onCloseEmitEvent)
        this.emberInviteModal?.close()
        const wasRoster = this.rolePickerRosterId
        this.rolePickerRosterId = undefined
        this.rolePicker.fadeOut()
        this.rolePicker.clearTarget()
        if (wasRoster) {
            // Hand the card, the connector and the slot back the way a roster wheel does when it closes.
            this.rolePickerConnector.clear()
            this.rolePickerConnector.visible = false
            this.emberRoster.clearSelection()
            this.positionEmberMinimap()
        }
        this.restoreContextMenu()
    }

    // Server-driven: put the name field up over one camp member, started on `name`. Gated to ember
    // rooms by the same per-room visibility flag as the command wheel and the role picker.
    public showNamePrompt = (entityId: EntityId, name?: string) => {
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        this.namePrompt.bindToEntity(entityId, name)
        if (!this.positionNamePromptOverEntity(entityId)) {
            // The entity isn't on this client yet (a hire names the recruit in the same breath as
            // swapping its script). Nothing to pin to, so there is nothing sensible to show.
            this.namePrompt.clearTarget()
            return
        }
        this.namePrompt.fadeIn()
        // The field now owns the keyboard: see Keyboard.suppressTyping. Released in hideNamePrompt,
        // and Escape still gets through, which is what guarantees there is always a way out.
        Keyboard.suppressTyping(true)
        this.preventContextMenu()
    }

    // Position the prompt so its tail tip sits at the member's center (the panel floats above).
    // Called once, at open: unlike the role picker the prompt does NOT follow its entity, because a
    // field that slides out from under the cursor while being typed into is far worse than one
    // left standing where the player opened it. The tail is what pays for that -- see
    // updateNamePrompt.
    private positionNamePromptOverEntity = (entityId: EntityId): boolean => {
        const local = this.entityStageLocal(entityId)
        if (!local) {
            return false
        }
        this.namePrompt.position.set(local.x, local.y)
        this.namePrompt.setTailVisible(true)
        return true
    }

    // Per-frame upkeep: drop the prompt once the entity being named is gone -- there is nobody left
    // to name. Deliberately NOT closed when that entity merely walks off screen: a member can wander
    // out of view while their name is half typed, and losing the field then would lose the name.
    //
    // What does go is the tail. The panel is pinned where it opened, so the member (or the camera)
    // only has to move for the pointer to be aiming at bare ground, which reads as the prompt being
    // about whatever is standing there now. Checked every frame, and it comes back if they walk
    // back under it.
    public updateNamePrompt = () => {
        if (!this.namePrompt.isOpen()) {
            return
        }
        const entityId = this.namePrompt.getTargetEntityId()
        if (!entityId) {
            return
        }
        if (!this.logic.getEntity(entityId) || this.logic.isDestroyed(entityId)) {
            this.hideNamePrompt()
            return
        }
        const local = this.entityStageLocal(entityId)
        const onTip =
            !!local && Math.hypot(local.x - this.namePrompt.x, local.y - this.namePrompt.y) <= PANEL_TAIL_SLACK
        this.namePrompt.setTailVisible(onTip)
    }

    public hideNamePrompt = () => {
        const targetEntityId = this.namePrompt.getTargetEntityId()
        const wasOpen = this.namePrompt.isOpen()
        this.namePrompt.fadeOut()
        this.namePrompt.clearTarget()
        Keyboard.suppressTyping(false)
        this.restoreContextMenu()
        if (wasOpen && targetEntityId) {
            this.logic.playerController.client.emitPlayerEvent({
                playerEventType: "NAME_PROMPT_DISMISSED",
                context: { targetEntityId },
            })
        }
    }

    public isNamePromptVisible = () => {
        return this.namePrompt.isOpen()
    }

    // The run is over: rebuild the scorecard around this payload and fade it in over the camp.
    public showRunSummary = (summary: EmberRunSummary) => {
        if (!summary) {
            return
        }
        // Nothing else should be competing for the screen once the run has ended.
        this.hideCommandWheel()
        this.hideRolePicker()
        this.hideNamePrompt()
        this.hideInvasionPicker()
        this.dismissFollowCam()
        // Prefer the synced entity (it carries the current appearance/inventory the paperdoll needs)
        // and fall back to the local player stub.
        const player = this.logic.getEntity(this.logic.playerEntity?.entityId) || this.logic.playerEntity
        this.runSummary.setSummary(summary, player)
        this.runSummary.fadeIn()
    }

    public hideRunSummary = () => {
        this.runSummary.fadeOut()
        // The picker is opened FROM the scorecard and is meaningless without it: the panel going
        // away means the player took one of its other ways on, and a chooser left floating over a
        // room they have already left would still send a `join`.
        this.hideInvasionPicker()
    }

    public isRunSummaryVisible = () => {
        return this.runSummary?.isOpen() === true
    }

    // Server-driven: the newest invasion offer (Protocol.type_updateInvasionOffer). An interval
    // feed, ~2s, NOT a reply to a press -- which is the point: the scorecard's third button lights
    // up by itself when a camp becomes available, and an open picker gains and loses cards while
    // it is being read.
    //
    // `null` forgets the offer and takes the picker down. An EMPTY list does neither: it is a real
    // answer ("no camp is open to the dead") and the normal steady state on a quiet server, so the
    // button stays, inert and saying so, and an open picker shows its empty state rather than
    // vanishing under the player.
    updateInvasionOffer = (candidates: EmberInvasionCandidate[] | null) => {
        if (!candidates) {
            this.invasionCandidates = undefined
            this.runSummary?.setInvasionOffer(undefined)
            this.hideInvasionPicker()
            return
        }
        this.invasionCandidates = candidates
        this.runSummary?.setInvasionOffer(candidates)
        if (this.invasionPicker?.isOpen()) {
            this.invasionPicker.setCandidates(candidates)
        }
    }

    // Open the chooser on what the client already holds. No round trip: see the scorecard's
    // onInvade. Refuses to open on nothing, because the inert button should not have been pressable
    // in the first place (runSummaryButtons) and a picker opening onto its own empty state would
    // read as a broken press rather than as an honest answer.
    public showInvasionPicker = () => {
        if (!this.invasionCandidates || this.invasionCandidates.length < 1) {
            return
        }
        this.invasionPicker.open(this.invasionCandidates)
    }

    public hideInvasionPicker = () => {
        if (this.invasionPicker?.isOpen()) {
            this.invasionPicker.close()
        }
    }

    public isInvasionPickerVisible = () => {
        return this.invasionPicker?.isOpen() === true
    }

    // Backing out of the picker, from any of its three ways out: the footer button, a press on its
    // own scrim, and Escape (the house convention for every other modal -- see Player's Escape
    // chain). All three go through here rather than each pairing the message with the close
    // themselves, which is how the keyboard path and the pointer path drift apart.
    public cancelInvasionPicker = () => {
        if (!this.isInvasionPickerVisible()) {
            return
        }
        this.logic.playerController?.client?.sendEmberInvasionCommand("cancel")
        this.hideInvasionPicker()
    }

    // Whether this player is currently wearing one of another camp's attackers (Message.invading).
    // Taken from the server's verdict on every message, like the downed flag, so the way out cannot
    // outlive the body -- or go missing while it lives.
    //
    // It is also what takes the SCORECARD down, and the timing is the point. The other two ways off
    // that panel dismiss it as they send, because they cannot fail; a `join` can -- the camp may
    // have closed, or filled, between the feed and the press -- and a panel dismissed optimistically
    // on a refused join would leave a still-fallen player with no way out at all until the camp's
    // own 3-minute net fired. So the panel waits for the server to say the visit really started.
    public setInvading = (invading: boolean) => {
        this.letGoButton?.setInvading(invading)
        // The gauge and that button share one slot. Taken down here rather than on the next roster
        // tick so the swap happens on the same frame the button appears.
        if (invading) {
            this.emberFireGauge?.update(undefined)
            // Forget which wave was last played, and what was being counted down to: the next
            // camp's wave 1 is a landing this player has not watched yet.
            this.wavePlayed = undefined
            this.waveAwaited = undefined
        }
        if (invading && !this.invading && this.isRunSummaryVisible()) {
            this.hideRunSummary()
        }
        // Each end of a visit, announced once. Both ends are this ONE verdict's transitions, which
        // is why neither needs a message of its own: the server already tells the client whether it
        // is wearing somebody else's body, every message, for its own entity.
        //
        // A visit that ends by dying also builds the player a fresh camp, so the "returned" splash
        // goes up alongside a room load -- hence its zIndex above everything, and hence nothing in
        // the room-change path takes it down. It waits to be read.
        const splash = splashForInvadingChange(this.invading, invading)
        if (splash) {
            this.showPossessionSplash(splash)
        }
        this.invading = invading
    }

    public showPossessionSplash = (kind: PossessionSplashKind) => {
        this.possessionSplash?.open(kind)
    }

    public hidePossessionSplash = () => {
        if (this.possessionSplash?.isOpen()) {
            this.possessionSplash.close()
        }
    }

    public isPossessionSplashVisible = () => this.possessionSplash?.isOpen() === true

    public isRolePickerVisible = () => {
        return this.rolePicker.isOpen() || this.emberInviteModal?.isOpen() === true
    }

    // Server-driven: put up the first-run tutorial invitation (or take it down again when the
    // server passes null -- e.g. the player answered on another client, or the camp moved on).
    // Gated to ember rooms by the same per-room visibility flag as the command wheel and the role
    // picker, so a stray payload can never park a bar over a normal room.
    public showTutorialPrompt = (prompt: EmberTutorialPrompt | null) => {
        // The chapter's own choices live at the foot of its checklist, not on a board of their own.
        if (prompt?.nextChapterChip !== undefined) {
            this.hideTutorialPrompt()
            this.chapterChecklist.setCallToAction({ kind: "next", chapter: prompt.nextChapterChip, title: prompt.body })
            return
        }
        if (prompt?.acceptCommand === "continue_story" && this.chapterChecklist.hasTasks()) {
            this.hideTutorialPrompt()
            this.chapterChecklist.setCallToAction({ kind: "continue", body: prompt.body, label: prompt.acceptLabel })
            return
        }
        if (prompt?.announcementChapter !== undefined || prompt?.closingCard) {
            this.chapterChecklist.clearNextChapter()
            this.hideTutorialPrompt()
            this.logic.clientRenderer.entity().releaseHeldSpeech()
            this.chapterAnnouncement.open(prompt)
            this.fitChapterAnnouncement()
            this.logic.playerController?.enabledKeyboardAction(false)
            this.logic.playerController?.enabledMouseAction(false)
            return
        }
        if (!prompt) {
            this.chapterChecklist.setCallToAction(null)
            this.hideTutorialPrompt()
            return
        }
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        this.tutorialPrompt.setPrompt(prompt)
        // The bar sizes itself to the server's wording, so re-anchor it to the bottom of the
        // screen AFTER it has been rebuilt -- otherwise a taller prompt hangs off the edge.
        this.anchorTutorialPrompt()
        this.tutorialPrompt.fadeIn()
    }

    // Keep the invitation a fixed distance off the bottom edge, whatever height its copy needs.
    private anchorTutorialPrompt = () => {
        this.tutorialPrompt.y = ScreenDimensions.h - this.tutorialPrompt.stageHeight() - 18
    }

    public hideTutorialPrompt = () => {
        this.tutorialPrompt.fadeOut()
    }

    /**
     * Fit the chapter title card to the whole screen, and tell it how large the stage is shown there: a
     * phone's is shown small, and the card draws its words larger for it.
     */
    private fitChapterAnnouncement = () => {
        const app = getRendererApp()
        const screen = app.renderer.screen
        const card = this.chapterAnnouncement
        const topLeft = card.toLocal(new Point(screen.x, screen.y))
        const bottomRight = card.toLocal(new Point(screen.x + screen.width, screen.y + screen.height))
        const height = bottomRight.y - topLeft.y
        const shown = app.view?.getBoundingClientRect?.().height
        card.resize(bottomRight.x - topLeft.x, height, topLeft.x, topLeft.y, shown && height > 0 ? shown / height : undefined)
    }

    public isChapterAnnouncementVisible = () => this.chapterAnnouncement?.isOpen() === true

    /**
     * Replay only (packages/client/src/replay): the recorded player SENT this, at this moment of the
     * recording. A few screens are closed by the player's own click and nothing the server sends --
     * the chapter title card, the character builder, a dialog they shut, a picker they answered --
     * so a replay would leave them up forever. The click is not in the recording, but what it sent
     * is: close the screen it answered, the same way the click does (anything that sends on the way
     * out goes nowhere in a replay). Screens backed out of without sending anything stay up, except
     * tutorials, which send telemetry for it (Tutorial.reportUse).
     */
    public actOutRecordedAnswer = (payloadType: string, payload: any) => {
        if (payloadType === Protocol.type_clientTelemetry) {
            if (payload?.kind === "tutorial" && typeof payload.tutorial === "string") {
                this.tutorial?.actOutPlayerUse(payload.tutorial, payload)
            }
            return
        }
        if (payloadType === Protocol.type_finalizeCharacter) {
            if (this.characterBuilder?.isOpen()) this.characterBuilder.close()
            return
        }
        if (payloadType === Protocol.type_setEmberPlayMode) {
            this.hideRolePicker()
            return
        }
        // The words the player sent, put back into the composer they typed them into.
        if (payloadType === Protocol.type_submitFeedback) {
            this.feedbackComposer?.actOutSent(payload?.feedback)
            return
        }
        if (payloadType !== Protocol.type_playerEvent) return
        const { playerEventType, context } = payload || {}
        switch (playerEventType) {
            case "EMBER_TUTORIAL_COMMAND": {
                const card = this.chapterAnnouncement
                if (!card?.isOpen()) break
                // Its own exit animation when it can play one. It cannot when the replay clock has
                // outrun the card's intro, and then (or if it declines) it simply goes.
                const ready = card.isReady()
                if (ready && context?.command === "maybe_later") card.maybeLater()
                else if (ready) card.begin()
                if (!ready || card.isReady()) card.close()
                break
            }
            case "DIALOG_CLOSE":
                if (this.currentModal) this.closeModal()
                break
            // "Next" on a held line: without it the speaker's following lines queue behind a bubble
            // the player had long since moved past.
            case "SPEECH_DISMISSED":
                if (context?.speakerEntityId) this.logic.clientRenderer.entity().advanceHeldSpeech?.(context.speakerEntityId)
                break
            // An answer pressed on a line that asked something: the question comes down as it did for them.
            case "SPEECH_CHOICE":
                if (context?.speakerEntityId) this.logic.clientRenderer.entity().answerHeldSpeech?.(context.speakerEntityId, context.choiceId)
                break
            case "FEEDBACK_CANCELLED":
                this.closeFeedbackComposer(context?.visitId)
                break
            case "SQUAD_COMMAND":
                this.hideCommandWheel()
                this.hideRolePicker()
                break
            case "RENAME_ENTITY":
                this.hideNamePrompt()
                break
            case "EMBER_RUN_COMMAND":
                this.hideRunSummary()
                break
            case "EMBER_INVASION_COMMAND":
                if (context?.command === "seek") this.showInvasionPicker()
                else this.hideInvasionPicker()
                break
        }
    }

    // Any screen, wheel, picker, prompt or splash that Escape would close. Read before the Escape
    // handlers run: while one is up, that press is its, and the world (build mode) keeps it.
    public isModalOpen = (): boolean =>
        Modal.isAnyOpen() ||
        this.isItemScreenOpen() ||
        this.isCommandWheelVisible() ||
        this.isRolePickerVisible() ||
        this.isPossessionSplashVisible() ||
        this.isInvasionPickerVisible() ||
        this.isNamePromptVisible() ||
        this.isChatComposerVisible() ||
        this.isRunSummaryVisible() ||
        this.characterBuilder?.isOpen() === true ||
        this.characterCustomizer?.isOpen() === true

    public isTutorialPromptVisible = () => {
        return this.tutorialPrompt.isOpen()
    }

    private preventContextMenu = () => {
        // Add document-level context menu prevention
        document.addEventListener("contextmenu", this.onContextMenu, true)
    }

    private restoreContextMenu = () => {
        // Remove document-level context menu prevention
        document.removeEventListener("contextmenu", this.onContextMenu, true)
    }

    private onContextMenu = (event: Event) => {
        // Only prevent context menu if command wheel is open
        if (this.commandWheel.isOpen()) {
            event.preventDefault()
            event.stopPropagation()
        }
    }
}
